import React, { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { 
  ChevronLeft, 
  BookOpen, 
  Layout, 
  Type, 
  DollarSign, 
  Image as ImageIcon, 
  Plus, 
  PlayCircle, 
  FileText, 
  CheckCircle2, 
  Trash2, 
  Clock, 
  Calendar, 
  Video, 
  UploadCloud, 
  Upload,
  Link as LinkIcon,
  ExternalLink,
  Loader2,
  Paperclip,
  ChevronDown, 
  ChevronUp, 
  Eye, 
  Check, 
  X, 
  Layers,
  FolderPlus,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Award,
  ShieldCheck,
  AlertCircle,
  User,
  Star,
  Globe,
  MapPin,
  Sparkles,
  Tag,
  GitBranch
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useAuthicatedUser } from "react-redux-django-auth";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import RichTextEditor from "../../components/common/RichTextEditor";
import { useToast } from "../../hooks/otherHooks/useToast";
import {
  CourseData,
  TrackData,
  ModuleData,
  LessonData,
  ExerciseData,
  TopicAttachment,
  AttachmentType,
  InstructorData,
  PresenceData,
  fetchInstructorsList,
  fetchCoursePresences,
  fetchCourseDetail,
  fetchTrackDetail,
  createCourse,
  updateCourse,
  createTrack,
  updateTrack,
  reorderTrackCourses,
  reorderCourseModules,
  reorderModuleLessons,
  fetchCoursesList,
  createModule,
  updateModule,
  deleteModule,
  createLesson,
  updateLesson,
  deleteLesson,
  createExercise,
  updateExercise,
  deleteExercise,
  uploadCurriculumAsset,
  approveContentItem,
  approveAllCourseContent,
  AISyllabusModule,
  generateAICurriculumContent,
  bulkApplyAISyllabus,
} from "../../services/curriculumService";
import MediaPickerModal from "../../components/common/MediaPickerModal";
import { MediaAsset, mediaService } from "../../services/mediaService";

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

// --- Video URL Helper ---
const getYouTubeID = (url?: string) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

// --- Google Classroom Attachment Icons ---
const GoogleDriveIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z" fill="#00ac47"/>
    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z" fill="#ea4335"/>
    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
    <path d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
  </svg>
);

const YouTubeLogoIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" fill="#FF0000"/>
    <path d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FFFFFF"/>
  </svg>
);

const GoogleCreatePlusIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5v6H5v2h6v6h2v-6h6v-2h-6V5h-2z" fill="url(#google-plus-grad)"/>
    <defs>
      <linearGradient id="google-plus-grad" x1="5" y1="5" x2="19" y2="19" gradientUnits="userSpaceOnUse">
        <stop stopColor="#EA4335" offset="0%"/>
        <stop stopColor="#FBBC05" offset="33%"/>
        <stop stopColor="#34A853" offset="66%"/>
        <stop stopColor="#4285F4" offset="100%"/>
      </linearGradient>
    </defs>
  </svg>
);

const GoogleDocsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#4285F4"/>
    <path d="M14 2v6h6l-6-6z" fill="#A1C2FA"/>
    <path d="M16 13H8v-1.5h8V13zm0 3H8v-1.5h8V16zm-3 3H8v-1.5h5V19z" fill="#FFFFFF"/>
  </svg>
);

const GoogleSlidesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z" fill="#F4B400"/>
    <path d="M17 7H7a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zm-1 8H8V9h8v6z" fill="#FFFFFF"/>
  </svg>
);

const GoogleSheetsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#0F9D58"/>
    <path d="M14 2v6h6l-6-6z" fill="#87CEAC"/>
    <path d="M8 12h8v7H8v-7zm1.5 1.5v1.5h2v-1.5h-2zm3.5 0v1.5h2v-1.5h-2zm-3.5 3v1.5h2v-1.5h-2zm3.5 0v1.5h2v-1.5h-2z" fill="#FFFFFF"/>
  </svg>
);

const GoogleDrawingsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="4" fill="#DB4437"/>
    <circle cx="8" cy="8" r="3" fill="#FFFFFF"/>
    <rect x="13" y="11" width="6" height="6" fill="#FFFFFF"/>
  </svg>
);

const GoogleFormsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" fill="#7248B9"/>
    <path d="M14 2v6h6l-6-6z" fill="#B39DDB"/>
    <circle cx="9" cy="13" r="1.2" fill="#FFFFFF"/>
    <path d="M12 12.5h4v1h-4v-1zm0 3.5h4v1h-4v-1z" fill="#FFFFFF"/>
    <circle cx="9" cy="17" r="1.2" fill="#FFFFFF"/>
  </svg>
);

const GoogleVidsIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="6" fill="#4285F4"/>
    <path d="M10 8l6 4-6 4V8z" fill="#FFFFFF"/>
  </svg>
);

const formatFileSize = (bytes?: number) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// --- Attachment Modal Component ---
interface AttachmentModalProps {
  isOpen: boolean;
  type: "drive" | "youtube" | "link" | "doc" | "slide" | "sheet" | "form" | "drawing" | "vids" | null;
  onClose: () => void;
  onAttach: (attachment: TopicAttachment) => void;
}

const AttachmentModal: React.FC<AttachmentModalProps> = ({
  isOpen,
  type,
  onClose,
  onAttach,
}) => {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");

  useEffect(() => {
    if (isOpen) {
      setUrl("");
      setTitle("");
      document.body.style.overflow = "hidden";
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") onClose();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = "unset";
        window.removeEventListener("keydown", handleKeyDown);
      };
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isOpen, onClose]);

  const ytId = useMemo(() => {
    if (type !== "youtube") return null;
    return getYouTubeID(url);
  }, [type, url]);

  const handleConfirm = () => {
    if (!url.trim()) return;
    let attachType: AttachmentType = "link";
    let defaultTitle = title.trim();

    if (type === "youtube") {
      attachType = "youtube";
      if (!defaultTitle) defaultTitle = "YouTube Video";
    } else if (type === "drive") {
      attachType = "drive";
      if (!defaultTitle) defaultTitle = "Google Drive Resource";
    } else if (["doc", "slide", "sheet", "form", "drawing", "vids"].includes(type || "")) {
      attachType = (type || "link") as AttachmentType;
      if (!defaultTitle && type) {
        defaultTitle = `Google ${type.charAt(0).toUpperCase() + type.slice(1)}`;
      }
    } else {
      attachType = "link";
      if (!defaultTitle) {
        try {
          const u = new URL(url.trim().startsWith("http") ? url.trim() : `https://${url.trim()}`);
          defaultTitle = u.hostname.replace("www.", "");
        } catch {
          defaultTitle = "Resource Link";
        }
      }
    }

    const finalUrl = url.trim().startsWith("http") || url.trim().startsWith("//") 
      ? url.trim() 
      : `https://${url.trim()}`;

    onAttach({
      id: `att-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      type: attachType,
      title: defaultTitle,
      url: finalUrl,
    });
    onClose();
  };

  const getModalConfig = () => {
    switch (type) {
      case "youtube":
        return {
          title: "Add YouTube Video",
          subtitle: "Attach high-definition video walkthroughs and lectures",
          icon: <YouTubeLogoIcon className="cht-w-6 cht-h-6" />,
          iconBg: "cht-bg-rose-50 cht-text-rose-600",
          badge: "YouTube",
          badgeColor: "cht-bg-rose-50 cht-text-rose-600 cht-border-rose-200/80",
          urlPlaceholder: "https://www.youtube.com/watch?v=...",
          urlLabel: "YouTube Video URL",
          titlePlaceholder: "e.g. Masterclass Walkthrough (Optional)",
        };
      case "drive":
        return {
          title: "Add Google Drive Resource",
          subtitle: "Attach shared files, project assets, or folders from Drive",
          icon: <GoogleDriveIcon className="cht-w-6 cht-h-6" />,
          iconBg: "cht-bg-blue-50",
          badge: "Google Drive",
          badgeColor: "cht-bg-amber-50 cht-text-amber-700 cht-border-amber-200/80",
          urlPlaceholder: "https://drive.google.com/...",
          urlLabel: "Google Drive Share Link",
          titlePlaceholder: "e.g. Starter Code & Assets Folder",
        };
      case "doc":
      case "slide":
      case "sheet":
      case "form":
      case "drawing":
      case "vids": {
        const appName = (type || "").charAt(0).toUpperCase() + (type || "").slice(1);
        const appIcon = 
          type === "doc" ? <GoogleDocsIcon className="cht-w-6 cht-h-6" /> :
          type === "slide" ? <GoogleSlidesIcon className="cht-w-6 cht-h-6" /> :
          type === "sheet" ? <GoogleSheetsIcon className="cht-w-6 cht-h-6" /> :
          type === "form" ? <GoogleFormsIcon className="cht-w-6 cht-h-6" /> :
          type === "drawing" ? <GoogleDrawingsIcon className="cht-w-6 cht-h-6" /> :
          <GoogleVidsIcon className="cht-w-6 cht-h-6" />;
        return {
          title: `Add Google ${appName}`,
          subtitle: `Attach a collaborative Google ${appName} for students`,
          icon: appIcon,
          iconBg: "cht-bg-slate-50",
          badge: `Google ${appName}`,
          badgeColor: "cht-bg-indigo-50 cht-text-indigo-700 cht-border-indigo-200/80",
          urlPlaceholder: "https://docs.google.com/...",
          urlLabel: `Google ${appName} Link`,
          titlePlaceholder: `e.g. Course Companion ${appName}`,
        };
      }
      case "link":
      default:
        return {
          title: "Add Web Link",
          subtitle: "Attach external articles, documentation, or repositories",
          icon: <LinkIcon size={22} className="cht-text-[#3e57da]" />,
          iconBg: "cht-bg-blue-50 cht-text-[#3e57da]",
          badge: "Web Link",
          badgeColor: "cht-bg-blue-50 cht-text-[#3e57da] cht-border-blue-200/80",
          urlPlaceholder: "https://example.com/docs",
          urlLabel: "Resource Web URL",
          titlePlaceholder: "e.g. Official Documentation",
        };
    }
  };

  if (typeof document === "undefined") return null;

  const config = type ? getModalConfig() : null;

  return createPortal(
    <AnimatePresence>
      {isOpen && type && config && (
        <div
          className="cht-fixed cht-inset-0 cht-z-[99999] cht-w-screen cht-h-screen cht-flex cht-items-center cht-justify-center cht-p-4 sm:cht-p-6"
          role="dialog"
          aria-modal="true"
        >
          {/* Full Screen Blurred Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="cht-fixed cht-inset-0 cht-w-full cht-h-full cht-bg-slate-950/75 cht-backdrop-blur-md cursor-pointer"
          />

          {/* Modal Dialog */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 15 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className="cht-relative cht-w-full cht-max-w-xl sm:cht-max-w-2xl cht-bg-white cht-rounded-[28px] cht-border cht-border-gray-100 cht-shadow-2xl cht-overflow-hidden cht-z-10 cht-flex cht-flex-col cht-max-h-[90vh]"
          >
            {/* Header */}
            <div className="cht-p-5 sm:cht-px-6 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-gap-4 cht-bg-slate-50/70">
              <div className="cht-flex cht-items-center cht-gap-3.5 cht-min-w-0">
                <div className={`cht-w-11 cht-h-11 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-shadow-sm ${config.iconBg}`}>
                  {config.icon}
                </div>
                <div className="cht-min-w-0">
                  <div className="cht-flex cht-items-center cht-gap-2">
                    <h3 className="cht-text-base sm:cht-text-lg cht-font-bold cht-text-slate-900 !cht-font-heading cht-truncate">
                      {config.title}
                    </h3>
                    <span className={`cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-px-2 cht-py-0.5 cht-rounded-full cht-border ${config.badgeColor}`}>
                      {config.badge}
                    </span>
                  </div>
                  <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5 cht-truncate">
                    {config.subtitle}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={onClose}
                className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-white cht-border cht-border-gray-200 hover:cht-bg-slate-100 cht-text-slate-400 hover:cht-text-slate-700 cht-flex cht-items-center cht-justify-center cht-transition-colors cht-shrink-0 cursor-pointer"
                title="Close (Esc)"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body Form */}
            <div className="cht-p-5 sm:cht-p-6 cht-space-y-4 cht-overflow-y-auto">
              {/* Tip for Google Docs creation */}
              {["doc", "slide", "sheet", "form"].includes(type) && (
                <div className="cht-p-3.5 cht-rounded-2xl cht-bg-indigo-50/60 cht-border cht-border-indigo-100 cht-flex cht-items-center cht-justify-between cht-gap-3">
                  <div className="cht-text-xs cht-text-slate-600">
                    Need to create a new file first?
                  </div>
                  <a
                    href={`https://${type === "doc" ? "docs.new" : type === "slide" ? "slides.new" : type === "sheet" ? "sheets.new" : "forms.new"}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-bg-white cht-border cht-border-indigo-200 hover:cht-border-indigo-400 cht-text-[#3e57da] cht-text-xs cht-font-bold cht-rounded-xl cht-shadow-sm hover:cht-shadow cht-transition-all cht-shrink-0"
                  >
                    Create on Google ↗
                  </a>
                </div>
              )}

              {/* URL Input */}
              <div>
                <Input
                  label={config.urlLabel}
                  placeholder={config.urlPlaceholder}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
                  autoFocus
                  leftIcon={<LinkIcon size={16} />}
                  required
                />
              </div>

              {/* YouTube Live Detection Preview */}
              {type === "youtube" && url && ytId && (
                <div className="cht-flex cht-items-center cht-gap-3.5 cht-p-3 cht-bg-slate-50 cht-rounded-2xl cht-border cht-border-rose-100">
                  <div className="cht-w-24 cht-h-14 cht-rounded-xl cht-bg-black cht-overflow-hidden cht-relative cht-shrink-0 cht-flex cht-items-center cht-justify-center">
                    <img
                      src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                      alt="YouTube Preview"
                      className="cht-w-full cht-h-full cht-object-cover"
                    />
                    <div className="cht-absolute cht-inset-0 cht-flex cht-items-center cht-justify-center cht-bg-black/30">
                      <PlayCircle size={20} className="cht-text-white" />
                    </div>
                  </div>
                  <div className="cht-min-w-0">
                    <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-rose-600 cht-bg-rose-50 cht-px-2 cht-py-0.5 cht-rounded-md cht-border cht-border-rose-100">
                      <Check size={10} /> Valid Video Stream
                    </span>
                    <p className="cht-text-[11px] cht-text-slate-500 cht-mt-1 cht-truncate">
                      Thumbnail and video player will be available to students.
                    </p>
                  </div>
                </div>
              )}

              {/* Title Input */}
              <div>
                <Input
                  label="Display Title / Label (Optional)"
                  placeholder={config.titlePlaceholder}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
                  leftIcon={<FileText size={16} />}
                  helperText="If left blank, a descriptive label will be generated automatically."
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="cht-p-4 sm:cht-px-6 cht-bg-slate-50/80 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-end cht-gap-3">
              <Button
                size="sm"
                variant="ghost"
                className="cht-rounded-xl cht-px-4"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                disabled={!url.trim()}
                className="cht-rounded-xl cht-px-5"
                onClick={handleConfirm}
                leftIcon={<Check size={15} />}
              >
                Attach to Topic
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// --- Module Card Component ---
const ModuleEditorCard = ({
  module,
  moduleIndex,
  totalModules = 1,
  onMoveModule,
  onMoveLesson,
  onUpdateTitle,
  onDeleteModule,
  onAddLesson,
  onUpdateLesson,
  onDeleteLesson,
  onAddExercise,
  onUpdateExercise,
  onDeleteExercise,
  isSuperAdmin = false,
  onApproveItem,
  courseTemplateRepo = "",
  courseTitle = "",
  courseLevel = "Beginner",
  geminiApiKey = "",
  geminiModel = "gemini-2.5-pro",
  geminiSystemPrompt = "",
}: {
  module: ModuleData;
  moduleIndex: number;
  totalModules?: number;
  onMoveModule?: (fromIndex: number, toIndex: number) => Promise<void>;
  onMoveLesson?: (moduleId: string | number, fromIndex: number, toIndex: number) => Promise<void>;
  onUpdateTitle: (mId: string | number, title: string) => Promise<void>;
  onDeleteModule: (mId: string | number) => Promise<void>;
  onAddLesson: (mId: string | number, data: Partial<LessonData>) => Promise<void>;
  onUpdateLesson: (lId: string | number, data: Partial<LessonData>) => Promise<void>;
  onDeleteLesson: (lId: string | number) => Promise<void>;
  onAddExercise: (mId: string | number, data: Partial<ExerciseData>) => Promise<void>;
  onUpdateExercise: (eId: string | number, data: Partial<ExerciseData>) => Promise<void>;
  onDeleteExercise: (eId: string | number) => Promise<void>;
  isSuperAdmin?: boolean;
  onApproveItem?: (
    itemType: "module" | "lesson" | "exercise",
    itemId: string | number,
    action: "approve" | "confirm_delete" | "restore"
  ) => Promise<void>;
  courseTemplateRepo?: string;
  courseTitle?: string;
  courseLevel?: string;
  geminiApiKey?: string;
  geminiModel?: string;
  geminiSystemPrompt?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [moduleTitle, setModuleTitle] = useState(module.title);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [approvingKey, setApprovingKey] = useState<string | null>(null);

  const handleAction = async (
    itemType: "module" | "lesson" | "exercise",
    itemId: string | number,
    action: "approve" | "confirm_delete" | "restore"
  ) => {
    if (!onApproveItem) return;
    const key = `${itemType}-${itemId}-${action}`;
    setApprovingKey(key);
    try {
      await onApproveItem(itemType, itemId, action);
    } finally {
      setApprovingKey(null);
    }
  };

  // Lesson Form State
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<string | number | null>(null);
  const [lessonForm, setLessonForm] = useState<{
    title: string;
    duration: string;
    video_url: string;
    content: string;
    attachments: TopicAttachment[];
  }>({
    title: "",
    duration: "15 mins",
    video_url: "",
    content: "",
    attachments: [],
  });
  const [isSavingLesson, setIsSavingLesson] = useState(false);
  const [isSavingExercise, setIsSavingExercise] = useState(false);

  // Exercise Form State
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [editingExerciseId, setEditingExerciseId] = useState<string | number | null>(null);
  const [exerciseForm, setExerciseForm] = useState({
    title: "",
    exercise_task: "",
    exercise_deadline: "",
    instructor_feedback: "",
    document_upload: "",
    requires_autograder: false,
    autograder_template_repo: "",
    autograder_module_name: "",
  });

  // Attach dialog / popover states
  const [activeAttachModal, setActiveAttachModal] = useState<
    "drive" | "youtube" | "link" | "doc" | "slide" | "sheet" | "form" | "drawing" | "vids" | null
  >(null);
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const createMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (createMenuRef.current && !createMenuRef.current.contains(e.target as Node)) {
        setShowCreateMenu(false);
      }
    };
    if (showCreateMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCreateMenu]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingFile(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const res = await uploadCurriculumAsset(file);
        const ext = file.name.split(".").pop()?.toLowerCase() || res.format || "file";
        const newAtt: TopicAttachment = {
          id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
          type: "upload",
          title: res.name || file.name,
          url: res.url,
          file_size: formatFileSize(res.bytes || file.size),
          file_type: ext,
        };
        setLessonForm(prev => ({
          ...prev,
          attachments: [...prev.attachments, newAtt],
        }));
      }
    } catch (err) {
      console.error("Failed to upload curriculum asset", err);
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setLessonForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== id),
    }));
  };

  const handleSaveModuleTitle = async () => {
    if (moduleTitle.trim() && moduleTitle !== module.title) {
      await onUpdateTitle(module.id!, moduleTitle);
    }
    setIsEditingTitle(false);
  };

  const handleStartEditLesson = (lesson: LessonData) => {
    setEditingLessonId(lesson.id!);
    let initialAttachments: TopicAttachment[] = Array.isArray(lesson.attachments) ? [...lesson.attachments] : [];
    if (initialAttachments.length === 0 && lesson.video_url) {
      initialAttachments.push({
        id: `att-legacy-${Date.now()}`,
        type: "youtube",
        title: "Video Lesson",
        url: lesson.video_url,
      });
    }

    setLessonForm({
      title: lesson.title,
      duration: lesson.duration || "15 mins",
      video_url: lesson.video_url || "",
      content: lesson.content || "",
      attachments: initialAttachments,
    });
    setShowAddLesson(true);
  };

  const handleSaveLesson = async () => {
    if (!lessonForm.title.trim() || isSavingLesson) return;
    setIsSavingLesson(true);
    try {
      const firstVideo = lessonForm.attachments.find(a => a.type === "youtube" || a.type === "video");
      const payload: Partial<LessonData> = {
        title: lessonForm.title,
        duration: lessonForm.duration || "15 mins",
        content: lessonForm.content,
        attachments: lessonForm.attachments,
        video_url: firstVideo ? firstVideo.url : (lessonForm.video_url || ""),
      };
      if (editingLessonId) {
        await onUpdateLesson(editingLessonId, payload);
      } else {
        await onAddLesson(module.id!, payload);
      }
      setShowAddLesson(false);
      setEditingLessonId(null);
      setLessonForm({ title: "", duration: "15 mins", video_url: "", content: "", attachments: [] });
    } catch (err) {
      console.error("Failed to save lesson:", err);
    } finally {
      setIsSavingLesson(false);
    }
  };

  const handleStartEditExercise = (exercise: ExerciseData) => {
    setEditingExerciseId(exercise.id!);
    setExerciseForm({
      title: exercise.title,
      exercise_task: exercise.exercise_task,
      exercise_deadline: exercise.exercise_deadline || "",
      instructor_feedback: exercise.instructor_feedback || "",
      document_upload: exercise.document_upload || "",
      requires_autograder: Boolean(exercise.requires_autograder),
      autograder_template_repo: exercise.autograder_template_repo || "",
      autograder_module_name: exercise.autograder_module_name || "",
    });
    setShowAddExercise(true);
  };

  const handleSaveExercise = async () => {
    if (!exerciseForm.title.trim() || isSavingExercise) return;
    setIsSavingExercise(true);
    try {
      const sanitizedForm = {
        ...exerciseForm,
        exercise_deadline: exerciseForm.exercise_deadline ? exerciseForm.exercise_deadline : undefined,
        document_upload: exerciseForm.document_upload ? exerciseForm.document_upload : undefined,
        autograder_template_repo: exerciseForm.requires_autograder
          ? (exerciseForm.autograder_template_repo.trim() || courseTemplateRepo.trim())
          : "",
        autograder_module_name: exerciseForm.requires_autograder
          ? exerciseForm.autograder_module_name.trim()
          : "",
      };
      if (editingExerciseId) {
        await onUpdateExercise(editingExerciseId, sanitizedForm);
      } else {
        await onAddExercise(module.id!, sanitizedForm);
      }
      setShowAddExercise(false);
      setEditingExerciseId(null);
      setExerciseForm({
        title: "",
        exercise_task: "",
        exercise_deadline: "",
        instructor_feedback: "",
        document_upload: "",
        requires_autograder: false,
        autograder_template_repo: "",
        autograder_module_name: "",
      });
    } catch (err) {
      console.error("Failed to save exercise:", err);
    } finally {
      setIsSavingExercise(false);
    }
  };

  // --- Gemini Pro Copilot for Topic Lesson & Module Exercise ---
  const [isGeneratingLessonAI, setIsGeneratingLessonAI] = useState(false);
  const [lessonAIPrompt, setLessonAIPrompt] = useState("");
  const [lessonPromptMode, setLessonPromptMode] = useState<"augment" | "override">("augment");
  const [lessonContentAction, setLessonContentAction] = useState<"append" | "refine" | "replace">("append");
  const [lessonUndoContent, setLessonUndoContent] = useState<string | null>(null);
  const [lessonAIError, setLessonAIError] = useState<string | null>(null);

  const [isGeneratingExerciseAI, setIsGeneratingExerciseAI] = useState(false);
  const [exerciseAIPrompt, setExerciseAIPrompt] = useState("");
  const [exercisePromptMode, setExercisePromptMode] = useState<"augment" | "override">("augment");
  const [exerciseContentAction, setExerciseContentAction] = useState<"append" | "refine" | "replace">("append");
  const [exerciseUndoTask, setExerciseUndoTask] = useState<string | null>(null);
  const [exerciseAIError, setExerciseAIError] = useState<string | null>(null);
  const [generatedExerciseFiles, setGeneratedExerciseFiles] = useState<{
    starter_code: string;
    pytest_code: string;
  } | null>(null);
  const [copiedCodeBlock, setCopiedCodeBlock] = useState<"starter" | "pytest" | null>(null);

  const appendLessonPromptChip = (snippet: string) => {
    setLessonAIPrompt((prev) => (prev.trim() ? `${prev.trim()}\n- ${snippet}` : `- ${snippet}`));
  };

  const appendExercisePromptChip = (snippet: string) => {
    setExerciseAIPrompt((prev) => (prev.trim() ? `${prev.trim()}\n- ${snippet}` : `- ${snippet}`));
  };

  const handleGenerateLessonWithGemini = async () => {
    setLessonAIError(null);
    setIsGeneratingLessonAI(true);
    const previousContent = lessonForm.content || "";
    try {
      const targetTitle = lessonForm.title.trim() || `Topic ${lessons.length + 1}: Core Concepts`;
      const effectiveAction = previousContent.trim() ? lessonContentAction : "replace";
      const res = await generateAICurriculumContent<{
        title: string;
        duration: string;
        content: string;
      }>({
        action: "lesson",
        api_key: geminiApiKey || undefined,
        model: geminiModel || undefined,
        course_title: courseTitle || "Technical Course",
        module_title: module.title,
        lesson_title: targetTitle,
        level: courseLevel || "Beginner",
        custom_prompt: lessonAIPrompt.trim(),
        system_prompt: geminiSystemPrompt || undefined,
        prompt_mode: lessonPromptMode,
        content_action: effectiveAction,
        existing_content: previousContent,
      });
      if (res?.data) {
        setLessonUndoContent(previousContent);
        const generatedHtml = (res.data.content || "").trim();
        setLessonForm((prev) => {
          const prevClean = (prev.content || "").trim();
          let nextContent = generatedHtml || prev.content;
          if (effectiveAction === "append" && prevClean && generatedHtml) {
            nextContent = `${prevClean}\n\n${generatedHtml}`;
          }
          return {
            ...prev,
            title: prev.title.trim() ? prev.title : res.data.title || targetTitle,
            duration: res.data.duration || prev.duration || "20 mins",
            content: nextContent,
          };
        });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to generate topic notes with Gemini.";
      setLessonAIError(msg);
    } finally {
      setIsGeneratingLessonAI(false);
    }
  };

  const handleGenerateExerciseWithGemini = async () => {
    setExerciseAIError(null);
    setIsGeneratingExerciseAI(true);
    const previousTask = exerciseForm.exercise_task || "";
    try {
      const targetTitle =
        exerciseForm.title.trim() || `Module ${moduleIndex + 1} Practical Coding Challenge`;
      const defaultFile =
        exerciseForm.autograder_module_name.trim() ||
        `module_0${moduleIndex + 1}/exercise_0${exercises.length + 1}.py`;
      const effectiveAction = previousTask.trim() ? exerciseContentAction : "replace";

      const res = await generateAICurriculumContent<{
        title: string;
        exercise_task: string;
        autograder_module_name: string;
        starter_code: string;
        pytest_code: string;
      }>({
        action: "exercise",
        api_key: geminiApiKey || undefined,
        model: geminiModel || undefined,
        course_title: courseTitle || "Technical Course",
        module_title: module.title,
        exercise_title: targetTitle,
        target_file: defaultFile,
        level: courseLevel || "Beginner",
        custom_prompt: exerciseAIPrompt.trim(),
        system_prompt: geminiSystemPrompt || undefined,
        prompt_mode: exercisePromptMode,
        content_action: effectiveAction,
        existing_content: previousTask,
      });

      if (res?.data) {
        setExerciseUndoTask(previousTask);
        const generatedTaskHtml = (res.data.exercise_task || "").trim();
        setExerciseForm((prev) => {
          const prevClean = (prev.exercise_task || "").trim();
          let nextTask = generatedTaskHtml || prev.exercise_task;
          if (effectiveAction === "append" && prevClean && generatedTaskHtml) {
            nextTask = `${prevClean}\n\n${generatedTaskHtml}`;
          }
          return {
            ...prev,
            title: prev.title.trim() ? prev.title : res.data.title || targetTitle,
            exercise_task: nextTask,
            requires_autograder: true,
            autograder_module_name: res.data.autograder_module_name || defaultFile,
          };
        });
        if (res.data.starter_code || res.data.pytest_code) {
          setGeneratedExerciseFiles({
            starter_code: res.data.starter_code || "",
            pytest_code: res.data.pytest_code || "",
          });
        }
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to generate exercise specification with Gemini.";
      setExerciseAIError(msg);
    } finally {
      setIsGeneratingExerciseAI(false);
    }
  };

  const lessons = module.lessons || [];
  const exercises = module.exercise_module || (module as any).exercises || [];

  return (
    <GlassCard className="cht-rounded-[24px] cht-overflow-hidden cht-border cht-border-gray-100 cht-transition-all">
      {/* Module Header Bar */}
      <div className="cht-p-5 cht-bg-slate-50/80 cht-border-b cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-flex cht-items-center cht-gap-3 cht-flex-1">
          <button 
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="cht-w-8 cht-h-8 cht-rounded-lg cht-bg-white cht-border cht-border-gray-200 cht-flex cht-items-center cht-justify-center cht-text-slate-600 hover:cht-bg-slate-100"
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-px-2.5 cht-py-1 cht-rounded-md">
            Module {moduleIndex + 1}
          </span>

          {isEditingTitle ? (
            <div className="cht-flex cht-items-center cht-gap-2 cht-flex-1">
              <Input
                placeholder="Module title..."
                value={moduleTitle}
                onChange={(e) => setModuleTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSaveModuleTitle()}
                autoFocus
                containerClassName="cht-flex-1"
                inputClassName="!cht-py-1.5 !cht-text-sm !cht-font-bold"
              />
              <Button size="sm" variant="primary" className="cht-rounded-xl cht-px-3" onClick={handleSaveModuleTitle}>
                <Check size={14} />
              </Button>
            </div>
          ) : (
            <h4 
              onClick={() => setIsEditingTitle(true)}
              className="cht-text-base cht-font-bold cht-text-slate-900 hover:cht-text-[#3e57da] cht-cursor-pointer cht-transition-colors !cht-font-heading"
            >
              {module.title}
            </h4>
          )}
        </div>

        <div className="cht-flex cht-items-center cht-gap-2.5">
          {module.pending_deletion ? (
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-rose-100 cht-text-rose-700 cht-border cht-border-rose-200">
              Pending Deletion Approval
            </span>
          ) : module.is_approved === false ? (
            <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-100 cht-text-amber-800 cht-border cht-border-amber-200">
              Pending SuperAdmin Approval
            </span>
          ) : null}

          <span className="cht-text-xs cht-font-semibold cht-text-slate-400">
            {lessons.length} {lessons.length === 1 ? "lesson" : "lessons"} • {exercises.length} {exercises.length === 1 ? "task" : "tasks"}
          </span>

          {isSuperAdmin && totalModules > 1 && onMoveModule && (
            <div className="cht-flex cht-items-center cht-gap-0.5 cht-bg-white cht-p-0.5 cht-rounded-lg cht-border cht-border-slate-200">
              <button
                type="button"
                onClick={() => onMoveModule(moduleIndex, moduleIndex - 1)}
                disabled={moduleIndex === 0}
                className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                  moduleIndex === 0
                    ? "cht-text-slate-300 cht-cursor-not-allowed"
                    : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                }`}
                title="Move Module Up"
              >
                <ArrowUp size={13} />
              </button>
              <button
                type="button"
                onClick={() => onMoveModule(moduleIndex, moduleIndex + 1)}
                disabled={moduleIndex === totalModules - 1}
                className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                  moduleIndex === totalModules - 1
                    ? "cht-text-slate-300 cht-cursor-not-allowed"
                    : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                }`}
                title="Move Module Down"
              >
                <ArrowDown size={13} />
              </button>
            </div>
          )}

          {isSuperAdmin && module.pending_deletion && onApproveItem ? (
            <div className="cht-flex cht-items-center cht-gap-1.5">
              <Button
                size="sm"
                variant="primary"
                className="!cht-py-1 !cht-px-2.5 cht-rounded-lg cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-[11px] cht-font-bold cht-text-white"
                onClick={() => handleAction("module", module.id!, "confirm_delete")}
                isLoading={approvingKey === `module-${module.id}-confirm_delete`}
              >
                Confirm Delete
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="!cht-py-1 !cht-px-2.5 cht-rounded-lg cht-text-[11px] cht-font-bold"
                onClick={() => handleAction("module", module.id!, "restore")}
                isLoading={approvingKey === `module-${module.id}-restore`}
              >
                Restore
              </Button>
            </div>
          ) : isSuperAdmin && module.is_approved === false && onApproveItem ? (
            <Button
              size="sm"
              variant="primary"
              className="!cht-py-1 !cht-px-2.5 cht-rounded-lg cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-[11px] cht-font-bold cht-text-white"
              onClick={() => handleAction("module", module.id!, "approve")}
              isLoading={approvingKey === `module-${module.id}-approve`}
            >
              Approve Module
            </Button>
          ) : (
            <button 
              type="button"
              disabled={Boolean(module.pending_deletion)}
              onClick={() => onDeleteModule(module.id!)}
              className={`cht-w-8 cht-h-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                module.pending_deletion 
                  ? "cht-text-slate-300 cht-cursor-not-allowed" 
                  : "cht-text-slate-400 hover:cht-text-rose-600 hover:cht-bg-rose-50"
              }`}
              title={module.pending_deletion ? "Deletion request pending SuperAdmin approval" : "Delete Module"}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Module Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="cht-p-5 cht-space-y-6"
          >
            {/* Section A: Lessons (Topics) */}
            <div className="cht-space-y-3">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <PlayCircle size={16} className="cht-text-[#3e57da]" />
                  <h5 className="cht-text-xs cht-font-black cht-text-slate-800 cht-uppercase cht-tracking-wider">
                    Topics & Video Lessons ({lessons.length})
                  </h5>
                </div>
                {!showAddLesson && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingLessonId(null);
                      setLessonForm({ title: "", duration: "15 mins", video_url: "", content: "", attachments: [] });
                      setShowAddLesson(true);
                    }}
                    className="cht-text-xs cht-font-bold cht-text-[#3e57da] hover:cht-underline cht-flex cht-items-center cht-gap-1"
                  >
                    <Plus size={13} /> Add Topic
                  </button>
                )}
              </div>

              {/* Lesson Form (Add / Edit) */}
              {showAddLesson && (
                <div className="cht-p-5 cht-rounded-2xl cht-bg-indigo-50/40 cht-border cht-border-[#3e57da]/20 cht-space-y-4">
                  <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-[#3e57da]/10 cht-pb-2">
                    <span className="cht-text-xs cht-font-bold cht-text-[#3e57da]">
                      {editingLessonId ? "Edit Topic" : "New Topic / Lesson"}
                    </span>
                    <button 
                      type="button"
                      onClick={() => setShowAddLesson(false)}
                      className="cht-text-slate-400 hover:cht-text-slate-600"
                    >
                      <X size={15} />
                    </button>
                  </div>

                  {/* Title & Duration */}
                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-4">
                    <div className="sm:cht-col-span-2">
                      <Input
                        label="Topic Title"
                        placeholder="e.g. Setting up your Next.js project..."
                        value={lessonForm.title}
                        onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                        leftIcon={<FileText size={16} />}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        label="Estimated Duration"
                        placeholder="e.g. 15 mins"
                        value={lessonForm.duration}
                        onChange={(e) => setLessonForm({ ...lessonForm, duration: e.target.value })}
                        leftIcon={<Clock size={16} />}
                      />
                    </div>
                  </div>

                  {/* SuperAdmin Gemini Pro Topic Lesson Writer */}
                  {isSuperAdmin && (
                    <div className="cht-p-4 cht-rounded-xl cht-bg-white cht-border cht-border-indigo-200/80 cht-shadow-sm cht-space-y-3">
                      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-2">
                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <Sparkles size={15} className="cht-text-[#3e57da]" />
                          <span className="cht-text-xs cht-font-black cht-text-slate-800">
                            Gemini Pro Topic Writer
                          </span>
                          <span className="cht-text-[10px] cht-font-bold cht-bg-indigo-50 cht-text-[#3e57da] cht-px-2 cht-py-0.5 cht-rounded-full">
                            {geminiModel}
                          </span>
                          {Boolean(lessonForm.content?.trim()) && (
                            <span className="cht-text-[10px] cht-font-bold cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200/80 cht-px-2 cht-py-0.5 cht-rounded-full">
                              Existing Notes Detected
                            </span>
                          )}
                        </div>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <div className="cht-inline-flex cht-rounded-lg cht-bg-slate-100 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setLessonContentAction("append")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                lessonContentAction === "append"
                                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Keep your existing lesson notes and append new sections at the bottom"
                            >
                              ➕ Add to Existing
                            </button>
                            <button
                              type="button"
                              onClick={() => setLessonContentAction("refine")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                lessonContentAction === "refine"
                                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Polish, restructure, and expand your existing draft while preserving all original points"
                            >
                              ✨ Refine Draft
                            </button>
                            <button
                              type="button"
                              onClick={() => setLessonContentAction("replace")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                lessonContentAction === "replace"
                                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Generate complete lesson notes from scratch and replace the editor content"
                            >
                              🔄 Replace
                            </button>
                          </div>

                          <div className="cht-inline-flex cht-rounded-lg cht-bg-slate-100 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setLessonPromptMode("augment")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                lessonPromptMode === "augment"
                                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Combine your custom prompt with Codehub's structured walkthrough template"
                            >
                              Augment Template
                            </button>
                            <button
                              type="button"
                              onClick={() => setLessonPromptMode("override")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                lessonPromptMode === "override"
                                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Use your custom prompt as the primary directive"
                            >
                              Full Custom Prompt
                            </button>
                          </div>

                          {lessonUndoContent !== null && (
                            <button
                              type="button"
                              onClick={() => {
                                setLessonForm((prev) => ({ ...prev, content: lessonUndoContent }));
                                setLessonUndoContent(null);
                              }}
                              className="cht-px-2.5 cht-py-1.5 cht-rounded-xl cht-bg-amber-50 hover:cht-bg-amber-100 cht-border cht-border-amber-200 cht-text-[11px] cht-font-bold cht-text-amber-800 cht-transition-colors"
                              title="Restore topic notes to their state before the last Gemini change"
                            >
                              ↩ Undo AI
                            </button>
                          )}

                          <Button
                            size="sm"
                            variant="primary"
                            className="!cht-py-1.5 !cht-px-3 cht-rounded-xl cht-text-xs"
                            leftIcon={<Sparkles size={13} />}
                            onClick={handleGenerateLessonWithGemini}
                            isLoading={isGeneratingLessonAI}
                          >
                            {!lessonForm.content?.trim()
                              ? "Draft Topic Notes"
                              : lessonContentAction === "append"
                              ? "Add to Topic Notes"
                              : lessonContentAction === "refine"
                              ? "Refine & Expand Notes"
                              : "Replace Topic Notes"}
                          </Button>
                        </div>
                      </div>

                      <textarea
                        rows={3}
                        placeholder={
                          lessonForm.content?.trim() && lessonContentAction === "append"
                            ? "Tell Gemini Pro what to ADD below your existing notes (e.g., 'Add 2 more practical code examples, an edge-case debugging section, and a quick practice quiz at the end')..."
                            : lessonForm.content?.trim() && lessonContentAction === "refine"
                            ? "Tell Gemini Pro how to REFINE & EXPAND your existing draft (e.g., 'Turn my rough bullet points into a complete tutorial with code blocks and explanations')..."
                            : lessonPromptMode === "override"
                            ? "Write your full custom prompt for Gemini Pro (e.g., 'Write a Socratic tutorial on Python decorators with 3 progressive examples, a performance benchmark, and a summary table')..."
                            : "Write custom instructions or paste your lecture outline to guide Gemini Pro (e.g., 'Include 3 annotated code snippets, common pitfalls, and a real-world fintech example')..."
                        }
                        value={lessonAIPrompt}
                        onChange={(e) => setLessonAIPrompt(e.target.value)}
                        className="cht-w-full cht-px-3 cht-py-2 cht-text-xs cht-rounded-xl cht-border cht-border-slate-200 focus:cht-outline-none focus:cht-border-[#3e57da] cht-resize-y"
                      />

                      <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-flex-wrap">
                        <div className="cht-flex cht-items-center cht-gap-1.5 cht-flex-wrap">
                          <span className="cht-text-[10px] cht-font-bold cht-text-slate-400">
                            Quick Prompts:
                          </span>
                          {[
                            "Add 2 more annotated code examples",
                            "Add common pitfalls & debugging checklist",
                            "Add real-world fintech/API case study",
                            "Add a summary cheat-sheet & practice quiz",
                          ].map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => appendLessonPromptChip(chip)}
                              className="cht-text-[10px] cht-font-semibold cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-slate-100 hover:cht-bg-indigo-50 cht-text-slate-600 hover:cht-text-[#3e57da] cht-transition-colors"
                            >
                              + {chip}
                            </button>
                          ))}
                        </div>
                        {lessonAIPrompt && (
                          <button
                            type="button"
                            onClick={() => setLessonAIPrompt("")}
                            className="cht-text-[10px] cht-font-semibold cht-text-slate-400 hover:cht-text-rose-600"
                          >
                            Clear Prompt
                          </button>
                        )}
                      </div>

                      {lessonAIError && (
                        <p className="cht-text-[11px] cht-font-semibold cht-text-rose-600">
                          {lessonAIError}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Lesson Notes / Reading Content */}
                  <div>
                    <RichTextEditor
                      key={editingLessonId ? `lesson-${editingLessonId}` : "new-lesson"}
                      label="Lesson Notes / Reading Content"
                      placeholder="Provide comprehensive walkthrough notes, code snippets, and instructions for the student..."
                      value={lessonForm.content}
                      onChange={(data) => setLessonForm((prev) => ({ ...prev, content: data }))}
                      minHeight="220px"
                      isLoading={isGeneratingLessonAI}
                      loadingText={isGeneratingLessonAI ? "Gemini Pro is drafting topic notes..." : undefined}
                    />
                  </div>

                  {/* Google Classroom-style Attach Section */}
                  <div className="cht-p-4 cht-rounded-2xl cht-bg-white cht-border cht-border-gray-200/90 cht-shadow-sm cht-space-y-3">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <span className="cht-text-xs cht-font-semibold cht-text-slate-700">Attach</span>
                      {(lessonForm.attachments || []).length > 0 && (
                        <span className="cht-text-[11px] cht-font-semibold cht-text-slate-400">
                          {(lessonForm.attachments || []).length} attached
                        </span>
                      )}
                    </div>

                    {/* Circular Action Buttons Row */}
                    <div className="cht-flex cht-items-center cht-gap-5 sm:cht-gap-6 cht-relative cht-pt-1">
                      {/* Drive */}
                      <div className="cht-flex cht-flex-col cht-items-center cht-gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveAttachModal("drive");
                            setShowCreateMenu(false);
                          }}
                          className="cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-full cht-bg-white cht-border cht-border-gray-200 hover:cht-border-blue-400 hover:cht-shadow-md cht-flex cht-items-center cht-justify-center cht-transition-all active:cht-scale-95"
                          title="Attach from Google Drive"
                        >
                          <GoogleDriveIcon className="cht-w-5 cht-h-5 sm:cht-w-6 sm:cht-h-6" />
                        </button>
                        <span className="cht-text-[11px] cht-font-medium cht-text-slate-600">Drive</span>
                      </div>

                      {/* YouTube */}
                      <div className="cht-flex cht-flex-col cht-items-center cht-gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveAttachModal("youtube");
                            setShowCreateMenu(false);
                          }}
                          className="cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-full cht-bg-white cht-border cht-border-gray-200 hover:cht-border-rose-400 hover:cht-shadow-md cht-flex cht-items-center cht-justify-center cht-transition-all active:cht-scale-95"
                          title="Attach YouTube Video"
                        >
                          <YouTubeLogoIcon className="cht-w-5 cht-h-5 sm:cht-w-6 sm:cht-h-6" />
                        </button>
                        <span className="cht-text-[11px] cht-font-medium cht-text-slate-600">YouTube</span>
                      </div>

                      {/* Create (+ with popup) */}
                      <div ref={createMenuRef} className="cht-relative cht-flex cht-flex-col cht-items-center cht-gap-1.5">
                        <button
                          type="button"
                          onClick={() => setShowCreateMenu(!showCreateMenu)}
                          className="cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-full cht-bg-white cht-border cht-border-gray-200 hover:cht-border-blue-400 hover:cht-shadow-md cht-flex cht-items-center cht-justify-center cht-transition-all active:cht-scale-95"
                          title="Create Docs, Slides, Sheets, Forms"
                        >
                          <GoogleCreatePlusIcon className="cht-w-5 cht-h-5 sm:cht-w-6 sm:cht-h-6" />
                        </button>
                        <span className="cht-text-[11px] cht-font-medium cht-text-slate-600">Create</span>

                        {/* Create Popup Menu */}
                        <AnimatePresence>
                          {showCreateMenu && (
                            <motion.div
                              initial={{ opacity: 0, y: -6, scale: 0.96 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              exit={{ opacity: 0, y: -6, scale: 0.96 }}
                              transition={{ duration: 0.15 }}
                              className="cht-absolute cht-bottom-full cht-mb-2 cht-left-1/2 -cht-translate-x-1/2 sm:cht-left-0 sm:cht-translate-x-0 cht-w-44 cht-bg-white cht-rounded-2xl cht-shadow-2xl cht-border cht-border-gray-200 cht-py-2 cht-z-40"
                            >
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("doc"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-gap-3 hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <GoogleDocsIcon className="cht-w-5 cht-h-5" />
                                <span className="cht-text-xs cht-font-medium cht-text-slate-800">Docs</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("slide"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-gap-3 hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <GoogleSlidesIcon className="cht-w-5 cht-h-5" />
                                <span className="cht-text-xs cht-font-medium cht-text-slate-800">Slides</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("sheet"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-gap-3 hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <GoogleSheetsIcon className="cht-w-5 cht-h-5" />
                                <span className="cht-text-xs cht-font-medium cht-text-slate-800">Sheets</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("drawing"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-gap-3 hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <GoogleDrawingsIcon className="cht-w-5 cht-h-5" />
                                <span className="cht-text-xs cht-font-medium cht-text-slate-800">Drawings</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("form"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-gap-3 hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <GoogleFormsIcon className="cht-w-5 cht-h-5" />
                                <span className="cht-text-xs cht-font-medium cht-text-slate-800">Forms</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setActiveAttachModal("vids"); setShowCreateMenu(false); }}
                                className="cht-w-full cht-px-3.5 cht-py-2 cht-flex cht-items-center cht-justify-between hover:cht-bg-slate-50 cht-text-left cht-transition-colors"
                              >
                                <div className="cht-flex cht-items-center cht-gap-3">
                                  <GoogleVidsIcon className="cht-w-5 cht-h-5" />
                                  <span className="cht-text-xs cht-font-medium cht-text-slate-800">Vids</span>
                                </div>
                                <span className="cht-text-[9px] cht-font-bold cht-bg-[#1a73e8] cht-text-white cht-px-1.5 cht-py-0.2 cht-rounded-full">
                                  New
                                </span>
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Upload */}
                      <div className="cht-flex cht-flex-col cht-items-center cht-gap-1.5">
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          className="cht-hidden"
                          onChange={handleFileUpload}
                        />
                        <button
                          type="button"
                          disabled={isUploadingFile}
                          onClick={() => {
                            setShowCreateMenu(false);
                            fileInputRef.current?.click();
                          }}
                          className="cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-full cht-bg-white cht-border cht-border-gray-200 hover:cht-border-slate-500 hover:cht-shadow-md cht-flex cht-items-center cht-justify-center cht-text-slate-700 cht-transition-all active:cht-scale-95 disabled:cht-opacity-50"
                          title="Upload File (PDF, DOC, ZIP, Slides, Images)"
                        >
                          {isUploadingFile ? (
                            <Loader2 size={18} className="cht-animate-spin cht-text-[#3e57da]" />
                          ) : (
                            <Upload size={18} />
                          )}
                        </button>
                        <span className="cht-text-[11px] cht-font-medium cht-text-slate-600">
                          {isUploadingFile ? "..." : "Upload"}
                        </span>
                      </div>

                      {/* Link */}
                      <div className="cht-flex cht-flex-col cht-items-center cht-gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveAttachModal("link");
                            setShowCreateMenu(false);
                          }}
                          className="cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-full cht-bg-white cht-border cht-border-gray-200 hover:cht-border-slate-500 hover:cht-shadow-md cht-flex cht-items-center cht-justify-center cht-text-slate-700 cht-transition-all active:cht-scale-95"
                          title="Attach External Link"
                        >
                          <LinkIcon size={18} />
                        </button>
                        <span className="cht-text-[11px] cht-font-medium cht-text-slate-600">Link</span>
                      </div>
                    </div>

                    {/* Active Attach Modal */}
                    <AttachmentModal
                      isOpen={activeAttachModal !== null}
                      type={activeAttachModal}
                      onClose={() => setActiveAttachModal(null)}
                      onAttach={(newAtt) => {
                        setLessonForm((prev) => ({
                          ...prev,
                          attachments: [...(prev.attachments || []), newAtt],
                        }));
                      }}
                    />

                    {/* Attached Items List */}
                    {(lessonForm.attachments || []).length > 0 && (
                      <div className="cht-pt-2 cht-space-y-2">
                        <div className="cht-text-[10px] cht-font-bold cht-text-slate-400 cht-uppercase cht-tracking-wider">
                          Attached Materials ({(lessonForm.attachments || []).length})
                        </div>
                        <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-2">
                          {(lessonForm.attachments || []).map((att) => {
                            const ytId = att.type === "youtube" ? getYouTubeID(att.url) : null;
                            return (
                              <div
                                key={att.id}
                                className="cht-flex cht-items-center cht-justify-between cht-p-2.5 cht-bg-slate-50/80 hover:cht-bg-white cht-rounded-xl cht-border cht-border-slate-200/80 hover:cht-border-indigo-200 cht-transition-all group"
                              >
                                <div className="cht-flex cht-items-center cht-gap-2.5 cht-min-w-0 cht-flex-1">
                                  {att.type === "youtube" ? (
                                    <div className="cht-w-10 cht-h-7 cht-rounded-md cht-bg-slate-900 cht-overflow-hidden cht-relative cht-flex-shrink-0 cht-flex cht-items-center cht-justify-center">
                                      {ytId ? (
                                        <img
                                          src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
                                          alt={att.title}
                                          className="cht-w-full cht-h-full cht-object-cover"
                                        />
                                      ) : (
                                        <YouTubeLogoIcon className="cht-w-4 cht-h-4" />
                                      )}
                                    </div>
                                  ) : att.type === "drive" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-amber-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleDriveIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "doc" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-blue-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleDocsIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "slide" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-amber-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleSlidesIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "sheet" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-emerald-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleSheetsIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "form" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-purple-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleFormsIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "drawing" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-red-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleDrawingsIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "vids" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-indigo-50 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                                      <GoogleVidsIcon className="cht-w-4 cht-h-4" />
                                    </div>
                                  ) : att.type === "upload" || att.type === "file" ? (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-slate-200 cht-flex cht-items-center cht-justify-center cht-text-slate-600 cht-shrink-0">
                                      <FileText size={14} />
                                    </div>
                                  ) : (
                                    <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-slate-200 cht-flex cht-items-center cht-justify-center cht-text-slate-600 cht-shrink-0">
                                      <LinkIcon size={14} />
                                    </div>
                                  )}

                                  <div className="cht-min-w-0 cht-flex-1">
                                    <p className="cht-text-xs cht-font-semibold cht-text-slate-800 cht-truncate">
                                      {att.title}
                                    </p>
                                    <p className="cht-text-[10px] cht-text-slate-400 cht-truncate">
                                      {att.type === "youtube"
                                        ? "YouTube video"
                                        : att.type === "upload"
                                        ? `${(att.file_type || "file").toUpperCase()}${att.file_size ? ` • ${att.file_size}` : ""}`
                                        : att.url}
                                    </p>
                                  </div>
                                </div>

                                <div className="cht-flex cht-items-center cht-gap-1 cht-shrink-0">
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cht-w-6 cht-h-6 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-slate-400 hover:cht-text-[#3e57da] hover:cht-bg-indigo-50 cht-transition-colors"
                                    title="Open link in new tab"
                                  >
                                    <ExternalLink size={12} />
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachment(att.id)}
                                    className="cht-w-6 cht-h-6 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-text-slate-300 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-transition-colors"
                                    title="Remove attachment"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="cht-flex cht-justify-end cht-gap-2 cht-pt-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="cht-rounded-xl" 
                      onClick={() => setShowAddLesson(false)}
                      disabled={isSavingLesson}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      variant="primary" 
                      className="cht-rounded-xl" 
                      onClick={handleSaveLesson}
                      isLoading={isSavingLesson}
                      disabled={isSavingLesson}
                    >
                      {isSavingLesson
                        ? editingLessonId
                          ? "Saving Topic..."
                          : "Adding Topic..."
                        : editingLessonId
                        ? "Save Topic"
                        : "Add Topic"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Lessons List */}
              <div className="cht-space-y-2">
                {lessons.map((lesson, idx) => {
                  const atts = Array.isArray(lesson.attachments) ? lesson.attachments : [];
                  const hasLegacyVideo = lesson.video_url && !atts.some(a => a.url === lesson.video_url);
                  const videoCount = atts.filter(a => a.type === "youtube" || a.type === "video").length + (hasLegacyVideo ? 1 : 0);
                  const fileCount = atts.filter(a => a.type === "upload" || a.type === "file").length;
                  const linkCount = atts.filter(a => ["link", "drive", "doc", "slide", "sheet", "form", "drawing", "vids"].includes(a.type)).length;

                  return (
                    <div 
                      key={lesson.id || idx}
                      className="cht-flex cht-items-center cht-justify-between cht-p-3.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100 hover:cht-border-indigo-200 cht-transition-all"
                    >
                      <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                        <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-blue-50 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
                          {videoCount > 0 ? <Video size={14} /> : <FileText size={14} />}
                        </div>
                        <div className="cht-min-w-0">
                          <p className="cht-text-xs cht-font-bold cht-text-slate-800 cht-truncate">
                            {idx + 1}. {lesson.title}
                          </p>
                          <div className="cht-flex cht-items-center cht-flex-wrap cht-gap-1.5 cht-mt-0.5">
                            {lesson.duration && (
                              <span className="cht-text-[10px] cht-font-medium cht-text-slate-400">
                                {lesson.duration}
                              </span>
                            )}
                            {lesson.content && (
                              <span className="cht-text-[9px] cht-font-bold cht-bg-slate-100 cht-text-slate-600 cht-px-1.5 cht-py-0.2 cht-rounded">
                                Notes
                              </span>
                            )}
                            {videoCount > 0 && (
                              <span className="cht-text-[9px] cht-font-bold cht-bg-rose-50 cht-text-rose-600 cht-px-1.5 cht-py-0.2 cht-rounded">
                                {videoCount === 1 ? "Video" : `${videoCount} Videos`}
                              </span>
                            )}
                            {fileCount > 0 && (
                              <span className="cht-text-[9px] cht-font-bold cht-bg-amber-50 cht-text-amber-700 cht-px-1.5 cht-py-0.2 cht-rounded">
                                {fileCount === 1 ? "1 File" : `${fileCount} Files`}
                              </span>
                            )}
                            {linkCount > 0 && (
                              <span className="cht-text-[9px] cht-font-bold cht-bg-blue-50 cht-text-blue-600 cht-px-1.5 cht-py-0.2 cht-rounded">
                                {linkCount === 1 ? "1 Link" : `${linkCount} Links`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="cht-flex cht-items-center cht-gap-2">
                        {lesson.pending_deletion ? (
                          <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-rose-100 cht-text-rose-700 cht-border cht-border-rose-200">
                            Pending Deletion
                          </span>
                        ) : lesson.is_approved === false ? (
                          <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-amber-100 cht-text-amber-800 cht-border cht-border-amber-200">
                            Pending Approval
                          </span>
                        ) : null}

                        {isSuperAdmin && lesson.pending_deletion && onApproveItem ? (
                          <div className="cht-flex cht-items-center cht-gap-1">
                            <button
                              type="button"
                              onClick={() => handleAction("lesson", lesson.id!, "confirm_delete")}
                              className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white cht-rounded-md"
                            >
                              Confirm Delete
                            </button>
                            <button
                              type="button"
                              onClick={() => handleAction("lesson", lesson.id!, "restore")}
                              className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-slate-100 hover:cht-bg-slate-200 cht-text-slate-700 cht-rounded-md"
                            >
                              Restore
                            </button>
                          </div>
                        ) : isSuperAdmin && lesson.is_approved === false && onApproveItem ? (
                          <button
                            type="button"
                            onClick={() => handleAction("lesson", lesson.id!, "approve")}
                            className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-white cht-rounded-md"
                          >
                            Approve
                          </button>
                        ) : null}

                        {isSuperAdmin && lessons.length > 1 && onMoveLesson && module.id && (
                          <div className="cht-flex cht-items-center cht-gap-0.5 cht-bg-slate-50 cht-p-0.5 cht-rounded-lg cht-border cht-border-slate-200">
                            <button
                              type="button"
                              onClick={() => onMoveLesson(module.id!, idx, idx - 1)}
                              disabled={idx === 0}
                              className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                idx === 0
                                  ? "cht-text-slate-300 cht-cursor-not-allowed"
                                  : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                              }`}
                              title="Move Topic Up"
                            >
                              <ArrowUp size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => onMoveLesson(module.id!, idx, idx + 1)}
                              disabled={idx === lessons.length - 1}
                              className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                idx === lessons.length - 1
                                  ? "cht-text-slate-300 cht-cursor-not-allowed"
                                  : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                              }`}
                              title="Move Topic Down"
                            >
                              <ArrowDown size={12} />
                            </button>
                          </div>
                        )}

                        <button
                          type="button"
                          disabled={Boolean(lesson.pending_deletion)}
                          onClick={() => handleStartEditLesson(lesson)}
                          className={`cht-px-2.5 cht-py-1 cht-text-[11px] cht-font-bold cht-rounded-lg cht-transition-colors ${
                            lesson.pending_deletion 
                              ? "cht-text-slate-300 cht-cursor-not-allowed" 
                              : "cht-text-slate-600 hover:cht-text-[#3e57da] hover:cht-bg-indigo-50"
                          }`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          disabled={Boolean(lesson.pending_deletion)}
                          onClick={() => onDeleteLesson(lesson.id!)}
                          className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                            lesson.pending_deletion 
                              ? "cht-text-slate-300 cht-cursor-not-allowed" 
                              : "cht-text-slate-300 hover:cht-text-rose-600 hover:cht-bg-rose-50"
                          }`}
                          title={lesson.pending_deletion ? "Deletion request pending SuperAdmin approval" : "Delete Topic"}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}

                {lessons.length === 0 && !showAddLesson && (
                  <p className="cht-text-xs cht-text-slate-400 cht-italic cht-py-1">
                    No topics added yet. Click "+ Add Topic" to include video or reading lessons.
                  </p>
                )}
              </div>
            </div>

            {/* Section B: Exercises (Assignments/Tasks) */}
            <div className="cht-space-y-3 cht-pt-3 cht-border-t cht-border-gray-100">
              <div className="cht-flex cht-items-center cht-justify-between">
                <div className="cht-flex cht-items-center cht-gap-2">
                  <CheckCircle2 size={16} className="cht-text-emerald-600" />
                  <h5 className="cht-text-xs cht-font-black cht-text-slate-800 cht-uppercase cht-tracking-wider">
                    Tasks & Exercises ({exercises.length})
                  </h5>
                </div>
                {!showAddExercise && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingExerciseId(null);
                      setExerciseForm({
                        title: "",
                        exercise_task: "",
                        exercise_deadline: "",
                        instructor_feedback: "",
                        document_upload: "",
                        requires_autograder: false,
                        autograder_template_repo: "",
                        autograder_module_name: `module_0${moduleIndex + 1}/exercise_0${exercises.length + 1}.py`,
                      });
                      setShowAddExercise(true);
                    }}
                    className="cht-text-xs cht-font-bold cht-text-emerald-600 hover:cht-underline cht-flex cht-items-center cht-gap-1"
                  >
                    <Plus size={13} /> Add Task
                  </button>
                )}
              </div>

              {/* Exercise Form */}
              {showAddExercise && (
                <div className="cht-p-5 cht-rounded-2xl cht-bg-emerald-50/40 cht-border cht-border-emerald-500/20 cht-space-y-4">
                  <div className="cht-flex cht-items-center cht-justify-between cht-border-b cht-border-emerald-500/10 cht-pb-2">
                    <span className="cht-text-xs cht-font-bold cht-text-emerald-700">
                      {editingExerciseId ? "Edit Task" : "New Exercise / Deliverable"}
                    </span>
                    <button 
                      type="button"
                      onClick={() => setShowAddExercise(false)}
                      className="cht-text-slate-400 hover:cht-text-slate-600"
                    >
                      <X size={15} />
                    </button>
                  </div>

                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-4">
                    <div className="sm:cht-col-span-2">
                      <Input
                        label="Task Title"
                        placeholder="e.g. Build an Interactive Dashboard Widget"
                        value={exerciseForm.title}
                        onChange={(e) => setExerciseForm({ ...exerciseForm, title: e.target.value })}
                        leftIcon={<CheckCircle2 size={16} />}
                        required
                      />
                    </div>
                    <div>
                      <Input
                        label="Due Date / Deadline"
                        type="date"
                        value={exerciseForm.exercise_deadline}
                        onChange={(e) => setExerciseForm({ ...exerciseForm, exercise_deadline: e.target.value })}
                        leftIcon={<Calendar size={16} />}
                      />
                    </div>
                  </div>

                  {/* SuperAdmin Gemini Pro Exercise & Pytest Suite Generator */}
                  {isSuperAdmin && (
                    <div className="cht-p-4 cht-rounded-xl cht-bg-white cht-border cht-border-emerald-200/80 cht-shadow-sm cht-space-y-3">
                      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-2">
                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <Sparkles size={15} className="cht-text-emerald-600" />
                          <span className="cht-text-xs cht-font-black cht-text-slate-800">
                            Gemini Pro Exercise & Pytest Generator
                          </span>
                          <span className="cht-text-[10px] cht-font-bold cht-bg-emerald-50 cht-text-emerald-700 cht-px-2 cht-py-0.5 cht-rounded-full">
                            {geminiModel}
                          </span>
                          {Boolean(exerciseForm.exercise_task?.trim()) && (
                            <span className="cht-text-[10px] cht-font-bold cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200/80 cht-px-2 cht-py-0.5 cht-rounded-full">
                              Existing Task Detected
                            </span>
                          )}
                        </div>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <div className="cht-inline-flex cht-rounded-lg cht-bg-slate-100 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setExerciseContentAction("append")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                exerciseContentAction === "append"
                                  ? "cht-bg-white cht-text-emerald-700 cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Keep your existing exercise task and append new requirements/bonus tasks + generate matching pytest suite"
                            >
                              ➕ Add to Existing
                            </button>
                            <button
                              type="button"
                              onClick={() => setExerciseContentAction("refine")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                exerciseContentAction === "refine"
                                  ? "cht-bg-white cht-text-emerald-700 cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Polish and expand your existing exercise draft + generate matching pytest suite"
                            >
                              ✨ Refine Draft
                            </button>
                            <button
                              type="button"
                              onClick={() => setExerciseContentAction("replace")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                exerciseContentAction === "replace"
                                  ? "cht-bg-white cht-text-emerald-700 cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                              title="Generate a brand-new exercise specification and replace the task editor"
                            >
                              🔄 Replace
                            </button>
                          </div>

                          <div className="cht-inline-flex cht-rounded-lg cht-bg-slate-100 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setExercisePromptMode("augment")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                exercisePromptMode === "augment"
                                  ? "cht-bg-white cht-text-emerald-700 cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                            >
                              Augment Template
                            </button>
                            <button
                              type="button"
                              onClick={() => setExercisePromptMode("override")}
                              className={`cht-px-2 cht-py-1 cht-rounded-md cht-transition-colors ${
                                exercisePromptMode === "override"
                                  ? "cht-bg-white cht-text-emerald-700 cht-shadow-sm"
                                  : "cht-text-slate-500 hover:cht-text-slate-800"
                              }`}
                            >
                              Full Custom Prompt
                            </button>
                          </div>

                          {exerciseUndoTask !== null && (
                            <button
                              type="button"
                              onClick={() => {
                                setExerciseForm((prev) => ({ ...prev, exercise_task: exerciseUndoTask }));
                                setExerciseUndoTask(null);
                              }}
                              className="cht-px-2.5 cht-py-1.5 cht-rounded-xl cht-bg-amber-50 hover:cht-bg-amber-100 cht-border cht-border-amber-200 cht-text-[11px] cht-font-bold cht-text-amber-800 cht-transition-colors"
                              title="Restore exercise task to its state before the last Gemini change"
                            >
                              ↩ Undo AI
                            </button>
                          )}

                          <Button
                            size="sm"
                            variant="primary"
                            className="!cht-py-1.5 !cht-px-3 cht-rounded-xl cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-xs"
                            leftIcon={<Sparkles size={13} />}
                            onClick={handleGenerateExerciseWithGemini}
                            isLoading={isGeneratingExerciseAI}
                          >
                            {!exerciseForm.exercise_task?.trim()
                              ? "Generate Task & Pytest"
                              : exerciseContentAction === "append"
                              ? "Add to Task & Pytest"
                              : exerciseContentAction === "refine"
                              ? "Refine Task & Pytest"
                              : "Replace Task & Pytest"}
                          </Button>
                        </div>
                      </div>

                      <textarea
                        rows={3}
                        placeholder={
                          exerciseForm.exercise_task?.trim() && exerciseContentAction === "append"
                            ? "Tell Gemini Pro what to ADD to your existing exercise (e.g., 'Add a bonus challenge on rate-limiting, sample input/output examples, and generate pytest unit tests for all requirements')..."
                            : exerciseForm.exercise_task?.trim() && exerciseContentAction === "refine"
                            ? "Tell Gemini Pro how to REFINE & EXPAND your existing exercise draft and generate its pytest grading suite..."
                            : exercisePromptMode === "override"
                            ? "Write your full custom exercise & pytest prompt (e.g., 'Design a BankAccount class in module_01/account.py with deposit, withdraw, and transfer methods, plus 6 pytest unit tests')..."
                            : "Specify custom requirements, function signatures, or edge cases (e.g., 'Create calculate_tax(income: float) -> float with progressive brackets and raise ValueError on negative income')..."
                        }
                        value={exerciseAIPrompt}
                        onChange={(e) => setExerciseAIPrompt(e.target.value)}
                        className="cht-w-full cht-px-3 cht-py-2 cht-text-xs cht-rounded-xl cht-border cht-border-slate-200 focus:cht-outline-none focus:cht-border-emerald-600 cht-resize-y"
                      />

                      <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-flex-wrap">
                        <div className="cht-flex cht-items-center cht-gap-1.5 cht-flex-wrap">
                          <span className="cht-text-[10px] cht-font-bold cht-text-slate-400">
                            Quick Constraints:
                          </span>
                          {[
                            "Add bonus edge-case requirements",
                            "Add sample input/output walkthrough",
                            "Generate strict pytest suite for existing task",
                            "Use Python type hints & ValueError checks",
                          ].map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => appendExercisePromptChip(chip)}
                              className="cht-text-[10px] cht-font-semibold cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-slate-100 hover:cht-bg-emerald-50 cht-text-slate-600 hover:cht-text-emerald-700 cht-transition-colors"
                            >
                              + {chip}
                            </button>
                          ))}
                        </div>
                        {exerciseAIPrompt && (
                          <button
                            type="button"
                            onClick={() => setExerciseAIPrompt("")}
                            className="cht-text-[10px] cht-font-semibold cht-text-slate-400 hover:cht-text-rose-600"
                          >
                            Clear Prompt
                          </button>
                        )}
                      </div>

                      {exerciseAIError && (
                        <p className="cht-text-[11px] cht-font-semibold cht-text-rose-600">
                          {exerciseAIError}
                        </p>
                      )}
                      {generatedExerciseFiles && (
                        <div className="cht-pt-2 cht-border-t cht-border-slate-100 cht-space-y-2">
                          <div className="cht-flex items-center justify-between">
                            <span className="cht-text-[11px] cht-font-bold cht-text-emerald-700">
                              Generated Template Repo Starter & Pytest Files
                            </span>
                            <button
                              type="button"
                              onClick={() => setGeneratedExerciseFiles(null)}
                              className="cht-text-[10px] cht-text-slate-400 hover:cht-text-slate-600"
                            >
                              Hide
                            </button>
                          </div>
                          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-2.5">
                            <div className="cht-p-2.5 cht-rounded-lg cht-bg-slate-900 cht-text-slate-100 cht-space-y-1.5">
                              <div className="cht-flex cht-items-center cht-justify-between">
                                <span className="cht-text-[10px] cht-font-mono cht-text-emerald-400">
                                  {exerciseForm.autograder_module_name || "starter.py"}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(generatedExerciseFiles.starter_code);
                                    setCopiedCodeBlock("starter");
                                    setTimeout(() => setCopiedCodeBlock(null), 2000);
                                  }}
                                  className="cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded cht-bg-slate-800 hover:cht-bg-slate-700 cht-text-white"
                                >
                                  {copiedCodeBlock === "starter" ? "Copied!" : "Copy Starter"}
                                </button>
                              </div>
                              <pre className="cht-text-[10px] cht-font-mono cht-max-h-32 cht-overflow-y-auto cht-whitespace-pre-wrap cht-text-slate-300">
                                {generatedExerciseFiles.starter_code}
                              </pre>
                            </div>
                            <div className="cht-p-2.5 cht-rounded-lg cht-bg-slate-900 cht-text-slate-100 cht-space-y-1.5">
                              <div className="cht-flex cht-items-center cht-justify-between">
                                <span className="cht-text-[10px] cht-font-mono cht-text-indigo-400">
                                  tests/test_exercise.py
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard.writeText(generatedExerciseFiles.pytest_code);
                                    setCopiedCodeBlock("pytest");
                                    setTimeout(() => setCopiedCodeBlock(null), 2000);
                                  }}
                                  className="cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded cht-bg-slate-800 hover:cht-bg-slate-700 cht-text-white"
                                >
                                  {copiedCodeBlock === "pytest" ? "Copied!" : "Copy Pytest"}
                                </button>
                              </div>
                              <pre className="cht-text-[10px] cht-font-mono cht-max-h-32 cht-overflow-y-auto cht-whitespace-pre-wrap cht-text-slate-300">
                                {generatedExerciseFiles.pytest_code}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <RichTextEditor
                      key={editingExerciseId ? `ex-task-${editingExerciseId}` : "new-ex-task"}
                      label="Task Instructions & Criteria"
                      placeholder="Outline the deliverable requirements, submission expectations, and evaluation rubric..."
                      value={exerciseForm.exercise_task}
                      onChange={(data) => setExerciseForm((prev) => ({ ...prev, exercise_task: data }))}
                      minHeight="160px"
                      isLoading={isGeneratingExerciseAI}
                      loadingText={isGeneratingExerciseAI ? "Gemini Pro is drafting criteria..." : undefined}
                      required
                    />
                  </div>

                  <div>
                    <RichTextEditor
                      key={editingExerciseId ? `ex-feedback-${editingExerciseId}` : "new-ex-feedback"}
                      label="Instructor Solution Notes / Guidance (Optional)"
                      placeholder="Key tips or solution hints provided to the student upon review..."
                      value={exerciseForm.instructor_feedback}
                      onChange={(data) => setExerciseForm((prev) => ({ ...prev, instructor_feedback: data }))}
                      minHeight="130px"
                      isLoading={isGeneratingExerciseAI}
                      loadingText={isGeneratingExerciseAI ? "Gemini Pro is drafting solution guidance..." : undefined}
                    />
                  </div>

                  {/* GitHub CI Autograder Configuration (Per-Exercise File in Course Repo) */}
                  <div className="cht-p-4 cht-rounded-2xl cht-bg-white cht-border cht-border-emerald-500/20 cht-space-y-3">
                    <label className="cht-flex cht-items-start cht-gap-3 cht-cursor-pointer">
                      <input
                        type="checkbox"
                        checked={exerciseForm.requires_autograder}
                        onChange={(e) =>
                          setExerciseForm({
                            ...exerciseForm,
                            requires_autograder: e.target.checked,
                            autograder_module_name:
                              e.target.checked && !exerciseForm.autograder_module_name
                                ? `module_0${moduleIndex + 1}/exercise_0${exercises.length + 1}.py`
                                : exerciseForm.autograder_module_name,
                          })
                        }
                        className="cht-mt-0.5 cht-w-4 cht-h-4 cht-rounded cht-text-emerald-600 focus:cht-ring-emerald-500"
                      />
                      <div>
                        <div className="cht-flex items-center cht-gap-2">
                          <GitBranch size={14} className="cht-text-emerald-600" />
                          <span className="cht-text-xs cht-font-bold cht-text-slate-800">
                            Grade this Exercise via Course GitHub Repository
                          </span>
                        </div>
                        <p className="cht-text-[11px] cht-text-slate-500 cht-mt-0.5 leading-relaxed">
                          Evaluates a specific file inside the student&apos;s provisioned course repository and grades this exercise individually.
                        </p>
                      </div>
                    </label>

                    {exerciseForm.requires_autograder && (
                      <div className="cht-pt-3 cht-border-t cht-border-slate-100 cht-space-y-3">
                        <div className="cht-p-2.5 cht-rounded-xl cht-bg-slate-50 cht-border cht-border-slate-200/80 cht-flex cht-items-center cht-justify-between cht-gap-2">
                          <span className="cht-text-[11px] cht-font-semibold cht-text-slate-600">
                            Course Template Repo:
                          </span>
                          <span className="cht-text-[11px] cht-font-mono cht-font-bold cht-text-[#3e57da] cht-bg-indigo-50 cht-px-2 cht-py-0.5 cht-rounded-md">
                            {courseTemplateRepo || exerciseForm.autograder_template_repo || "Set in Course Settings"}
                          </span>
                        </div>

                        <Input
                          label="Expected File Name / Path in Course Repo"
                          placeholder={`e.g. variables.py or module_0${moduleIndex + 1}/variables.py`}
                          value={exerciseForm.autograder_module_name}
                          onChange={(e) =>
                            setExerciseForm({
                              ...exerciseForm,
                              autograder_module_name: e.target.value.trim(),
                            })
                          }
                          leftIcon={<FileText size={16} />}
                          required
                        />
                        <p className="cht-text-[10px] cht-text-slate-400">
                          Specify the file name or relative path inside the course repository that the student must edit for this exercise (e.g., <code>variables.py</code> or <code>module_01/variables.py</code>). The autograder will grade this exercise based on the test score for this file.
                        </p>

                        {!courseTemplateRepo && (
                          <div className="cht-pt-1">
                            <Input
                              label="Course Template Repo Fallback (if not set on Course)"
                              placeholder="e.g. python-codehub-assignments-bundle"
                              value={exerciseForm.autograder_template_repo}
                              onChange={(e) =>
                                setExerciseForm({
                                  ...exerciseForm,
                                  autograder_template_repo: e.target.value.trim(),
                                })
                              }
                              leftIcon={<GitBranch size={16} />}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="cht-flex cht-justify-end cht-gap-2 cht-pt-1">
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="cht-rounded-xl" 
                      onClick={() => setShowAddExercise(false)}
                      disabled={isSavingExercise}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      variant="primary" 
                      className="cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-rounded-xl" 
                      onClick={handleSaveExercise}
                      isLoading={isSavingExercise}
                      disabled={isSavingExercise}
                    >
                      {isSavingExercise
                        ? editingExerciseId
                          ? "Saving Task..."
                          : "Adding Task..."
                        : editingExerciseId
                        ? "Save Task"
                        : "Add Task"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Exercises List */}
              <div className="cht-space-y-2">
                {exercises.map((exercise, idx) => (
                  <div 
                    key={exercise.id || idx}
                    className="cht-flex cht-items-center cht-justify-between cht-p-3.5 cht-bg-white cht-rounded-xl cht-border cht-border-gray-100 hover:cht-border-emerald-200 cht-transition-all"
                  >
                    <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                      <div className="cht-w-7 cht-h-7 cht-rounded-lg cht-bg-emerald-50 cht-text-emerald-600 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                        <CheckCircle2 size={14} />
                      </div>
                      <div className="cht-min-w-0">
                        <p className="cht-text-xs cht-font-bold cht-text-slate-800 cht-truncate">
                          {exercise.title}
                        </p>
                        {exercise.exercise_deadline && (
                          <div className="cht-flex cht-items-center cht-gap-1.5 cht-mt-0.5 cht-text-[10px] cht-text-slate-400">
                            <Calendar size={11} className="cht-text-slate-400" />
                            <span>Due: {exercise.exercise_deadline}</span>
                          </div>
                        )}
                        {exercise.requires_autograder && (
                          <div className="cht-flex cht-items-center cht-flex-wrap cht-gap-1.5 cht-mt-1">
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-mono cht-font-bold cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200/60">
                              <FileText size={10} />
                              Target File: {exercise.autograder_module_name || "Not specified"}
                            </span>
                            {(courseTemplateRepo || exercise.autograder_template_repo) && (
                              <span className="cht-inline-flex cht-items-center cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-mono cht-font-semibold cht-bg-slate-100 cht-text-slate-600">
                                {courseTemplateRepo || exercise.autograder_template_repo}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="cht-flex cht-items-center cht-gap-2">
                      {exercise.pending_deletion ? (
                        <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-rose-100 cht-text-rose-700 cht-border cht-border-rose-200">
                          Pending Deletion
                        </span>
                      ) : exercise.is_approved === false ? (
                        <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-amber-100 cht-text-amber-800 cht-border cht-border-amber-200">
                          Pending Approval
                        </span>
                      ) : null}

                      {isSuperAdmin && exercise.pending_deletion && onApproveItem ? (
                        <div className="cht-flex cht-items-center cht-gap-1">
                          <button
                            type="button"
                            onClick={() => handleAction("exercise", exercise.id!, "confirm_delete")}
                            className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white cht-rounded-md"
                          >
                            Confirm Delete
                          </button>
                          <button
                            type="button"
                            onClick={() => handleAction("exercise", exercise.id!, "restore")}
                            className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-slate-100 hover:cht-bg-slate-200 cht-text-slate-700 cht-rounded-md"
                          >
                            Restore
                          </button>
                        </div>
                      ) : isSuperAdmin && exercise.is_approved === false && onApproveItem ? (
                        <button
                          type="button"
                          onClick={() => handleAction("exercise", exercise.id!, "approve")}
                          className="cht-px-2 cht-py-1 cht-text-[10px] cht-font-bold cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-white cht-rounded-md"
                        >
                          Approve
                        </button>
                      ) : null}

                      <button
                        type="button"
                        disabled={Boolean(exercise.pending_deletion)}
                        onClick={() => handleStartEditExercise(exercise)}
                        className={`cht-px-2.5 cht-py-1 cht-text-[11px] cht-font-bold cht-rounded-lg cht-transition-colors ${
                          exercise.pending_deletion 
                            ? "cht-text-slate-300 cht-cursor-not-allowed" 
                            : "cht-text-slate-600 hover:cht-text-emerald-600 hover:cht-bg-emerald-50"
                        }`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={Boolean(exercise.pending_deletion)}
                        onClick={() => onDeleteExercise(exercise.id!)}
                        className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                          exercise.pending_deletion 
                            ? "cht-text-slate-300 cht-cursor-not-allowed" 
                            : "cht-text-slate-300 hover:cht-text-rose-600 hover:cht-bg-rose-50"
                        }`}
                        title={exercise.pending_deletion ? "Deletion request pending SuperAdmin approval" : "Delete Task"}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}

                {exercises.length === 0 && !showAddExercise && (
                  <p className="cht-text-xs cht-text-slate-400 cht-italic cht-py-1">
                    No tasks or exercises added yet. Click "+ Add Task" to set assignments.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </GlassCard>
  );
};

// --- Main Instructor Course Editor ---
const InstructorCourseEditor: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();

  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Role detection: SuperAdmin Console vs Instructor Dashboard
  const { currentUser, isLoading: isAuthLoading } = useAuthicatedUser();
  const isUserSuperAdmin = Boolean(
    (currentUser as any)?.is_superuser ||
    (currentUser as any)?.role === "SUPERADMIN" ||
    (currentUser as any)?.role === "SuperAdmin" ||
    (Array.isArray((currentUser as any)?.roles) && (currentUser as any).roles.map((r: string) => String(r).toUpperCase()).includes("SUPERADMIN"))
  );
  const isSuperAdmin = location.pathname.startsWith("/superadmin") || isUserSuperAdmin;
  const isSuperAdminRef = useRef(isSuperAdmin);
  isSuperAdminRef.current = isSuperAdmin;

  const initialType = searchParams.get("type") === "track" || location.pathname.includes("/tracks/") ? "track" : "course";

  // Enforce that only SuperAdmin can initiate creation of new courses or tracks
  useEffect(() => {
    if (isAuthLoading) return;
    if (!isSuperAdmin && (!id || id === "create")) {
      addToastRef.current("Course and Track creation is restricted to SuperAdmin. Instructors can only manage curriculum for courses assigned to them.", "warning");
      navigateRef.current("/instructor/dashboard/my-courses", { replace: true });
    }
  }, [isAuthLoading, isSuperAdmin, id]);

  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(() => Boolean(id && id !== "create"));
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Available Standalone Courses (used when building Career Tracks)
  const [availableCourses, setAvailableCourses] = useState<CourseData[]>([]);
  // Available Instructors for Governance Assignment
  const [instructors, setInstructors] = useState<InstructorData[]>([]);
  // Available Presence Types (Online, Onsite)
  const [presences, setPresences] = useState<PresenceData[]>([]);

  // Courses inside Career Track (differentiated phases)
  const [coursesInTrack, setCoursesInTrack] = useState<CourseData[]>([]);
  // Selected course filter within a Career Track ("all" or courseId)
  const [selectedCourseId, setSelectedCourseId] = useState<string | number | "all">("all");

  // Comprehensive Curriculum Form Data (Exposing all Course & Track model attributes)
  const [formData, setFormData] = useState<{
    id?: string | number;
    type: "course" | "track";
    title: string;
    slug?: string;
    description: string;
    requirement: string;
    outline: string;
    outline_snippet: string;
    why_choose: string;
    level: "Beginner" | "Intermidiate" | "Advance" | string;
    duration: string;
    online_price: string | number;
    onsite_price: string | number;
    image_url: string;
    course_ids: (string | number)[];
    modules: ModuleData[];
    // Full Model Governance Fields
    instructor: string | number | "";
    presence_ids: number[];
    payment_type: "Paid" | "Free";
    rating: string | number;
    review_count: string;
    enrolled_count: string;
    lessons_count: number;
    has_certificate: boolean;
    has_lifetime_access: boolean;
    has_mentor_support: boolean;
    has_digital_assets: boolean;
    autograder_template_repo: string;
    approval_status?: string;
    is_live: boolean;
  }>({
    id: id && id !== "create" ? id : undefined,
    type: initialType,
    title: "",
    slug: "",
    description: "",
    requirement: "",
    outline: "",
    outline_snippet: "",
    why_choose: "",
    level: "Beginner",
    duration: "8 Weeks",
    online_price: "45000",
    onsite_price: "65000",
    image_url: "",
    course_ids: [],
    modules: [],
    instructor: "",
    presence_ids: [1, 2],
    payment_type: "Paid",
    rating: "0.0",
    review_count: "0",
    enrolled_count: "0",
    lessons_count: 0,
    has_certificate: true,
    has_lifetime_access: true,
    has_mentor_support: false,
    has_digital_assets: true,
    autograder_template_repo: "",
    approval_status: "APPROVED",
    is_live: false,
  });

  // Return route helper
  const returnPath = isSuperAdmin
    ? (formData.type === "track" ? "/superadmin/dashboard/tracks" : "/superadmin/dashboard/courses")
    : "/instructor/dashboard/my-courses";

  // Selected Lead Instructor Object
  const selectedInstructor = useMemo(() => {
    if (!formData.instructor) return null;
    return instructors.find((i) => String(i.id) === String(formData.instructor)) || null;
  }, [formData.instructor, instructors]);

  // Active course in track (when viewing a specific course phase)
  const activeCourse = useMemo(() => {
    if (formData.type !== "track" || selectedCourseId === "all") return null;
    return coursesInTrack.find((c) => String(c.id) === String(selectedCourseId)) || null;
  }, [formData.type, selectedCourseId, coursesInTrack]);

  const activeCoursePhaseNumber = useMemo(() => {
    if (!activeCourse) return 1;
    const idx = coursesInTrack.findIndex((c) => String(c.id) === String(activeCourse.id));
    return idx >= 0 ? idx + 1 : 1;
  }, [activeCourse, coursesInTrack]);

  // Live DB & Curriculum Metrics (Topics + Practical Tasks)
  const { liveTopicsCount, liveTasksCount, liveLessonsAndTasksCount } = useMemo(() => {
    const activeModules =
      formData.type === "track"
        ? coursesInTrack.flatMap((c) => c.modules || [])
        : formData.modules || [];
    const topics = activeModules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
    const tasks = activeModules.reduce(
      (acc, m) => acc + ((m.exercise_module || (m as any).exercises)?.length || 0),
      0
    );
    const totalFromModules = topics + tasks;
    const total = totalFromModules > 0 ? totalFromModules : Number(formData.lessons_count) || 0;
    return {
      liveTopicsCount: topics,
      liveTasksCount: tasks,
      liveLessonsAndTasksCount: total,
    };
  }, [formData.type, formData.modules, formData.lessons_count, coursesInTrack]);

  // Load existing course or track data alongside global instructors and presences
  useEffect(() => {
    async function loadData() {
      const isEditMode = Boolean(id && id !== "create");
      if (isEditMode) {
        setLoading(true);
      }

      // Fetch metadata lists and detail concurrently
      const metadataPromise = Promise.all([
        fetchCoursesList().catch(() => []),
        fetchInstructorsList().catch(() => []),
        fetchCoursePresences().catch(() => []),
      ]);

      const detailPromise = isEditMode
        ? (async () => {
            let isTrack = initialType === "track";
            let track: TrackData | null = null;
            let course: CourseData | null = null;

            if (isTrack) {
              try {
                track = await fetchTrackDetail(id!);
              } catch (err) {
                // Fallback: in case ID actually belongs to a standalone course
                try {
                  course = await fetchCourseDetail(id!);
                  isTrack = false;
                } catch {
                  throw err;
                }
              }
            } else {
              try {
                course = await fetchCourseDetail(id!);
              } catch (err) {
                // Fallback: in case ID actually belongs to a career track
                try {
                  track = await fetchTrackDetail(id!);
                  isTrack = true;
                } catch {
                  throw err;
                }
              }
            }
            return { isTrack, track, course };
          })()
        : Promise.resolve(null);

      try {
        const [metadata, detailResult] = await Promise.all([metadataPromise, detailPromise]);
        const [courses, instrs, pres] = metadata;
        setAvailableCourses(courses);
        setInstructors(instrs);
        setPresences(pres);

        if (!isEditMode) {
          if (pres.length > 0) {
            setFormData((prev) => ({
              ...prev,
              presence_ids: pres.map((p) => p.id),
            }));
          }
          return;
        }

        const { isTrack, track, course } = detailResult!;

        const normalizeLevel = (lvl?: string) => {
          if (!lvl) return "Beginner";
          const lower = lvl.toLowerCase();
          if (lower.startsWith("inter")) return "Intermidiate";
          if (lower.startsWith("adv")) return "Advance";
          return "Beginner";
        };

        if (isTrack && track) {
          const trackCourses: CourseData[] = (track.course_in_track || []).map((c: any) => ({
            ...c,
            modules: (c.modules || []).map((m: any) => ({
              ...m,
              course: c.id,
              lessons: m.lessons || [],
              exercise_module: m.exercise_module || m.exercises || [],
            })),
          }));
          const allModules = trackCourses.flatMap((c) => c.modules || []);

          const trackPresences = Array.isArray(track.presence)
            ? track.presence.map((p: any) => (typeof p === "object" ? p.id : Number(p)))
            : (Array.isArray((track as any).presence_ids) ? (track as any).presence_ids : []);
          const trackInstructorId = track.instructor
            ? (typeof track.instructor === "object" ? track.instructor.id : track.instructor)
            : "";

          setCoursesInTrack(trackCourses);
          setFormData({
            id: track.id,
            type: "track",
            title: track.title || "",
            slug: track.slug || "",
            description: track.description || "",
            requirement: track.requirement || "",
            outline: track.outline || "",
            outline_snippet: track.outline_snippet || "",
            why_choose: track.why_choose_track || (track as any).why_choose || "",
            level: normalizeLevel(track.level),
            duration: track.duration || "12 Weeks",
            online_price: track.online_price !== undefined ? track.online_price : "0",
            onsite_price: track.onsite_price !== undefined ? track.onsite_price : "0",
            image_url: track.track_img_url || (track as any).course_img_url || (track as any).image_url || "",
            course_ids: (track.course_order && track.course_order.length > 0)
              ? track.course_order
              : (trackCourses.length > 0 ? trackCourses.map((c) => c.id!) : (track.course_ids || [])),
            modules: allModules,
            instructor: trackInstructorId,
            presence_ids: trackPresences.length > 0 ? trackPresences : (pres.length > 0 ? pres.map((p) => p.id) : [1, 2]),
            payment_type: track.payment_type ? (String(track.payment_type).toLowerCase() === "free" ? "Free" : "Paid") : "Paid",
            rating: track.rating !== undefined && track.rating !== null ? String(track.rating) : "0.0",
            review_count: track.review_count !== undefined && track.review_count !== null ? String(track.review_count) : "0",
            enrolled_count: track.enrolled_count !== undefined && track.enrolled_count !== null ? String(track.enrolled_count) : "0",
            lessons_count: track.lessons_count ?? 0,
            has_certificate: track.has_certificate ?? true,
            has_lifetime_access: track.has_lifetime_access ?? true,
            has_mentor_support: track.has_mentor_support ?? false,
            has_digital_assets: track.has_digital_assets ?? true,
            autograder_template_repo: "",
            approval_status: track.approval_status || "APPROVED",
            is_live: track.is_live ?? false,
          });
        } else if (course) {
          const normalizedModules = (course.modules || []).map((m: any) => ({
            ...m,
            course: course.id,
            lessons: m.lessons || [],
            exercise_module: m.exercise_module || m.exercises || [],
          }));

          const coursePresences = Array.isArray(course.presence)
            ? course.presence.map((p: any) => (typeof p === "object" ? p.id : Number(p)))
            : (Array.isArray((course as any).presence_ids) ? (course as any).presence_ids : []);
          const courseInstructorId = course.instructor
            ? (typeof course.instructor === "object" ? course.instructor.id : course.instructor)
            : "";

          // Fallback to first exercise's autograder_template_repo if course doesn't have one yet
          let fallbackRepo = course.autograder_template_repo || "";
          if (!fallbackRepo) {
            for (const m of normalizedModules) {
              for (const ex of m.exercise_module || []) {
                if (ex.autograder_template_repo) {
                  fallbackRepo = ex.autograder_template_repo;
                  break;
                }
              }
              if (fallbackRepo) break;
            }
          }

          setCoursesInTrack([]);
          setFormData({
            id: course.id,
            type: "course",
            title: course.title || "",
            slug: course.slug || "",
            description: course.description || "",
            requirement: course.requirement || "",
            outline: course.outline || "",
            outline_snippet: course.outline_snippet || "",
            why_choose: course.why_choose_course || (course as any).why_choose || "",
            level: normalizeLevel(course.level),
            duration: course.duration || "8 Weeks",
            online_price: course.online_price !== undefined ? course.online_price : "0",
            onsite_price: course.onsite_price !== undefined ? course.onsite_price : "0",
            image_url: course.course_img_url || (course as any).track_img_url || (course as any).image_url || "",
            course_ids: [],
            modules: normalizedModules,
            instructor: courseInstructorId,
            presence_ids: coursePresences.length > 0 ? coursePresences : (pres.length > 0 ? pres.map((p) => p.id) : [1, 2]),
            payment_type: course.payment_type ? (String(course.payment_type).toLowerCase() === "free" ? "Free" : "Paid") : "Paid",
            rating: course.rating !== undefined && course.rating !== null ? String(course.rating) : "0.0",
            review_count: course.review_count !== undefined && course.review_count !== null ? String(course.review_count) : "0",
            enrolled_count: course.enrolled_count !== undefined && course.enrolled_count !== null ? String(course.enrolled_count) : "0",
            lessons_count: course.lessons_count ?? 0,
            has_certificate: course.has_certificate ?? true,
            has_lifetime_access: course.has_lifetime_access ?? true,
            has_mentor_support: course.has_mentor_support ?? false,
            has_digital_assets: course.has_digital_assets ?? true,
            autograder_template_repo: fallbackRepo,
            approval_status: course.approval_status || "APPROVED",
            is_live: course.is_live ?? false,
          });
        }
      } catch (err: any) {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        console.error("Failed to load curriculum details:", err);
        if (err?.response?.status === 403) {
          addToastRef.current("Access Denied: You are not assigned as faculty to this course or track.", "error");
          const fallbackPath = isSuperAdminRef.current ? "/superadmin/dashboard/courses" : "/instructor/dashboard/my-courses";
          navigateRef.current(fallbackPath, { replace: true });
        } else {
          addToastRef.current("Failed to load curriculum details.", "error");
        }
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id, initialType]);

  // Media Picker modal for cover image
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // Handle Selection from Media Library
  const handleSelectFromMediaLibrary = (asset: MediaAsset) => {
    const selectedUrl =
      asset.url ||
      asset.thumbnail_url ||
      asset.external_url ||
      (typeof asset.file === "string" ? asset.file : "") ||
      "";
    setFormData((prev) => ({ ...prev, image_url: selectedUrl }));
    addToast(`Cover image set from Media Library: "${asset.title || 'Selected asset'}"`, "success");
  };

  // Handle Cover Image Upload (registers to Media Library)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const mediaFormData = new FormData();
      mediaFormData.append("file", file);
      mediaFormData.append("title", `${formData.title || "Curriculum"} Cover Image`);
      mediaFormData.append("media_type", "image");

      let uploadedUrl = "";
      try {
        const asset = await mediaService.createMediaAsset(mediaFormData);
        uploadedUrl = asset.url || asset.thumbnail_url || "";
      } catch (mediaErr) {
        console.warn("Direct MediaAsset creation failed, falling back to curriculum upload:", mediaErr);
        const res = await uploadCurriculumAsset(file);
        uploadedUrl = res.url;
      }

      if (uploadedUrl) {
        setFormData((prev) => ({ ...prev, image_url: uploadedUrl }));
        addToast("Cover image uploaded and registered to Media Library!", "success");
      }
    } catch (err: any) {
      console.error("Asset upload error:", err);
      addToast("Failed to upload image. Please try again.", "error");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Step 1: Save Identity & Proceed to Step 2
  const handleSaveIdentity = async () => {
    if (!formData.title.trim()) {
      addToast("Please provide a title for the curriculum.", "error");
      return;
    }

    setIsSaving(true);
    const validPresenceIds = presences.length > 0
      ? formData.presence_ids.filter((pid) => presences.some((p) => p.id === pid))
      : formData.presence_ids;
    const finalPresenceIds = validPresenceIds.length > 0
      ? validPresenceIds
      : (presences.length > 0 ? [presences[0].id] : formData.presence_ids);

    try {
      if (formData.type === "track") {
        const payload: Partial<TrackData> = {
          title: formData.title,
          slug: formData.slug?.trim() || undefined,
          description: formData.description,
          requirement: formData.requirement,
          outline: formData.outline,
          outline_snippet: formData.outline_snippet || formData.description.replace(/<[^>]*>?/gm, "").trim().slice(0, 150),
          why_choose_track: formData.why_choose,
          payment_type: formData.payment_type,
          level: formData.level,
          duration: formData.duration,
          online_price: Number(formData.online_price) || 0,
          onsite_price: Number(formData.onsite_price) || 0,
          track_img_url: formData.image_url || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800",
          course_ids: formData.course_ids,
          presence_ids: finalPresenceIds,
          rating: Number(formData.rating) || 0,
          review_count: String(formData.review_count || "0"),
          enrolled_count: String(formData.enrolled_count || "0"),
          lessons_count: liveLessonsAndTasksCount,
          has_certificate: formData.has_certificate,
          has_lifetime_access: formData.has_lifetime_access,
          has_mentor_support: formData.has_mentor_support,
          has_digital_assets: formData.has_digital_assets,
          is_live: formData.is_live,
          instructor: formData.instructor ? Number(formData.instructor) : null,
        };

        if (formData.id) {
          const updated = await updateTrack(formData.id, payload);
          setFormData((prev) => ({ ...prev, slug: updated.slug }));
          try {
            const refreshedTrack = await fetchTrackDetail(formData.id);
            const refreshedCourses: CourseData[] = (refreshedTrack.course_in_track || []).map((c: any) => ({
              ...c,
              modules: (c.modules || []).map((m: any) => ({
                ...m,
                course: c.id,
                lessons: m.lessons || [],
                exercise_module: m.exercise_module || m.exercises || [],
              })),
            }));
            setCoursesInTrack(refreshedCourses);
            setFormData((prev) => ({
              ...prev,
              modules: refreshedCourses.flatMap((c) => c.modules || []),
            }));
          } catch (refErr) {
            console.error("Failed to refresh track courses:", refErr);
          }
          addToast("Career Track updated successfully!", "success");
        } else {
          const created = await createTrack(payload);
          setFormData((prev) => ({ ...prev, id: created.id, slug: created.slug }));
          try {
            const refreshedTrack = await fetchTrackDetail(created.id);
            const refreshedCourses: CourseData[] = (refreshedTrack.course_in_track || []).map((c: any) => ({
              ...c,
              modules: (c.modules || []).map((m: any) => ({
                ...m,
                course: c.id,
                lessons: m.lessons || [],
                exercise_module: m.exercise_module || m.exercises || [],
              })),
            }));
            setCoursesInTrack(refreshedCourses);
            setFormData((prev) => ({
              ...prev,
              modules: refreshedCourses.flatMap((c) => c.modules || []),
            }));
          } catch (refErr) {
            console.error("Failed to refresh track courses:", refErr);
          }
          addToast("Career Track created! Now configure its courses and modules.", "success");
          const dest = isSuperAdmin
            ? `/superadmin/dashboard/tracks/edit/${created.id}`
            : `/instructor/dashboard/my-courses/edit/${created.id}?type=track`;
          navigate(dest, { replace: true });
        }
      } else {
        const payload: Partial<CourseData> = {
          title: formData.title,
          slug: formData.slug?.trim() || undefined,
          description: formData.description,
          requirement: formData.requirement,
          outline: formData.outline,
          outline_snippet: formData.outline_snippet || formData.description.replace(/<[^>]*>?/gm, "").trim().slice(0, 150),
          why_choose_course: formData.why_choose,
          payment_type: formData.payment_type,
          level: formData.level,
          duration: formData.duration,
          online_price: Number(formData.online_price) || 0,
          onsite_price: Number(formData.onsite_price) || 0,
          course_img_url: formData.image_url || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800",
          presence_ids: finalPresenceIds,
          rating: Number(formData.rating) || 0,
          review_count: String(formData.review_count || "0"),
          enrolled_count: String(formData.enrolled_count || "0"),
          lessons_count: liveLessonsAndTasksCount,
          has_certificate: formData.has_certificate,
          has_lifetime_access: formData.has_lifetime_access,
          has_mentor_support: formData.has_mentor_support,
          has_digital_assets: formData.has_digital_assets,
          autograder_template_repo: formData.autograder_template_repo?.trim() || "",
          is_live: formData.is_live,
          instructor: formData.instructor ? Number(formData.instructor) : null,
        };

        if (formData.id) {
          const updated = await updateCourse(formData.id, payload);
          setFormData((prev) => ({
            ...prev,
            slug: updated.slug,
            autograder_template_repo: updated.autograder_template_repo ?? prev.autograder_template_repo,
          }));
          addToast("Course updated successfully!", "success");
        } else {
          const created = await createCourse(payload);
          setFormData((prev) => ({
            ...prev,
            id: created.id,
            slug: created.slug,
            autograder_template_repo: created.autograder_template_repo ?? prev.autograder_template_repo,
          }));
          addToast("Course created! Now build your curriculum modules.", "success");
          const dest = isSuperAdmin
            ? `/superadmin/dashboard/courses/edit/${created.id}`
            : `/instructor/dashboard/my-courses/edit/${created.id}?type=course`;
          navigate(dest, { replace: true });
        }
      }
      setStep(2);
    } catch (err: any) {
      console.error("Save identity error:", err);
      addToast("Failed to save curriculum identity.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCourseTemplateRepo = async (courseId: string | number, repoName: string) => {
    try {
      const cleaned = repoName.trim();
      await updateCourse(courseId, { autograder_template_repo: cleaned });
      if (formData.type === "course" && String(formData.id) === String(courseId)) {
        setFormData((prev) => ({ ...prev, autograder_template_repo: cleaned }));
      } else {
        setCoursesInTrack((prev) =>
          prev.map((c) => (String(c.id) === String(courseId) ? { ...c, autograder_template_repo: cleaned } : c))
        );
      }
      addToast("Course GitHub Autograder Template Repository saved!", "success");
    } catch (err) {
      console.error("Failed to save course template repo:", err);
      addToast("Failed to save Course GitHub Template Repository.", "error");
    }
  };

  // --- Step 2: Curriculum Operations ---
  const handleAddModule = async (targetCourseId?: string | number) => {
    if (!formData.id) {
      addToast("Please save the curriculum identity first.", "error");
      return;
    }

    if (formData.type === "track") {
      const resolvedCourseId = targetCourseId || (selectedCourseId !== "all" ? selectedCourseId : coursesInTrack[0]?.id);
      if (!resolvedCourseId) {
        addToast("Please link at least one course to this track first.", "error");
        return;
      }

      const targetCourse = coursesInTrack.find((c) => String(c.id) === String(resolvedCourseId));
      const courseModules = targetCourse?.modules || [];
      const newTitle = `Module ${courseModules.length + 1}: Getting Started`;

      try {
        const createdMod = await createModule({
          course: resolvedCourseId,
          title: newTitle,
        });

        const newModuleFormatted: ModuleData = {
          ...createdMod,
          course: resolvedCourseId,
          lessons: [],
          exercise_module: [],
        };

        setCoursesInTrack((prev) =>
          prev.map((c) =>
            String(c.id) === String(resolvedCourseId)
              ? { ...c, modules: [...(c.modules || []), newModuleFormatted] }
              : c
          )
        );

        setFormData((prev) => ({
          ...prev,
          modules: [...prev.modules, newModuleFormatted],
        }));

        addToast("Module added to course phase!", "success");
      } catch (err: any) {
        console.error("Failed to add module to track course:", err);
        addToast("Failed to add module.", "error");
      }
    } else {
      const newTitle = `Module ${formData.modules.length + 1}: Getting Started`;
      try {
        const createdMod = await createModule({
          course: formData.id,
          title: newTitle,
        });
        const newModuleFormatted: ModuleData = {
          ...createdMod,
          course: formData.id,
          lessons: [],
          exercise_module: [],
        };
        setFormData((prev) => ({
          ...prev,
          modules: [...prev.modules, newModuleFormatted],
        }));
        addToast("Module added successfully!", "success");
      } catch (err: any) {
        console.error("Failed to add module:", err);
        addToast("Failed to add module.", "error");
      }
    }
  };

  const handleUpdateModuleTitle = async (mId: string | number, title: string) => {
    try {
      await updateModule(mId, { title });
      setFormData((prev) => ({
        ...prev,
        modules: prev.modules.map((m) => (String(m.id) === String(mId) ? { ...m, title } : m)),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).map((m) =>
            String(m.id) === String(mId) ? { ...m, title } : m
          ),
        }))
      );
      addToast("Module title updated.", "success");
    } catch (err: any) {
      console.error("Failed to update module title:", err);
      addToast("Failed to update module title.", "error");
    }
  };

  const handleDeleteModule = async (mId: string | number) => {
    try {
      await deleteModule(mId);
      if (!isSuperAdmin) {
        setFormData((prev) => ({
          ...prev,
          approval_status: "PENDING",
          modules: prev.modules.map((m) =>
            String(m.id) === String(mId)
              ? { ...m, pending_deletion: true, is_approved: false }
              : m
          ),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).map((m) =>
              String(m.id) === String(mId)
                ? { ...m, pending_deletion: true, is_approved: false }
                : m
            ),
          }))
        );
        addToast("Module deletion request submitted for SuperAdmin approval.", "info");
      } else {
        setFormData((prev) => ({
          ...prev,
          modules: prev.modules.filter((m) => String(m.id) !== String(mId)),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).filter((m) => String(m.id) !== String(mId)),
          }))
        );
        addToast("Module deleted permanently.", "info");
      }
    } catch (err: any) {
      console.error("Failed to delete module:", err);
      addToast("Failed to delete module.", "error");
    }
  };

  const handleAddLesson = async (mId: string | number, lessonData: Partial<LessonData>) => {
    try {
      const created = await createLesson({
        module: mId,
        title: lessonData.title || "New Topic",
        duration: lessonData.duration || "15 mins",
        video_url: lessonData.video_url || "",
        content: lessonData.content || "",
        attachments: lessonData.attachments || [],
      });
      setFormData((prev) => ({
        ...prev,
        approval_status: !isSuperAdmin ? "PENDING" : prev.approval_status,
        modules: prev.modules.map((m) =>
          String(m.id) === String(mId) ? { ...m, lessons: [...(m.lessons || []), created] } : m
        ),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).map((m) =>
            String(m.id) === String(mId)
              ? { ...m, lessons: [...(m.lessons || []), created] }
              : m
          ),
        }))
      );
      if (!isSuperAdmin) {
        addToast("Topic added and submitted for SuperAdmin approval!", "info");
      } else {
        addToast("Topic added to module!", "success");
      }
    } catch (err: any) {
      console.error("Failed to add lesson:", err);
      addToast("Failed to add topic.", "error");
    }
  };

  const handleUpdateLesson = async (lId: string | number, lessonData: Partial<LessonData>) => {
    try {
      const updated = await updateLesson(lId, lessonData);
      setFormData((prev) => ({
        ...prev,
        approval_status: !isSuperAdmin ? "PENDING" : prev.approval_status,
        modules: prev.modules.map((m) => ({
          ...m,
          lessons: (m.lessons || []).map((l) => (String(l.id) === String(lId) ? { ...l, ...updated } : l)),
        })),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).map((m) => ({
            ...m,
            lessons: (m.lessons || []).map((l) =>
              String(l.id) === String(lId) ? { ...l, ...updated } : l
            ),
          })),
        }))
      );
      if (!isSuperAdmin) {
        addToast("Topic updated and submitted for SuperAdmin approval!", "info");
      } else {
        addToast("Topic updated successfully!", "success");
      }
    } catch (err: any) {
      console.error("Failed to update lesson:", err);
      addToast("Failed to update topic.", "error");
    }
  };

  const handleDeleteLesson = async (lId: string | number) => {
    try {
      await deleteLesson(lId);
      if (!isSuperAdmin) {
        setFormData((prev) => ({
          ...prev,
          approval_status: "PENDING",
          modules: prev.modules.map((m) => ({
            ...m,
            lessons: (m.lessons || []).map((l) =>
              String(l.id) === String(lId)
                ? { ...l, pending_deletion: true, is_approved: false }
                : l
            ),
          })),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).map((m) => ({
              ...m,
              lessons: (m.lessons || []).map((l) =>
                String(l.id) === String(lId)
                  ? { ...l, pending_deletion: true, is_approved: false }
                  : l
              ),
            })),
          }))
        );
        addToast("Topic deletion request submitted for SuperAdmin approval.", "info");
      } else {
        setFormData((prev) => ({
          ...prev,
          modules: prev.modules.map((m) => ({
            ...m,
            lessons: (m.lessons || []).filter((l) => String(l.id) !== String(lId)),
          })),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).map((m) => ({
              ...m,
              lessons: (m.lessons || []).filter((l) => String(l.id) !== String(lId)),
            })),
          }))
        );
        addToast("Topic deleted.", "info");
      }
    } catch (err: any) {
      console.error("Failed to delete lesson:", err);
      addToast("Failed to delete topic.", "error");
    }
  };

  const handleAddExercise = async (mId: string | number, exerciseData: Partial<ExerciseData>) => {
    try {
      const created = await createExercise({
        module: mId,
        title: exerciseData.title || "New Task",
        exercise_task: exerciseData.exercise_task || "Task requirements...",
        instructor_feedback: exerciseData.instructor_feedback || "",
        exercise_deadline: exerciseData.exercise_deadline || undefined,
        document_upload: exerciseData.document_upload || undefined,
        requires_autograder: Boolean(exerciseData.requires_autograder),
        autograder_template_repo: exerciseData.autograder_template_repo || "",
        autograder_module_name: exerciseData.autograder_module_name || "",
      });
      setFormData((prev) => ({
        ...prev,
        approval_status: !isSuperAdmin ? "PENDING" : prev.approval_status,
        modules: prev.modules.map((m) =>
          String(m.id) === String(mId)
            ? { ...m, exercise_module: [...(m.exercise_module || []), created] }
            : m
        ),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).map((m) =>
            String(m.id) === String(mId)
              ? { ...m, exercise_module: [...(m.exercise_module || []), created] }
              : m
          ),
        }))
      );
      if (!isSuperAdmin) {
        addToast("Task added and submitted for SuperAdmin approval!", "info");
      } else {
        addToast("Task added to module!", "success");
      }
    } catch (err: any) {
      console.error("Failed to add exercise:", err);
      addToast("Failed to add task.", "error");
    }
  };

  const handleUpdateExercise = async (eId: string | number, exerciseData: Partial<ExerciseData>) => {
    try {
      const updated = await updateExercise(eId, exerciseData);
      setFormData((prev) => ({
        ...prev,
        approval_status: !isSuperAdmin ? "PENDING" : prev.approval_status,
        modules: prev.modules.map((m) => ({
          ...m,
          exercise_module: (m.exercise_module || []).map((e) =>
            String(e.id) === String(eId) ? { ...e, ...updated } : e
          ),
        })),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).map((m) => ({
            ...m,
            exercise_module: (m.exercise_module || []).map((e) =>
              String(e.id) === String(eId) ? { ...e, ...updated } : e
            ),
          })),
        }))
      );
      if (!isSuperAdmin) {
        addToast("Task updated and submitted for SuperAdmin approval!", "info");
      } else {
        addToast("Task updated successfully!", "success");
      }
    } catch (err: any) {
      console.error("Failed to update exercise:", err);
      addToast("Failed to update task.", "error");
    }
  };

  const handleDeleteExercise = async (eId: string | number) => {
    try {
      await deleteExercise(eId);
      if (!isSuperAdmin) {
        setFormData((prev) => ({
          ...prev,
          approval_status: "PENDING",
          modules: prev.modules.map((m) => ({
            ...m,
            exercise_module: (m.exercise_module || []).map((e) =>
              String(e.id) === String(eId)
                ? { ...e, pending_deletion: true, is_approved: false }
                : e
            ),
          })),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).map((m) => ({
              ...m,
              exercise_module: (m.exercise_module || []).map((e) =>
                String(e.id) === String(eId)
                  ? { ...e, pending_deletion: true, is_approved: false }
                  : e
              ),
            })),
          }))
        );
        addToast("Task deletion request submitted for SuperAdmin approval.", "info");
      } else {
        setFormData((prev) => ({
          ...prev,
          modules: prev.modules.map((m) => ({
            ...m,
            exercise_module: (m.exercise_module || []).filter((e) => String(e.id) !== String(eId)),
          })),
        }));
        setCoursesInTrack((prev) =>
          prev.map((c) => ({
            ...c,
            modules: (c.modules || []).map((m) => ({
              ...m,
              exercise_module: (m.exercise_module || []).filter((e) => String(e.id) !== String(eId)),
            })),
          }))
        );
        addToast("Task deleted.", "info");
      }
    } catch (err: any) {
      console.error("Failed to delete exercise:", err);
      addToast("Failed to delete task.", "error");
    }
  };

  // --- Content Approval Actions (SuperAdmin) ---
  const handleApproveContentItem = async (
    itemType: "module" | "lesson" | "exercise",
    itemId: string | number,
    action: "approve" | "confirm_delete" | "restore"
  ) => {
    try {
      await approveContentItem(itemType, itemId, action);
      if (action === "confirm_delete") {
        if (itemType === "module") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.filter((m) => String(m.id) !== String(itemId)),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).filter((m) => String(m.id) !== String(itemId)),
            }))
          );
        } else if (itemType === "lesson") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              lessons: (m.lessons || []).filter((l) => String(l.id) !== String(itemId)),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                lessons: (m.lessons || []).filter((l) => String(l.id) !== String(itemId)),
              })),
            }))
          );
        } else if (itemType === "exercise") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              exercise_module: (m.exercise_module || []).filter((e) => String(e.id) !== String(itemId)),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                exercise_module: (m.exercise_module || []).filter((e) => String(e.id) !== String(itemId)),
              })),
            }))
          );
        }
        addToast("Item permanently deleted.", "info");
      } else if (action === "restore") {
        const restoreItem = (item: any) => ({
          ...item,
          pending_deletion: false,
          is_approved: true,
        });
        if (itemType === "module") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => String(m.id) === String(itemId) ? restoreItem(m) : m),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => String(m.id) === String(itemId) ? restoreItem(m) : m),
            }))
          );
        } else if (itemType === "lesson") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              lessons: (m.lessons || []).map((l) => String(l.id) === String(itemId) ? restoreItem(l) : l),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                lessons: (m.lessons || []).map((l) => String(l.id) === String(itemId) ? restoreItem(l) : l),
              })),
            }))
          );
        } else if (itemType === "exercise") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              exercise_module: (m.exercise_module || []).map((e) => String(e.id) === String(itemId) ? restoreItem(e) : e),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                exercise_module: (m.exercise_module || []).map((e) => String(e.id) === String(itemId) ? restoreItem(e) : e),
              })),
            }))
          );
        }
        addToast("Item restored and approved.", "success");
      } else {
        // action === 'approve'
        const approveItem = (item: any) => ({
          ...item,
          is_approved: true,
          pending_deletion: false,
        });
        if (itemType === "module") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => String(m.id) === String(itemId) ? approveItem(m) : m),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => String(m.id) === String(itemId) ? approveItem(m) : m),
            }))
          );
        } else if (itemType === "lesson") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              lessons: (m.lessons || []).map((l) => String(l.id) === String(itemId) ? approveItem(l) : l),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                lessons: (m.lessons || []).map((l) => String(l.id) === String(itemId) ? approveItem(l) : l),
              })),
            }))
          );
        } else if (itemType === "exercise") {
          setFormData((prev) => ({
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              exercise_module: (m.exercise_module || []).map((e) => String(e.id) === String(itemId) ? approveItem(e) : e),
            })),
          }));
          setCoursesInTrack((prev) =>
            prev.map((c) => ({
              ...c,
              modules: (c.modules || []).map((m) => ({
                ...m,
                exercise_module: (m.exercise_module || []).map((e) => String(e.id) === String(itemId) ? approveItem(e) : e),
              })),
            }))
          );
        }
        addToast("Item approved.", "success");
      }
    } catch (err: any) {
      console.error("Failed to execute approval action:", err);
      addToast("Failed to process approval action.", "error");
    }
  };

  const [isApprovingAll, setIsApprovingAll] = useState(false);
  const handleApproveAll = async () => {
    if (!formData.id) return;
    setIsApprovingAll(true);
    try {
      await approveAllCourseContent(formData.id);
      addToast("All pending curriculum changes have been approved!", "success");
      const approveItemRecursive = (m: ModuleData): ModuleData => ({
        ...m,
        is_approved: true,
        pending_deletion: false,
        lessons: (m.lessons || []).filter((l) => !l.pending_deletion).map((l) => ({ ...l, is_approved: true, pending_deletion: false })),
        exercise_module: (m.exercise_module || []).filter((e) => !e.pending_deletion).map((e) => ({ ...e, is_approved: true, pending_deletion: false })),
      });
      setFormData((prev) => ({
        ...prev,
        approval_status: "APPROVED",
        modules: prev.modules.filter((m) => !m.pending_deletion).map(approveItemRecursive),
      }));
      setCoursesInTrack((prev) =>
        prev.map((c) => ({
          ...c,
          modules: (c.modules || []).filter((m) => !m.pending_deletion).map(approveItemRecursive),
        }))
      );
    } catch (err: any) {
      console.error("Failed to approve all content:", err);
      addToast("Failed to approve all course content.", "error");
    } finally {
      setIsApprovingAll(false);
    }
  };

  const pendingCount = useMemo(() => {
    let count = 0;
    const allMods = formData.type === "track" 
      ? coursesInTrack.flatMap((c) => c.modules || []) 
      : formData.modules;
    for (const m of allMods) {
      if (m.is_approved === false || m.pending_deletion) count++;
      for (const l of m.lessons || []) {
        if (l.is_approved === false || l.pending_deletion) count++;
      }
      for (const e of (m.exercise_module || [])) {
        if (e.is_approved === false || e.pending_deletion) count++;
      }
    }
    return count;
  }, [formData.modules, coursesInTrack, formData.type]);

  // Preview Student View
  const handlePreviewStudentView = () => {
    const previewSlug = formData.slug || formData.id;
    if (!previewSlug) {
      addToast("Please save curriculum identity first.", "error");
      return;
    }
    const path = isSuperAdmin
      ? (formData.type === "track"
          ? `/superadmin/dashboard/tracks/${previewSlug}/preview?trackId=${formData.id}`
          : `/superadmin/dashboard/courses/${previewSlug}/preview?courseId=${formData.id}`)
      : (formData.type === "track"
          ? `/instructor/dashboard/my-tracks/${previewSlug}/preview?trackId=${formData.id}`
          : `/instructor/dashboard/my-courses/${previewSlug}/preview?courseId=${formData.id}`);
    navigate(path);
  };

  // Toggle Linked Course for Track
  const toggleCourseInTrack = (courseId: string | number) => {
    const isSelected = formData.course_ids.some((id) => String(id) === String(courseId));
    let nextIds: (string | number)[];

    if (isSelected) {
      nextIds = formData.course_ids.filter((id) => String(id) !== String(courseId));
      setCoursesInTrack((prev) => prev.filter((c) => String(c.id) !== String(courseId)));
    } else {
      nextIds = [...formData.course_ids, courseId];
      const found = availableCourses.find((c) => String(c.id) === String(courseId));
      if (found) {
        setCoursesInTrack((prev) => [...prev, { ...found, modules: found.modules || [] }]);
      }
    }
    setFormData((prev) => ({ ...prev, course_ids: nextIds }));
  };

  // Reorder Course Phase within Career Track
  const handleMoveCoursePhase = async (fromIndex: number, toIndex: number) => {
    if (coursesInTrack.length > 0) {
      if (toIndex < 0 || toIndex >= coursesInTrack.length || fromIndex === toIndex) return;

      const newCourses = [...coursesInTrack];
      const [moved] = newCourses.splice(fromIndex, 1);
      newCourses.splice(toIndex, 0, moved);

      setCoursesInTrack(newCourses);

      const newOrder = newCourses.map((c) => Number(c.id));
      setFormData((prev) => ({
        ...prev,
        course_ids: newOrder,
      }));

      if (formData.id) {
        try {
          await reorderTrackCourses(formData.id, newOrder);
          addToast(`Course phase moved to Phase 0${toIndex + 1}!`, "success");
        } catch (err) {
          console.error("Failed to persist phase order:", err);
          addToast("Failed to save new phase order on server.", "error");
        }
      }
    } else if (formData.course_ids.length > 0) {
      if (toIndex < 0 || toIndex >= formData.course_ids.length || fromIndex === toIndex) return;
      const newCourseIds = [...formData.course_ids];
      const [movedId] = newCourseIds.splice(fromIndex, 1);
      newCourseIds.splice(toIndex, 0, movedId);

      setFormData((prev) => ({
        ...prev,
        course_ids: newCourseIds,
      }));

      if (formData.id) {
        try {
          await reorderTrackCourses(formData.id, newCourseIds.map(Number));
          addToast(`Course phase moved to Phase 0${toIndex + 1}!`, "success");
        } catch (err) {
          console.error("Failed to persist phase order:", err);
        }
      }
    }
  };

  // Reorder Module within a Course (Standalone or inside Career Track Phase)
  const handleMoveModule = async (
    fromIndex: number,
    toIndex: number,
    targetCourseId?: string | number
  ) => {
    if (targetCourseId !== undefined) {
      const targetCourse = coursesInTrack.find((c) => String(c.id) === String(targetCourseId));
      const mods = targetCourse?.modules || [];
      if (toIndex < 0 || toIndex >= mods.length || fromIndex === toIndex) return;

      const newMods = [...mods];
      const [movedMod] = newMods.splice(fromIndex, 1);
      newMods.splice(toIndex, 0, movedMod);

      setCoursesInTrack((prev) =>
        prev.map((c) =>
          String(c.id) === String(targetCourseId) ? { ...c, modules: newMods } : c
        )
      );

      const newOrder = newMods.map((m) => Number(m.id)).filter((id) => !Number.isNaN(id));
      try {
        await reorderCourseModules(targetCourseId, newOrder);
        addToast(`Module moved to Module ${toIndex + 1}!`, "success");
      } catch (err) {
        console.error("Failed to persist module order:", err);
        addToast("Failed to save new module order on server.", "error");
      }
      return;
    }

    const mods = formData.modules || [];
    if (toIndex < 0 || toIndex >= mods.length || fromIndex === toIndex) return;

    const newMods = [...mods];
    const [movedMod] = newMods.splice(fromIndex, 1);
    newMods.splice(toIndex, 0, movedMod);

    setFormData((prev) => ({
      ...prev,
      modules: newMods,
    }));

    if (formData.id) {
      const newOrder = newMods.map((m) => Number(m.id)).filter((id) => !Number.isNaN(id));
      try {
        await reorderCourseModules(formData.id, newOrder);
        addToast(`Module moved to Module ${toIndex + 1}!`, "success");
      } catch (err) {
        console.error("Failed to persist module order:", err);
        addToast("Failed to save new module order on server.", "error");
      }
    }
  };

  // Reorder Topic (Lesson) within a Module
  const handleMoveLesson = async (
    moduleId: string | number,
    fromIndex: number,
    toIndex: number
  ) => {
    const standaloneMod = formData.modules.find((m) => String(m.id) === String(moduleId));
    const trackMod = coursesInTrack
      .flatMap((c) => c.modules || [])
      .find((m) => String(m.id) === String(moduleId));
    const currentLessons = standaloneMod?.lessons || trackMod?.lessons || [];

    if (toIndex < 0 || toIndex >= currentLessons.length || fromIndex === toIndex) return;

    const newLessons = [...currentLessons];
    const [movedLesson] = newLessons.splice(fromIndex, 1);
    newLessons.splice(toIndex, 0, movedLesson);

    setFormData((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        String(m.id) === String(moduleId) ? { ...m, lessons: newLessons } : m
      ),
    }));

    setCoursesInTrack((prev) =>
      prev.map((c) => ({
        ...c,
        modules: (c.modules || []).map((m) =>
          String(m.id) === String(moduleId) ? { ...m, lessons: newLessons } : m
        ),
      }))
    );

    const newOrder = newLessons.map((l) => Number(l.id)).filter((id) => !Number.isNaN(id));
    try {
      await reorderModuleLessons(moduleId, newOrder);
      addToast(`Topic moved to position ${toIndex + 1}!`, "success");
    } catch (err) {
      console.error("Failed to persist topic order:", err);
      addToast("Failed to save new topic order on server.", "error");
    }
  };

  // --- SuperAdmin Gemini Pro AI Curriculum Copilot State & Handlers ---
  const [geminiApiKey, setGeminiApiKey] = useState<string>(() => {
    try {
      return localStorage.getItem("cht_gemini_api_key") || "";
    } catch {
      return "";
    }
  });
  const [geminiModel, setGeminiModel] = useState<string>(() => {
    try {
      return localStorage.getItem("cht_gemini_model") || "gemini-2.5-pro";
    } catch {
      return "gemini-2.5-pro";
    }
  });
  const [geminiSystemPrompt, setGeminiSystemPrompt] = useState<string>(() => {
    try {
      return localStorage.getItem("cht_gemini_system_prompt") || "";
    } catch {
      return "";
    }
  });
  const [showGeminiConfig, setShowGeminiConfig] = useState(false);

  // Step 1: Course Identity AI Generation
  const [isGeneratingIdentity, setIsGeneratingIdentity] = useState(false);
  const [geminiIdentityPrompt, setGeminiIdentityPrompt] = useState("");
  const [identityPromptMode, setIdentityPromptMode] = useState<"augment" | "override">("augment");
  const [identityContentAction, setIdentityContentAction] = useState<"append" | "refine" | "replace">("append");
  const [identityUndoSnapshot, setIdentityUndoSnapshot] = useState<{
    description: string;
    requirement: string;
    why_choose: string;
    outline: string;
    outline_snippet: string;
  } | null>(null);

  const hasExistingIdentityContent = Boolean(
    formData.description?.trim() ||
      formData.requirement?.trim() ||
      formData.why_choose?.trim() ||
      formData.outline?.trim()
  );

  // Step 2: Syllabus Architect Modal State
  const [isSyllabusModalOpen, setIsSyllabusModalOpen] = useState(false);
  const [syllabusTargetCourseId, setSyllabusTargetCourseId] = useState<string | number | undefined>(undefined);
  const [syllabusModuleCount, setSyllabusModuleCount] = useState<number>(4);
  const [syllabusTopicsPerModule, setSyllabusTopicsPerModule] = useState<number>(3);
  const [syllabusIncludeAutograder, setSyllabusIncludeAutograder] = useState<boolean>(true);
  const [syllabusReplaceExisting, setSyllabusReplaceExisting] = useState<boolean>(false);
  const [syllabusExtraPrompt, setSyllabusExtraPrompt] = useState<string>("");
  const [syllabusPromptMode, setSyllabusPromptMode] = useState<"augment" | "override">("augment");
  const [isGeneratingSyllabus, setIsGeneratingSyllabus] = useState<boolean>(false);
  const [isApplyingSyllabus, setIsApplyingSyllabus] = useState<boolean>(false);
  const [generatedSyllabusModules, setGeneratedSyllabusModules] = useState<AISyllabusModule[]>([]);

  const appendIdentityPromptChip = (snippet: string) => {
    setGeminiIdentityPrompt((prev) => (prev.trim() ? `${prev.trim()}\n- ${snippet}` : `- ${snippet}`));
  };

  const appendSyllabusPromptChip = (snippet: string) => {
    setSyllabusExtraPrompt((prev) => (prev.trim() ? `${prev.trim()}\n- ${snippet}` : `- ${snippet}`));
  };

  const handleSaveGeminiConfig = (newKey: string, newModel: string, newSystemPrompt?: string) => {
    const trimmedKey = newKey.trim();
    const trimmedSystem = (newSystemPrompt !== undefined ? newSystemPrompt : geminiSystemPrompt).trim();
    setGeminiApiKey(trimmedKey);
    setGeminiModel(newModel);
    setGeminiSystemPrompt(trimmedSystem);
    try {
      if (trimmedKey) {
        localStorage.setItem("cht_gemini_api_key", trimmedKey);
      } else {
        localStorage.removeItem("cht_gemini_api_key");
      }
      localStorage.setItem("cht_gemini_model", newModel);
      if (trimmedSystem) {
        localStorage.setItem("cht_gemini_system_prompt", trimmedSystem);
      } else {
        localStorage.removeItem("cht_gemini_system_prompt");
      }
    } catch {
      // Ignore storage errors
    }
    addToast("Gemini Pro API key, model, and custom system persona saved!", "success");
  };

  const handleGenerateIdentityWithGemini = async () => {
    if (!formData.title.trim()) {
      addToast("Please enter a Curriculum Title first so Gemini Pro knows what to generate.", "warning");
      return;
    }
    const snapshot = {
      description: formData.description || "",
      requirement: formData.requirement || "",
      why_choose: formData.why_choose || "",
      outline: formData.outline || "",
      outline_snippet: formData.outline_snippet || "",
    };
    const effectiveAction = hasExistingIdentityContent ? identityContentAction : "replace";
    setIsGeneratingIdentity(true);
    try {
      const res = await generateAICurriculumContent({
        action: "identity",
        title: formData.title.trim(),
        level: formData.level,
        duration: formData.duration || "8 Weeks",
        item_type: formData.type,
        custom_prompt: geminiIdentityPrompt.trim(),
        system_prompt: geminiSystemPrompt || undefined,
        prompt_mode: identityPromptMode,
        content_action: effectiveAction,
        existing_content: {
          description: snapshot.description,
          requirement: snapshot.requirement,
          why_choose_course: snapshot.why_choose,
          outline: snapshot.outline,
          outline_snippet: snapshot.outline_snippet,
        },
        api_key: geminiApiKey || undefined,
        model: geminiModel || "gemini-2.5-pro",
      });
      const data = res?.data || {};
      setIdentityUndoSnapshot(snapshot);

      const mergeHtmlField = (existingVal: string, newVal?: string) => {
        const cleanExisting = (existingVal || "").trim();
        const cleanNew = (newVal || "").trim();
        if (!cleanNew) return existingVal;
        if (effectiveAction === "append" && cleanExisting) {
          return `${cleanExisting}\n\n${cleanNew}`;
        }
        return cleanNew;
      };

      setFormData((prev) => ({
        ...prev,
        description: mergeHtmlField(prev.description, data.description),
        requirement: mergeHtmlField(prev.requirement, data.requirement),
        why_choose: mergeHtmlField(prev.why_choose, data.why_choose_course),
        outline: mergeHtmlField(prev.outline, data.outline),
        outline_snippet:
          effectiveAction === "append" && prev.outline_snippet?.trim()
            ? prev.outline_snippet
            : data.outline_snippet || prev.outline_snippet,
      }));
      addToast(
        effectiveAction === "append"
          ? "New sections appended to your existing curriculum content with Gemini Pro!"
          : effectiveAction === "refine"
          ? "Existing curriculum draft refined and expanded with Gemini Pro!"
          : "Course description, prerequisites, and outline generated with Gemini Pro!",
        "success"
      );
    } catch (err: any) {
      console.error("Gemini identity generation failed:", err);
      const errMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to generate course identity with Gemini. Check your API key in Gemini Settings.";
      addToast(errMsg, "error");
      if (String(errMsg).toLowerCase().includes("api_key") || String(errMsg).toLowerCase().includes("api key")) {
        setShowGeminiConfig(true);
      }
    } finally {
      setIsGeneratingIdentity(false);
    }
  };

  const handleOpenSyllabusArchitectModal = (targetCourseId?: string | number) => {
    const resolvedId = targetCourseId || formData.id;
    if (!resolvedId) {
      addToast("Please save the curriculum identity first before generating a syllabus.", "warning");
      return;
    }
    setSyllabusTargetCourseId(resolvedId);
    setGeneratedSyllabusModules([]);
    setIsSyllabusModalOpen(true);
  };

  const handleGenerateSyllabusPreviewWithGemini = async () => {
    const targetCourse =
      formData.type === "track"
        ? coursesInTrack.find((c) => String(c.id) === String(syllabusTargetCourseId))
        : formData;
    const courseTitle = targetCourse?.title || formData.title;
    const courseLevel = targetCourse?.level || formData.level;
    const existingModulesForTarget = !syllabusReplaceExisting
      ? (targetCourse?.modules || []).map((m) => ({
          title: m.title,
          lessons: (m.lessons || []).map((l) => l.title),
        }))
      : [];

    if (!courseTitle?.trim()) {
      addToast("Course title is required to generate a syllabus.", "warning");
      return;
    }

    setIsGeneratingSyllabus(true);
    try {
      const res = await generateAICurriculumContent({
        action: "syllabus",
        title: courseTitle.trim(),
        course_title: courseTitle.trim(),
        level: courseLevel || "Beginner",
        description: targetCourse?.description || formData.description || "",
        module_count: syllabusModuleCount,
        topics_per_module: syllabusTopicsPerModule,
        lessons_per_module: syllabusTopicsPerModule,
        include_autograder: syllabusIncludeAutograder,
        include_exercises: syllabusIncludeAutograder,
        custom_prompt: syllabusExtraPrompt.trim(),
        system_prompt: geminiSystemPrompt || undefined,
        prompt_mode: syllabusPromptMode,
        existing_modules: existingModulesForTarget.length > 0 ? existingModulesForTarget : undefined,
        api_key: geminiApiKey || undefined,
        model: geminiModel || "gemini-2.5-pro",
      });
      const modulesList: AISyllabusModule[] = res?.data?.modules || [];
      if (modulesList.length === 0) {
        addToast("Gemini returned an empty syllabus. Please try again.", "warning");
      } else {
        setGeneratedSyllabusModules(modulesList);
        addToast(`Generated ${modulesList.length} modules with Gemini Pro! Review below and click Apply.`, "success");
      }
    } catch (err: any) {
      console.error("Gemini syllabus generation failed:", err);
      const errMsg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to generate syllabus with Gemini. Check your API key.";
      addToast(errMsg, "error");
    } finally {
      setIsGeneratingSyllabus(false);
    }
  };

  const handleBulkApplyGeneratedSyllabus = async () => {
    const resolvedCourseId = syllabusTargetCourseId || formData.id;
    if (!resolvedCourseId) {
      addToast("Missing target course ID.", "error");
      return;
    }
    if (generatedSyllabusModules.length === 0) {
      addToast("Please generate a syllabus preview first.", "warning");
      return;
    }

    setIsApplyingSyllabus(true);
    try {
      const res = await bulkApplyAISyllabus(resolvedCourseId, {
        modules: generatedSyllabusModules,
        replace_existing: syllabusReplaceExisting,
      });

      const createdNormalized: ModuleData[] = (res?.modules || []).map((m: any) => ({
        ...m,
        course: resolvedCourseId,
        lessons: m.lessons || [],
        exercise_module: m.exercise_module || m.exercises || [],
      }));

      if (formData.type === "course") {
        setFormData((prev) => ({
          ...prev,
          modules: syllabusReplaceExisting
            ? createdNormalized
            : [...prev.modules, ...createdNormalized],
        }));
      } else {
        setCoursesInTrack((prev) =>
          prev.map((c) => {
            if (String(c.id) !== String(resolvedCourseId)) return c;
            const updatedMods = syllabusReplaceExisting
              ? createdNormalized
              : [...(c.modules || []), ...createdNormalized];
            return { ...c, modules: updatedMods };
          })
        );
        setFormData((prev) => ({
          ...prev,
          modules: syllabusReplaceExisting
            ? [
                ...prev.modules.filter((m) => String(m.course) !== String(resolvedCourseId)),
                ...createdNormalized,
              ]
            : [...prev.modules, ...createdNormalized],
        }));
      }

      addToast(
        `Successfully applied ${createdNormalized.length} AI-generated modules to the course!`,
        "success"
      );
      setIsSyllabusModalOpen(false);
      setGeneratedSyllabusModules([]);
    } catch (err: any) {
      console.error("Failed to bulk-apply AI syllabus:", err);
      addToast(
        err?.response?.data?.error || "Failed to apply generated syllabus to course.",
        "error"
      );
    } finally {
      setIsApplyingSyllabus(false);
    }
  };

  if (loading && id && id !== "create") {
    return (
      <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-300">
        <header className="cht-flex cht-items-center cht-gap-4">
          <div className="cht-w-10 cht-h-10 cht-rounded-full cht-bg-slate-200 cht-animate-pulse" />
          <div className="cht-space-y-2">
            <div className="cht-w-32 cht-h-4 cht-rounded cht-bg-slate-200 cht-animate-pulse" />
            <div className="cht-w-64 cht-h-8 cht-rounded-lg cht-bg-slate-200 cht-animate-pulse" />
          </div>
        </header>
        <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-3 cht-gap-8">
          <div className="lg:cht-col-span-2 cht-space-y-6">
            <GlassCard className="cht-p-8 cht-rounded-[28px] cht-space-y-6">
              <div className="cht-w-40 cht-h-5 cht-rounded cht-bg-slate-200 cht-animate-pulse" />
              <div className="cht-h-12 cht-rounded-xl cht-bg-slate-100 cht-animate-pulse" />
              <div className="cht-h-44 cht-rounded-2xl cht-bg-slate-100 cht-animate-pulse" />
              <div className="cht-h-36 cht-rounded-2xl cht-bg-slate-100 cht-animate-pulse" />
            </GlassCard>
          </div>
          <div className="cht-space-y-6">
            <GlassCard className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
              <div className="cht-w-32 cht-h-5 cht-rounded cht-bg-slate-200 cht-animate-pulse" />
              <div className="cht-h-48 cht-rounded-2xl cht-bg-slate-100 cht-animate-pulse" />
            </GlassCard>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-500">
      {/* Top Header */}
      <header className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-4">
        <div className="cht-flex cht-items-center cht-gap-4">
          <button 
            type="button"
            onClick={() => navigate(returnPath)}
            className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-bg-white cht-rounded-full cht-border cht-border-gray-200 hover:cht-bg-gray-50 transition-colors"
            title="Return to Dashboard"
          >
            <ChevronLeft size={20} className="cht-text-gray-600" />
          </button>
          <div>
            <div className="cht-flex cht-items-center cht-gap-2.5 cht-mb-1">
              {isSuperAdmin ? (
                <span className="cht-px-2.5 cht-py-0.5 cht-rounded-lg cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-inline-flex cht-items-center cht-gap-1">
                  <ShieldCheck size={12} /> SuperAdmin Governance
                </span>
              ) : (
                <span className={`cht-w-2.5 cht-h-2.5 cht-rounded-full ${step === 1 ? "cht-bg-[#3e57da]" : "cht-bg-emerald-500"}`} />
              )}
              <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-gray-400">
                Step {step} of 2: {step === 1 ? "Program Identity & Configuration" : "Curriculum Architect"}
              </span>
            </div>
            <h1 className="cht-text-2xl md:cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
              {formData.id ? "Configure" : "Create"} {formData.type === "course" ? "Course" : "Career Track"}
            </h1>
          </div>
        </div>

        <div className="cht-flex cht-items-center cht-gap-3">
          {formData.id && (
            <Button 
              variant="outline" 
              size="sm" 
              leftIcon={<Eye size={14} />} 
              className="cht-rounded-xl"
              onClick={handlePreviewStudentView}
            >
              Preview Student View
            </Button>
          )}

          {step === 1 ? (
            <Button 
              variant="primary" 
              size="sm" 
              className="cht-rounded-xl"
              onClick={handleSaveIdentity}
              isLoading={isSaving}
            >
              Save & Continue to Curriculum
            </Button>
          ) : (
            <Button 
              variant="primary" 
              size="sm" 
              className="cht-rounded-xl"
              onClick={() => navigate(returnPath)}
            >
              Done & Return
            </Button>
          )}
        </div>
      </header>

      {/* Main Layout Grid */}
      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8">
        {/* Left / Main Workspace (8 cols) */}
        <div className="lg:cht-col-span-8 cht-space-y-6">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="cht-space-y-6"
              >
                {/* Type Selector (Course vs Track) */}
                <GlassCard className="cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-space-y-6">
                  <div>
                    <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider cht-mb-1">
                      Curriculum Format
                    </h3>
                    <p className="cht-text-xs cht-text-slate-500">
                      Select whether you are authoring a single standalone course or a multi-course career path.
                    </p>
                  </div>

                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: "course" })}
                      className={`cht-p-5 cht-rounded-2xl cht-border-2 cht-transition-all cht-text-left ${
                        formData.type === "course"
                          ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5"
                          : "cht-border-gray-100 hover:cht-border-gray-200"
                      }`}
                    >
                      <BookOpen className={formData.type === "course" ? "cht-text-[#3e57da]" : "cht-text-gray-400"} size={24} />
                      <h4 className="cht-mt-3 cht-text-sm cht-font-black cht-text-slate-900">Standalone Course</h4>
                      <p className="cht-text-[11px] cht-text-slate-500 cht-mt-0.5">Focused syllabus with lessons & exercises</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, type: "track" })}
                      className={`cht-p-5 cht-rounded-2xl cht-border-2 cht-transition-all cht-text-left ${
                        formData.type === "track"
                          ? "cht-border-[#3e57da] cht-bg-[#3e57da]/5"
                          : "cht-border-gray-100 hover:cht-border-gray-200"
                      }`}
                    >
                      <Layout className={formData.type === "track" ? "cht-text-[#3e57da]" : "cht-text-gray-400"} size={24} />
                      <h4 className="cht-mt-3 cht-text-sm cht-font-black cht-text-slate-900">Career Track</h4>
                      <p className="cht-text-[11px] cht-text-slate-500 cht-mt-0.5">Bundled multi-month mastery learning path</p>
                    </button>
                  </div>
                </GlassCard>

                {/* Primary Details Card */}
                <GlassCard className="cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-space-y-5">
                  <div className="cht-flex cht-items-center cht-justify-between">
                    <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                      Core Information
                    </h3>
                    <span className="cht-text-[11px] cht-font-bold cht-text-slate-400">
                      Identity & Discovery
                    </span>
                  </div>

                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
                    <Input
                      label="Curriculum Title"
                      placeholder="e.g. Master the Art of Fullstack UI/UX Design"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      leftIcon={<Type size={18} />}
                      required
                    />

                    <Input
                      label="Custom URL Slug (Optional)"
                      placeholder="e.g. fullstack-ui-ux-design"
                      value={formData.slug || ""}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      leftIcon={<Tag size={16} />}
                      helperText={formData.slug ? `Path: codehub.ng/courses/${formData.slug}` : "Auto-generated from title if blank"}
                    />
                  </div>

                  <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
                    <Input
                      label="Difficulty Level"
                      as="select"
                      value={formData.level}
                      onChange={(e) => setFormData({ ...formData, level: e.target.value as any })}
                      leftIcon={<Layers size={18} />}
                      options={[
                        { label: "Beginner", value: "Beginner" },
                        { label: "Intermediate", value: "Intermidiate" },
                        { label: "Advanced", value: "Advance" },
                      ]}
                    />

                    <Input
                      label="Estimated Duration"
                      placeholder="e.g. 8 Weeks, 40 Hours"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      leftIcon={<Clock size={18} />}
                    />
                  </div>

                  {formData.type === "course" && (
                    <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50/80 cht-border cht-border-indigo-100 cht-space-y-2">
                      <Input
                        label="Course GitHub Autograder Template Repository (Optional)"
                        placeholder="e.g. python-codehub-assignments-bundle"
                        value={formData.autograder_template_repo}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            autograder_template_repo: e.target.value.trim(),
                          })
                        }
                        leftIcon={<GitBranch size={18} />}
                        helperText="One private repository per student will be generated from this template for the entire course. Individual module exercises map to subfolders/test keys within this repo."
                      />
                    </div>
                  )}

                  {isSuperAdmin && (
                    <div className="cht-p-4 sm:cht-p-5 cht-rounded-2xl cht-bg-gradient-to-r cht-from-indigo-50/90 cht-via-violet-50/80 cht-to-blue-50/80 cht-border cht-border-indigo-200/80 cht-space-y-3.5">
                      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-2.5">
                        <div className="cht-flex cht-items-center cht-gap-2.5">
                          <div className="cht-w-8 cht-h-8 cht-rounded-xl cht-bg-[#3e57da] cht-text-white cht-flex cht-items-center cht-justify-center cht-shadow-sm">
                            <Sparkles size={16} />
                          </div>
                          <div>
                            <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                              <span className="cht-text-xs cht-font-black cht-uppercase cht-tracking-wider cht-text-indigo-950">
                                Gemini Pro Curriculum Copilot
                              </span>
                              <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-indigo-100 cht-text-[#3e57da] cht-text-[10px] cht-font-bold">
                                {geminiModel}
                              </span>
                              {geminiSystemPrompt && (
                                <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-violet-100 cht-text-violet-700 cht-text-[10px] cht-font-bold">
                                  Custom System Persona Active
                                </span>
                              )}
                              {hasExistingIdentityContent && (
                                <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-amber-100 cht-text-amber-800 cht-text-[10px] cht-font-bold">
                                  Existing Content Detected
                                </span>
                              )}
                            </div>
                            <p className="cht-text-[11px] cht-text-slate-600">
                              Generate fresh curriculum metadata, append new sections to your existing draft, or polish and expand what you already wrote.
                            </p>
                          </div>
                        </div>

                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <div className="cht-inline-flex cht-rounded-xl cht-bg-white/90 cht-border cht-border-indigo-200 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setIdentityContentAction("append")}
                              className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                                identityContentAction === "append"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-text-slate-600 hover:cht-text-slate-900"
                              }`}
                              title="Keep your existing Description, Prerequisites, Why Choose, and Outline, and append new sections below them"
                            >
                              ➕ Add to Existing
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdentityContentAction("refine")}
                              className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                                identityContentAction === "refine"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-text-slate-600 hover:cht-text-slate-900"
                              }`}
                              title="Polish, restructure, and expand your existing draft while preserving all original points"
                            >
                              ✨ Refine Draft
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdentityContentAction("replace")}
                              className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                                identityContentAction === "replace"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-text-slate-600 hover:cht-text-slate-900"
                              }`}
                              title="Generate fresh content from scratch and replace existing fields"
                            >
                              🔄 Replace
                            </button>
                          </div>

                          <div className="cht-inline-flex cht-rounded-xl cht-bg-white/90 cht-border cht-border-indigo-200 cht-p-0.5 cht-text-[10px] cht-font-bold">
                            <button
                              type="button"
                              onClick={() => setIdentityPromptMode("augment")}
                              className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                                identityPromptMode === "augment"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-text-slate-600 hover:cht-text-slate-900"
                              }`}
                              title="Combine your custom prompt with Codehub's structured curriculum template"
                            >
                              Augment Template
                            </button>
                            <button
                              type="button"
                              onClick={() => setIdentityPromptMode("override")}
                              className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                                identityPromptMode === "override"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-text-slate-600 hover:cht-text-slate-900"
                              }`}
                              title="Use your custom prompt as the primary directive"
                            >
                              Full Custom Prompt
                            </button>
                          </div>

                          <button
                            type="button"
                            onClick={() => setShowGeminiConfig((prev) => !prev)}
                            className="cht-px-2.5 cht-py-1.5 cht-rounded-xl cht-bg-white cht-border cht-border-indigo-200 cht-text-[11px] cht-font-bold cht-text-slate-700 hover:cht-bg-indigo-50 cht-transition-colors"
                          >
                            {showGeminiConfig ? "Hide Settings" : geminiApiKey ? "Key & Persona ✓" : "API Key & Persona"}
                          </button>

                          {identityUndoSnapshot && (
                            <button
                              type="button"
                              onClick={() => {
                                setFormData((prev) => ({
                                  ...prev,
                                  description: identityUndoSnapshot.description,
                                  requirement: identityUndoSnapshot.requirement,
                                  why_choose: identityUndoSnapshot.why_choose,
                                  outline: identityUndoSnapshot.outline,
                                  outline_snippet: identityUndoSnapshot.outline_snippet,
                                }));
                                setIdentityUndoSnapshot(null);
                                addToast("Reverted Course Identity fields to their previous state.", "info");
                              }}
                              className="cht-px-2.5 cht-py-1.5 cht-rounded-xl cht-bg-amber-50 hover:cht-bg-amber-100 cht-border cht-border-amber-200 cht-text-[11px] cht-font-bold cht-text-amber-800 cht-transition-colors"
                              title="Undo the last Gemini change to Description, Prerequisites, Why Choose, and Outline"
                            >
                              ↩ Undo AI
                            </button>
                          )}

                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            leftIcon={<Sparkles size={14} />}
                            onClick={handleGenerateIdentityWithGemini}
                            isLoading={isGeneratingIdentity}
                            className="cht-rounded-xl"
                          >
                            {!hasExistingIdentityContent
                              ? "Auto-Fill Identity with Gemini"
                              : identityContentAction === "append"
                              ? "Add to Existing Identity"
                              : identityContentAction === "refine"
                              ? "Refine & Expand Identity"
                              : "Replace Identity"}
                          </Button>
                        </div>
                      </div>

                      <textarea
                        rows={3}
                        value={geminiIdentityPrompt}
                        onChange={(e) => setGeminiIdentityPrompt(e.target.value)}
                        placeholder={
                          hasExistingIdentityContent && identityContentAction === "append"
                            ? "Tell Gemini Pro what to ADD to your existing course overview, prerequisites, or outline (e.g., 'Add a capstone project milestone table, 3 cloud deployment outcomes, and advanced Docker prerequisites')..."
                            : hasExistingIdentityContent && identityContentAction === "refine"
                            ? "Tell Gemini Pro how to REFINE & EXPAND your existing draft (e.g., 'Expand my rough bullet points into a rich HTML curriculum overview while keeping all original tools and topics')..."
                            : identityPromptMode === "override"
                            ? "Enter your full custom prompt for Gemini Pro (e.g., 'Write a complete course overview for a 10-week Fintech Engineering bootcamp using FastAPI, PostgreSQL, Docker, and GitHub Actions CI/CD...')..."
                            : "Enter your custom prompt or focus instructions (e.g., 'Focus on fintech backend APIs, FastAPI, PostgreSQL, Docker deployment, and include 5 portfolio project milestones')..."
                        }
                        className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-xs cht-bg-white cht-border cht-border-indigo-200/80 cht-rounded-xl focus:cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/20 cht-resize-y"
                      />

                      <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-flex-wrap">
                        <div className="cht-flex cht-items-center cht-gap-1.5 cht-flex-wrap">
                          <span className="cht-text-[10px] cht-font-bold cht-text-indigo-900/60">
                            Quick Prompts:
                          </span>
                          {[
                            "Add week-by-week project milestones",
                            "Add real-world portfolio & capstone outcomes",
                            "Expand prerequisites & setup checklist",
                            "Add production architecture & cloud deployment section",
                          ].map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => appendIdentityPromptChip(chip)}
                              className="cht-text-[10px] cht-font-semibold cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-white/90 hover:cht-bg-white cht-border cht-border-indigo-200/60 cht-text-slate-700 hover:cht-text-[#3e57da] cht-transition-colors"
                            >
                              + {chip}
                            </button>
                          ))}
                        </div>
                        {geminiIdentityPrompt && (
                          <button
                            type="button"
                            onClick={() => setGeminiIdentityPrompt("")}
                            className="cht-text-[10px] cht-font-semibold cht-text-slate-500 hover:cht-text-rose-600"
                          >
                            Clear Prompt
                          </button>
                        )}
                      </div>

                      {showGeminiConfig && (
                        <div className="cht-p-4 cht-rounded-xl cht-bg-white/95 cht-border cht-border-indigo-200 cht-space-y-3.5">
                          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-3">
                            <div className="sm:cht-col-span-2">
                              <label className="cht-block cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600 cht-mb-1">
                                Google AI Studio / Gemini API Key (Optional if set in backend .env)
                              </label>
                              <input
                                type="password"
                                value={geminiApiKey}
                                onChange={(e) => setGeminiApiKey(e.target.value)}
                                placeholder="AIzaSy... (Saved in browser localStorage for SuperAdmin)"
                                className="cht-w-full cht-px-3 cht-py-2 cht-text-xs cht-font-mono cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-lg focus:cht-outline-none focus:cht-border-[#3e57da]"
                              />
                            </div>
                            <div>
                              <label className="cht-block cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600 cht-mb-1">
                                Gemini Model
                              </label>
                              <select
                                value={geminiModel}
                                onChange={(e) => setGeminiModel(e.target.value)}
                                className="cht-w-full cht-px-3 cht-py-2 cht-text-xs cht-font-bold cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-lg focus:cht-outline-none focus:cht-border-[#3e57da]"
                              >
                                <option value="gemini-2.5-pro">Gemini 2.5 Pro (Deep Reasoning)</option>
                                <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fast Drafting)</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-mb-1">
                              <label className="cht-block cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600">
                                Global Custom System Prompt / Teaching Persona (Applies to All AI Generations)
                              </label>
                              <div className="cht-flex cht-items-center cht-gap-1.5 cht-flex-wrap">
                                {[
                                  {
                                    label: "Bootcamp Mentor",
                                    prompt:
                                      "Write with the voice of an encouraging, hands-on coding bootcamp mentor. Emphasize practical real-world projects, clean code habits, and step-by-step clarity.",
                                  },
                                  {
                                    label: "Production Architect",
                                    prompt:
                                      "Write from the perspective of a Principal Systems Architect. Emphasize scalability, security best practices, edge-case handling, and automated testing.",
                                  },
                                  {
                                    label: "Socratic Beginner Guide",
                                    prompt:
                                      "Explain every technical concept from first principles using everyday analogies before introducing syntax. Avoid unexplained jargon.",
                                  },
                                ].map((preset) => (
                                  <button
                                    key={preset.label}
                                    type="button"
                                    onClick={() => setGeminiSystemPrompt(preset.prompt)}
                                    className="cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded cht-bg-indigo-50 cht-text-[#3e57da] hover:cht-bg-indigo-100"
                                  >
                                    {preset.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <textarea
                              rows={2}
                              value={geminiSystemPrompt}
                              onChange={(e) => setGeminiSystemPrompt(e.target.value)}
                              placeholder="Optional global system instruction (e.g., 'Always use Python 3.11+ type annotations, PEP 8 conventions, and African fintech/e-commerce domain examples')..."
                              className="cht-w-full cht-px-3 cht-py-2 cht-text-xs cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-lg focus:cht-outline-none focus:cht-border-[#3e57da] cht-resize-y"
                            />
                          </div>

                          <div className="cht-flex cht-items-center cht-justify-between cht-gap-2">
                            <span className="cht-text-[11px] cht-text-slate-500">
                              Your key & custom system persona are saved locally and sent securely over HTTPS.
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="cht-rounded-lg"
                              onClick={() => {
                                handleSaveGeminiConfig(geminiApiKey, geminiModel, geminiSystemPrompt);
                                setShowGeminiConfig(false);
                              }}
                            >
                              Save Settings
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <RichTextEditor
                    key={`course-desc-${id || "new"}`}
                    label="Description & Learning Objectives"
                    placeholder="Describe what students will learn, hands-on tasks, and key takeaways..."
                    value={formData.description}
                    onChange={(data) => setFormData((prev) => ({ ...prev, description: data }))}
                    minHeight="200px"
                    isLoading={loading || isGeneratingIdentity}
                    loadingText={isGeneratingIdentity ? "Drafting description with AI..." : loading ? "Loading course content..." : undefined}
                  />

                  <RichTextEditor
                    key={`course-req-${id || "new"}`}
                    label="Prerequisites & Requirements"
                    placeholder="e.g. Basic JavaScript knowledge, laptop with 8GB RAM..."
                    value={formData.requirement}
                    onChange={(data) => setFormData((prev) => ({ ...prev, requirement: data }))}
                    minHeight="150px"
                    isLoading={loading || isGeneratingIdentity}
                    loadingText={isGeneratingIdentity ? "Drafting requirements with AI..." : loading ? "Loading course content..." : undefined}
                  />
                </GlassCard>

                {/* Academic Leadership & Lead Faculty */}
                <GlassCard className="cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-space-y-5">
                  <div className="cht-flex cht-items-center cht-justify-between">
                    <div>
                      <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                        Academic Leadership & Lead Instructor
                      </h3>
                      <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5">
                        Assign a certified faculty member to lead this program, conduct code reviews, and provide mentorship.
                      </p>
                    </div>
                    <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-text-[11px] cht-font-bold cht-flex cht-items-center cht-gap-1">
                      <ShieldCheck size={14} /> Faculty Assigned
                    </span>
                  </div>

                  {!isSuperAdmin ? (
                    <div className="cht-p-3.5 cht-rounded-xl cht-bg-slate-50 cht-border cht-border-gray-200 cht-flex cht-items-center cht-gap-2.5 cht-text-xs cht-text-slate-600">
                      <ShieldCheck size={16} className="cht-text-[#3e57da] cht-shrink-0" />
                      <span>Assigned to your faculty profile by the SuperAdmin. Faculty program assignments are managed exclusively by the SuperAdmin.</span>
                    </div>
                  ) : (
                    <Input
                      label="Select Lead Faculty / Instructor"
                      as="select"
                      value={formData.instructor ? String(formData.instructor) : ""}
                      onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                      leftIcon={<User size={18} />}
                      options={[
                        { label: "Unassigned / Platform Led (Codehub Academic Board)", value: "" },
                        ...instructors.map((instr) => ({
                          label: `${instr.name} (★ ${instr.rating || 4.9} • ${instr.students_count || "12k+"} students)`,
                          value: String(instr.id),
                        })),
                      ]}
                    />
                  )}

                  {selectedInstructor && (
                    <div className="cht-p-4 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-gray-200/80 cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-gap-4 cht-transition-all">
                      <img
                        src={selectedInstructor.get_photo_url || "https://cdn-icons-png.flaticon.com/512/147/147142.png"}
                        alt={selectedInstructor.name}
                        className="cht-w-14 cht-h-14 cht-rounded-2xl cht-object-cover cht-border-2 cht-border-white cht-shadow-sm cht-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = "https://cdn-icons-png.flaticon.com/512/147/147142.png";
                        }}
                      />
                      <div className="cht-min-w-0 cht-flex-1">
                        <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap">
                          <h4 className="cht-text-sm cht-font-black cht-text-slate-900">
                            {selectedInstructor.name}
                          </h4>
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200">
                            <Check size={10} /> Verified Lead Faculty
                          </span>
                        </div>
                        <div className="cht-flex cht-items-center cht-gap-3 cht-mt-1 cht-text-[11px] cht-text-slate-500">
                          <span className="cht-flex cht-items-center cht-gap-1 cht-text-amber-600 cht-font-bold">
                            <Star size={12} fill="#d97706" /> {selectedInstructor.rating || "4.9"} Instructor Rating
                          </span>
                          <span>•</span>
                          <span>{selectedInstructor.students_count || "12k+"} Students Mentored</span>
                        </div>
                        {selectedInstructor.bio && (
                          <p className="cht-text-xs cht-text-slate-600 cht-mt-1.5 cht-line-clamp-2 cht-leading-relaxed">
                            {selectedInstructor.bio.replace(/<[^>]*>?/gm, "")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </GlassCard>

                {/* Syllabus & Academic Positioning Card */}
                <GlassCard className="cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-space-y-5">
                  <div>
                    <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                      Syllabus & Academic Positioning
                    </h3>
                    <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5">
                      Configure high-level curriculum outline, marketing highlights, and short preview snippets.
                    </p>
                  </div>

                  <Input
                    label="Card Snippet / Summary (Max 250 characters)"
                    placeholder="Concise overview snippet rendered on search and catalogue summary cards..."
                    value={formData.outline_snippet}
                    onChange={(e) => setFormData((prev) => ({ ...prev, outline_snippet: e.target.value }))}
                    helperText={`${formData.outline_snippet?.length || 0}/250 characters`}
                    leftIcon={<Tag size={16} />}
                  />

                  <RichTextEditor
                    key={`course-why-${id || "new"}`}
                    label="Why Choose This Program (Value Proposition)"
                    placeholder="Highlight the unique advantages, industry-standard deliverables, and career pathways..."
                    value={formData.why_choose}
                    onChange={(data) => setFormData((prev) => ({ ...prev, why_choose: data }))}
                    minHeight="160px"
                    isLoading={loading || isGeneratingIdentity}
                    loadingText={isGeneratingIdentity ? "Drafting value proposition with AI..." : loading ? "Loading course content..." : undefined}
                  />

                  <RichTextEditor
                    key={`course-outline-${id || "new"}`}
                    label="Executive Outline / High-Level Syllabus"
                    placeholder="Breakdown of core phases, topics, tools mastered, and milestones..."
                    value={formData.outline}
                    onChange={(data) => setFormData((prev) => ({ ...prev, outline: data }))}
                    minHeight="200px"
                    isLoading={loading || isGeneratingSyllabus || isApplyingSyllabus}
                    loadingText={
                      isGeneratingSyllabus
                        ? "AI is structuring syllabus outline..."
                        : isApplyingSyllabus
                        ? "Applying syllabus to course..."
                        : loading
                        ? "Loading course content..."
                        : undefined
                    }
                  />
                </GlassCard>

                {/* For Tracks: Select Included Courses */}
                {formData.type === "track" && (
                  <GlassCard className="cht-p-6 sm:cht-p-8 cht-rounded-[28px] cht-space-y-5">
                    <div>
                      <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                        Courses in this Career Track ({formData.course_ids.length} selected)
                      </h3>
                      <p className="cht-text-xs cht-text-slate-500 cht-mt-0.5">
                        Select which existing standalone courses are sequenced together into this track.
                      </p>
                    </div>

                    <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-3">
                      {availableCourses.map((c) => {
                        const isSelected = formData.course_ids.includes(c.id!);
                        return (
                          <div
                            key={c.id}
                            onClick={() => toggleCourseInTrack(c.id!)}
                            className={`cht-p-3.5 cht-rounded-xl cht-border cht-cursor-pointer cht-flex cht-items-center cht-justify-between cht-transition-all ${
                              isSelected
                                ? "cht-bg-[#3e57da]/10 cht-border-[#3e57da] cht-text-slate-900"
                                : "cht-bg-white cht-border-gray-200 hover:cht-border-gray-300"
                            }`}
                          >
                            <span className="cht-text-xs cht-font-bold cht-line-clamp-1">{c.title}</span>
                            <div className={`cht-w-5 cht-h-5 cht-rounded-md cht-flex cht-items-center cht-justify-center ${
                              isSelected ? "cht-bg-[#3e57da] cht-text-white" : "cht-border cht-border-gray-300"
                            }`}>
                              {isSelected && <Check size={12} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Ordered Phase Sequence Preview in Step 1 */}
                    {formData.course_ids.length > 0 && (
                      <div className="cht-pt-4 cht-border-t cht-border-gray-100 cht-space-y-3">
                        <div className="cht-flex cht-items-center cht-justify-between">
                          <span className="cht-text-xs cht-font-bold cht-text-slate-700">
                            Phase Sequence ({formData.course_ids.length} Phases)
                          </span>
                          <span className="cht-text-[11px] cht-text-slate-400">
                            Use arrows to arrange phase sequence order
                          </span>
                        </div>

                        <div className="cht-space-y-2">
                          {formData.course_ids.map((cid, idx) => {
                            const courseObj = availableCourses.find((c) => String(c.id) === String(cid)) ||
                              coursesInTrack.find((c) => String(c.id) === String(cid));
                            const title = courseObj?.title || `Course ID #${cid}`;

                            return (
                              <div
                                key={cid}
                                className="cht-p-3 cht-rounded-xl cht-bg-slate-50 cht-border cht-border-gray-100 cht-flex cht-items-center cht-justify-between cht-gap-3"
                              >
                                <div className="cht-flex cht-items-center cht-gap-2.5 cht-min-w-0">
                                  <span className="cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-[#3e57da] cht-text-white cht-text-[10px] cht-font-bold cht-uppercase">
                                    Phase 0{idx + 1}
                                  </span>
                                  <span className="cht-text-xs cht-font-semibold cht-text-slate-800 cht-truncate">
                                    {title}
                                  </span>
                                </div>

                                <div className="cht-flex cht-items-center cht-gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleMoveCoursePhase(idx, idx - 1)}
                                    disabled={idx === 0}
                                    className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                      idx === 0
                                        ? "cht-text-slate-300 cht-cursor-not-allowed"
                                        : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da] hover:cht-shadow-xs"
                                    }`}
                                    title="Move Phase Earlier"
                                  >
                                    <ArrowUp size={12} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleMoveCoursePhase(idx, idx + 1)}
                                    disabled={idx === formData.course_ids.length - 1}
                                    className={`cht-w-6 cht-h-6 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                      idx === formData.course_ids.length - 1
                                        ? "cht-text-slate-300 cht-cursor-not-allowed"
                                        : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da] hover:cht-shadow-xs"
                                    }`}
                                    title="Move Phase Later"
                                  >
                                    <ArrowDown size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </GlassCard>
                )}

                <div className="cht-flex cht-justify-end">
                  <Button 
                    variant="primary" 
                    size="lg" 
                    className="cht-rounded-2xl cht-px-8"
                    onClick={handleSaveIdentity}
                    isLoading={isSaving}
                  >
                    Next: Build Curriculum Modules
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="cht-space-y-6"
              >
                {/* Governance & Approval Review Banner */}
                {isSuperAdmin && pendingCount > 0 ? (
                  <div className="cht-p-5 cht-rounded-2xl cht-bg-gradient-to-r cht-from-amber-500/10 cht-via-indigo-50/50 cht-to-emerald-50/20 cht-border cht-border-amber-300/80 cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-4 cht-shadow-sm">
                    <div className="cht-flex cht-items-center cht-gap-3.5">
                      <div className="cht-w-10 cht-h-10 cht-rounded-xl cht-bg-amber-500/20 cht-text-amber-700 cht-flex cht-items-center cht-justify-center cht-shrink-0">
                        <AlertCircle size={22} />
                      </div>
                      <div>
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <h4 className="cht-text-sm cht-font-black cht-text-slate-900">
                            Faculty Curriculum Review Pending
                          </h4>
                          <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-amber-200/80 cht-text-amber-900">
                            {pendingCount} Pending Item{pendingCount === 1 ? "" : "s"}
                          </span>
                        </div>
                        <p className="cht-text-xs cht-text-slate-600 cht-mt-0.5">
                          Assigned faculty have submitted curriculum changes or deletion requests. You can approve or reject items individually or batch-approve all pending items.
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      className="cht-rounded-xl cht-bg-emerald-600 hover:cht-bg-emerald-700 cht-text-white cht-shadow-md !cht-py-2 !cht-px-4 cht-shrink-0"
                      leftIcon={<Check size={14} />}
                      isLoading={isApprovingAll}
                      onClick={handleApproveAll}
                    >
                      Approve All Pending Changes
                    </Button>
                  </div>
                ) : !isSuperAdmin ? (
                  <div className="cht-p-4 cht-rounded-2xl cht-bg-blue-50/60 cht-border cht-border-blue-100 cht-flex cht-items-center cht-gap-3">
                    <div className="cht-w-8 cht-h-8 cht-rounded-lg cht-bg-blue-100 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
                      <ShieldCheck size={18} />
                    </div>
                    <p className="cht-text-xs cht-text-slate-600">
                      <strong className="cht-text-slate-800">Faculty Curriculum Studio:</strong> Content you author or update is submitted for SuperAdmin review before students can view it.
                      {pendingCount > 0 && (
                        <span className="cht-block cht-mt-0.5 cht-font-bold cht-text-amber-700">
                          • {pendingCount} item{pendingCount === 1 ? "" : "s"} awaiting SuperAdmin approval.
                        </span>
                      )}
                    </p>
                  </div>
                ) : null}

                {formData.type === "track" ? (
                  <div className="cht-space-y-6">
                    {/* Track Architect Header */}
                    <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-3">
                      <div>
                        <div className="cht-flex cht-items-center cht-gap-2 cht-mb-1">
                          <span className="cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
                            Career Track Architect
                          </span>
                          <span className="cht-text-xs cht-text-slate-400 cht-font-medium">
                            {coursesInTrack.length} {coursesInTrack.length === 1 ? "Course Phase" : "Course Phases"}
                          </span>
                        </div>
                        <h3 className="cht-text-xl cht-font-black cht-text-slate-900 !cht-font-heading">
                          Track Curriculum & Phases
                        </h3>
                        <p className="cht-text-xs cht-text-slate-500">
                          Structure your multi-course track into progressive learning phases, modules, topics, and hands-on deliverables.
                        </p>
                      </div>
                      {coursesInTrack.length > 0 && (
                        <Button 
                          variant="primary" 
                          size="sm" 
                          leftIcon={<FolderPlus size={15} />} 
                          className="cht-rounded-xl"
                          onClick={() => handleAddModule(activeCourse ? activeCourse.id : undefined)}
                        >
                          {activeCourse ? `Add Module to Phase 0${activeCoursePhaseNumber}` : "Add Module to Track"}
                        </Button>
                      )}
                    </div>

                    {/* If Track has no linked courses */}
                    {coursesInTrack.length === 0 ? (
                      <div className="cht-py-16 cht-px-6 cht-text-center cht-bg-white cht-rounded-[28px] cht-border cht-border-dashed cht-border-gray-200 cht-space-y-4">
                        <div className="cht-w-16 cht-h-16 cht-rounded-2xl cht-bg-indigo-50 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-mx-auto">
                          <Layers size={28} />
                        </div>
                        <div className="cht-space-y-1.5 cht-max-w-md cht-mx-auto">
                          <h4 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                            No Courses Linked to this Track Yet
                          </h4>
                          <p className="cht-text-xs cht-text-slate-500 cht-leading-relaxed">
                            A Career Track is composed of progressive courses (Phases). Go back to Step 1 to select or link existing courses to this track before architecting modules.
                          </p>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          className="cht-rounded-xl"
                          leftIcon={<ChevronLeft size={16} />}
                          onClick={() => setStep(1)}
                        >
                          Back to Step 1: Link Courses
                        </Button>
                      </div>
                    ) : (
                      <>
                        {/* Career Track Course Milestone Roadmap */}
                        <div className="cht-space-y-4">
                          <div className="cht-flex cht-items-center cht-justify-between">
                            <div className="cht-flex cht-items-center cht-gap-2">
                              <Layers size={18} className="cht-text-[#3e57da]" />
                              <h4 className="cht-text-sm sm:cht-text-base cht-font-bold cht-text-slate-900 !cht-font-heading">
                                Track Courses & Milestones ({coursesInTrack.length})
                              </h4>
                            </div>
                            
                            <button
                              type="button"
                              onClick={() => setSelectedCourseId("all")}
                              className={`cht-px-3.5 cht-py-1.5 cht-rounded-xl cht-text-xs cht-font-semibold cht-transition-all ${
                                selectedCourseId === "all"
                                  ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                                  : "cht-bg-white cht-text-slate-600 hover:cht-bg-slate-50 cht-border cht-border-slate-200/80"
                              }`}
                            >
                              All Courses ({coursesInTrack.length})
                            </button>
                          </div>

                          {/* Course Milestone Cards Grid */}
                          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-3.5">
                            {coursesInTrack.map((course, idx) => {
                              const isSelected = String(selectedCourseId) === String(course.id);
                              const cModules = course.modules || [];
                              const cLessonsCount = cModules.reduce((acc, m) => acc + (m.lessons?.length || 0), 0);
                              const cTasksCount = cModules.reduce((acc, m) => acc + ((m.exercise_module || (m as any).exercises)?.length || 0), 0);

                              return (
                                <div
                                  key={course.id || idx}
                                  onClick={() => setSelectedCourseId(course.id!)}
                                  className={`cht-p-5 cht-rounded-[22px] cht-border cht-cursor-pointer cht-transition-all cht-relative cht-overflow-hidden ${
                                    isSelected
                                      ? "cht-bg-white cht-border-[#3e57da] cht-shadow-[0_4px_20px_rgba(62,87,218,0.1)] cht-ring-2 cht-ring-[#3e57da]/20"
                                      : "cht-bg-white/90 cht-border-slate-200/80 hover:cht-border-slate-300 hover:cht-bg-white hover:cht-shadow-sm"
                                  }`}
                                >
                                  <div className="cht-flex cht-items-center cht-justify-between cht-gap-2">
                                    <span className="cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-slate-100 cht-text-slate-600 cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider">
                                      Phase 0{idx + 1}
                                    </span>
                                    <div className="cht-flex cht-items-center cht-gap-1.5">
                                      <span className="cht-text-[11px] cht-font-bold cht-text-[#3e57da] cht-bg-[#3e57da]/10 cht-px-2 cht-py-0.5 cht-rounded-md">
                                        {cModules.length} {cModules.length === 1 ? "module" : "modules"}
                                      </span>
                                      {/* Reorder Arrows */}
                                      {coursesInTrack.length > 1 && (
                                        <div className="cht-flex cht-items-center cht-gap-0.5 cht-bg-slate-100 cht-p-0.5 cht-rounded-lg">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveCoursePhase(idx, idx - 1);
                                            }}
                                            disabled={idx === 0}
                                            className={`cht-w-5 cht-h-5 cht-rounded cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                              idx === 0
                                                ? "cht-text-slate-300 cht-cursor-not-allowed"
                                                : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da] hover:cht-shadow-xs"
                                            }`}
                                            title="Move Phase Earlier"
                                          >
                                            <ArrowLeft size={11} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveCoursePhase(idx, idx + 1);
                                            }}
                                            disabled={idx === coursesInTrack.length - 1}
                                            className={`cht-w-5 cht-h-5 cht-rounded cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                              idx === coursesInTrack.length - 1
                                                ? "cht-text-slate-300 cht-cursor-not-allowed"
                                                : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da] hover:cht-shadow-xs"
                                            }`}
                                            title="Move Phase Later"
                                          >
                                            <ArrowRight size={11} />
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <h5 className="cht-text-sm sm:cht-text-[15px] cht-font-bold cht-text-slate-900 cht-mt-2.5 cht-line-clamp-2 hover:cht-text-[#3e57da] cht-transition-colors !cht-font-heading">
                                    {course.title}
                                  </h5>

                                  <p className="cht-text-xs cht-text-slate-400 cht-mt-1">
                                    {cLessonsCount} topics • {cTasksCount} tasks • {course.duration || "Self-paced"}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Active Course View or All Courses Grouped View */}
                        {activeCourse ? (
                          <div className="cht-space-y-6">
                            {/* Active Course Banner */}
                            <div className="cht-bg-white cht-border cht-border-indigo-100 cht-rounded-[24px] cht-p-5 sm:cht-p-6 cht-shadow-[0_4px_20px_rgba(62,87,218,0.06)] cht-relative cht-overflow-hidden">
                              <div className="cht-absolute cht-left-0 cht-top-0 cht-bottom-0 cht-w-1.5 cht-bg-[#3e57da]" />

                              <div className="cht-flex cht-flex-col lg:cht-flex-row lg:cht-items-center lg:cht-justify-between cht-gap-5">
                                <div className="cht-space-y-2 cht-flex-1 cht-min-w-0">
                                  <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
                                    <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-[#3e57da] cht-text-white cht-text-[10px] cht-font-bold cht-uppercase cht-tracking-wider cht-shadow-sm">
                                      <Layers size={11} />
                                      Current Course Phase
                                    </span>
                                    <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-indigo-50 cht-text-[#3e57da] cht-text-xs cht-font-bold cht-border cht-border-indigo-100/80">
                                      Phase 0{activeCoursePhaseNumber} of {coursesInTrack.length}
                                    </span>
                                  </div>

                                  <h4 className="cht-text-lg sm:cht-text-xl cht-font-extrabold cht-text-slate-900 cht-tracking-tight !cht-font-heading">
                                    {activeCourse.title}
                                  </h4>

                                  {activeCourse.description && (
                                    <p className="cht-text-xs sm:cht-text-[13px] cht-text-slate-600 cht-line-clamp-2 cht-max-w-3xl">
                                      {activeCourse.description.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}
                                    </p>
                                  )}
                                </div>

                                <div className="cht-flex cht-items-center cht-gap-2.5 cht-shrink-0">
                                  {coursesInTrack.length > 1 && (
                                    <div className="cht-flex cht-items-center cht-gap-1 cht-bg-slate-50 cht-p-1 cht-rounded-xl cht-border cht-border-slate-200/80">
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCoursePhase(activeCoursePhaseNumber - 1, activeCoursePhaseNumber - 2)}
                                        disabled={activeCoursePhaseNumber === 1}
                                        className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                          activeCoursePhaseNumber === 1
                                            ? "cht-text-slate-300 cht-cursor-not-allowed"
                                            : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da]"
                                        }`}
                                        title="Move Phase Earlier"
                                      >
                                        <ArrowLeft size={14} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleMoveCoursePhase(activeCoursePhaseNumber - 1, activeCoursePhaseNumber)}
                                        disabled={activeCoursePhaseNumber === coursesInTrack.length}
                                        className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                          activeCoursePhaseNumber === coursesInTrack.length
                                            ? "cht-text-slate-300 cht-cursor-not-allowed"
                                            : "cht-text-slate-600 hover:cht-bg-white hover:cht-text-[#3e57da]"
                                        }`}
                                        title="Move Phase Later"
                                      >
                                        <ArrowRight size={14} />
                                      </button>
                                    </div>
                                  )}

                                  {isSuperAdmin && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      leftIcon={<Sparkles size={14} />}
                                      className="cht-rounded-xl cht-border-indigo-200 cht-text-[#3e57da] hover:cht-bg-indigo-50"
                                      onClick={() => handleOpenSyllabusArchitectModal(activeCourse.id)}
                                    >
                                      AI Syllabus Architect
                                    </Button>
                                  )}

                                  <Button
                                    variant="primary"
                                    size="sm"
                                    leftIcon={<Plus size={15} />}
                                    className="cht-rounded-xl"
                                    onClick={() => handleAddModule(activeCourse.id)}
                                  >
                                    Add Module to Phase 0{activeCoursePhaseNumber}
                                  </Button>
                                </div>
                              </div>

                              {/* Course-level GitHub Autograder Template Repo for this Phase */}
                              <div className="cht-mt-4 cht-pt-4 cht-border-t cht-border-slate-100 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-end cht-gap-3">
                                <div className="cht-flex-1">
                                  <Input
                                    label="Course GitHub Autograder Template Repository"
                                    placeholder="e.g. python-codehub-assignments-bundle"
                                    value={activeCourse.autograder_template_repo || ""}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setCoursesInTrack((prev) =>
                                        prev.map((c) =>
                                          String(c.id) === String(activeCourse.id)
                                            ? { ...c, autograder_template_repo: val }
                                            : c
                                        )
                                      );
                                    }}
                                    leftIcon={<GitBranch size={16} />}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="cht-rounded-xl cht-shrink-0"
                                  onClick={() =>
                                    handleSaveCourseTemplateRepo(
                                      activeCourse.id!,
                                      activeCourse.autograder_template_repo || ""
                                    )
                                  }
                                >
                                  Save Repo
                                </Button>
                              </div>
                            </div>

                            {/* Active Course Modules */}
                            <div className="cht-space-y-5">
                              {(activeCourse.modules || []).map((module, idx) => (
                                <ModuleEditorCard
                                  key={module.id || idx}
                                  module={module}
                                  moduleIndex={idx}
                                  totalModules={(activeCourse.modules || []).length}
                                  onMoveModule={(fromIdx, toIdx) =>
                                    handleMoveModule(fromIdx, toIdx, activeCourse.id)
                                  }
                                  onMoveLesson={handleMoveLesson}
                                  onUpdateTitle={handleUpdateModuleTitle}
                                  onDeleteModule={handleDeleteModule}
                                  onAddLesson={handleAddLesson}
                                  onUpdateLesson={handleUpdateLesson}
                                  onDeleteLesson={handleDeleteLesson}
                                  onAddExercise={handleAddExercise}
                                  onUpdateExercise={handleUpdateExercise}
                                  onDeleteExercise={handleDeleteExercise}
                                  isSuperAdmin={isSuperAdmin}
                                  onApproveItem={handleApproveContentItem}
                                  courseTemplateRepo={activeCourse.autograder_template_repo || ""}
                                  courseTitle={activeCourse.title}
                                  courseLevel={activeCourse.level || formData.level}
                                  geminiApiKey={geminiApiKey}
                                  geminiModel={geminiModel}
                                  geminiSystemPrompt={geminiSystemPrompt}
                                />
                              ))}

                              {(activeCourse.modules || []).length === 0 && (
                                <div className="cht-py-14 cht-text-center cht-bg-white cht-rounded-[28px] cht-border cht-border-dashed cht-border-gray-200">
                                  <FolderPlus size={36} className="cht-text-slate-300 cht-mx-auto cht-mb-3" />
                                  <h5 className="cht-text-base cht-font-bold cht-text-slate-800 !cht-font-heading">
                                    No modules yet in {activeCourse.title}
                                  </h5>
                                  <p className="cht-text-xs cht-text-slate-400 cht-max-w-sm cht-mx-auto cht-mt-1">
                                    Start structuring Phase 0{activeCoursePhaseNumber} by creating its first module.
                                  </p>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="cht-mt-4 cht-rounded-xl"
                                    leftIcon={<Plus size={14} />}
                                    onClick={() => handleAddModule(activeCourse.id)}
                                  >
                                    Create First Module for Phase 0{activeCoursePhaseNumber}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          /* All Courses Grouped View */
                          <div className="cht-space-y-8">
                            {coursesInTrack.map((course, cIdx) => {
                              const cModules = course.modules || [];
                              return (
                                <div key={course.id || cIdx} className="cht-space-y-4">
                                  {/* Phase Header */}
                                  <div className="cht-p-5 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-3">
                                    <div className="cht-flex cht-items-center cht-gap-3">
                                      <span className="cht-px-2.5 cht-py-1 cht-rounded-lg cht-bg-[#3e57da] cht-text-white cht-text-xs cht-font-black cht-uppercase cht-tracking-wider">
                                        Phase 0{cIdx + 1}
                                      </span>
                                      <div>
                                        <h4 className="cht-text-base cht-font-bold cht-text-slate-900 !cht-font-heading">
                                          {course.title}
                                        </h4>
                                        <p className="cht-text-xs cht-text-slate-400 cht-mt-0.5">
                                          {cModules.length} {cModules.length === 1 ? "module" : "modules"} • {course.duration || "Self-paced"}
                                        </p>
                                      </div>
                                    </div>

                                    <div className="cht-flex cht-items-center cht-gap-2 cht-shrink-0">
                                      {coursesInTrack.length > 1 && (
                                        <div className="cht-flex cht-items-center cht-gap-1 cht-bg-white cht-p-1 cht-rounded-xl cht-border cht-border-slate-200">
                                          <button
                                            type="button"
                                            onClick={() => handleMoveCoursePhase(cIdx, cIdx - 1)}
                                            disabled={cIdx === 0}
                                            className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                              cIdx === 0
                                                ? "cht-text-slate-300 cht-cursor-not-allowed"
                                                : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                                            }`}
                                            title="Move Phase Up"
                                          >
                                            <ArrowUp size={14} />
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleMoveCoursePhase(cIdx, cIdx + 1)}
                                            disabled={cIdx === coursesInTrack.length - 1}
                                            className={`cht-w-7 cht-h-7 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                                              cIdx === coursesInTrack.length - 1
                                                ? "cht-text-slate-300 cht-cursor-not-allowed"
                                                : "cht-text-slate-600 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                                            }`}
                                            title="Move Phase Down"
                                          >
                                            <ArrowDown size={14} />
                                          </button>
                                        </div>
                                      )}

                                      {isSuperAdmin && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          leftIcon={<Sparkles size={13} />}
                                          className="cht-rounded-xl cht-border-indigo-200 cht-text-[#3e57da] hover:cht-bg-indigo-50"
                                          onClick={() => handleOpenSyllabusArchitectModal(course.id)}
                                        >
                                          AI Syllabus
                                        </Button>
                                      )}

                                      <Button
                                        variant="outline"
                                        size="sm"
                                        leftIcon={<Plus size={14} />}
                                        className="cht-rounded-xl"
                                        onClick={() => handleAddModule(course.id)}
                                      >
                                        Add Module to Phase 0{cIdx + 1}
                                      </Button>
                                    </div>
                                  </div>

                                  {/* Phase Modules */}
                                  <div className="cht-space-y-4 cht-pl-0 sm:cht-pl-3">
                                    {cModules.map((module, mIdx) => (
                                      <ModuleEditorCard
                                        key={module.id || mIdx}
                                        module={module}
                                        moduleIndex={mIdx}
                                        totalModules={cModules.length}
                                        onMoveModule={(fromIdx, toIdx) =>
                                          handleMoveModule(fromIdx, toIdx, course.id)
                                        }
                                        onMoveLesson={handleMoveLesson}
                                        onUpdateTitle={handleUpdateModuleTitle}
                                        onDeleteModule={handleDeleteModule}
                                        onAddLesson={handleAddLesson}
                                        onUpdateLesson={handleUpdateLesson}
                                        onDeleteLesson={handleDeleteLesson}
                                        onAddExercise={handleAddExercise}
                                        onUpdateExercise={handleUpdateExercise}
                                        onDeleteExercise={handleDeleteExercise}
                                        isSuperAdmin={isSuperAdmin}
                                        onApproveItem={handleApproveContentItem}
                                        courseTemplateRepo={course.autograder_template_repo || ""}
                                        courseTitle={course.title}
                                        courseLevel={course.level || formData.level}
                                        geminiApiKey={geminiApiKey}
                                        geminiModel={geminiModel}
                                        geminiSystemPrompt={geminiSystemPrompt}
                                      />
                                    ))}

                                    {cModules.length === 0 && (
                                      <div className="cht-py-10 cht-text-center cht-bg-white cht-rounded-2xl cht-border cht-border-dashed cht-border-gray-200">
                                        <p className="cht-text-xs cht-text-slate-400">
                                          No modules in Phase 0{cIdx + 1} yet.
                                        </p>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          className="cht-mt-3 cht-rounded-xl"
                                          leftIcon={<Plus size={13} />}
                                          onClick={() => handleAddModule(course.id)}
                                        >
                                          Add Module to Phase 0{cIdx + 1}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="cht-space-y-6">
                    {/* Standalone Course Module Builder Header */}
                    <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-3">
                      <div>
                        <h3 className="cht-text-xl cht-font-black cht-text-slate-900 !cht-font-heading">
                          Curriculum Architect
                        </h3>
                        <p className="cht-text-xs cht-text-slate-500">
                          Organize your course syllabus into progressive modules, video lectures, and hands-on exercises.
                        </p>
                      </div>
                      <div className="cht-flex cht-items-center cht-gap-2.5">
                        {isSuperAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            leftIcon={<Sparkles size={15} />}
                            className="cht-rounded-xl cht-border-indigo-200 cht-text-[#3e57da] hover:cht-bg-indigo-50"
                            onClick={() => handleOpenSyllabusArchitectModal(formData.id)}
                          >
                            Generate Syllabus with Gemini Pro
                          </Button>
                        )}
                        <Button 
                          variant="primary" 
                          size="sm" 
                          leftIcon={<FolderPlus size={15} />} 
                          className="cht-rounded-xl"
                          onClick={() => handleAddModule()}
                        >
                          Add New Module
                        </Button>
                      </div>
                    </div>

                    {/* Course-level GitHub Autograder Template Repo Card */}
                    <div className="cht-p-5 cht-rounded-[24px] cht-bg-white cht-border cht-border-indigo-100 cht-shadow-sm cht-space-y-3">
                      <div className="cht-flex cht-items-center cht-justify-between cht-gap-2">
                        <div className="cht-flex cht-items-center cht-gap-2">
                          <GitBranch size={16} className="cht-text-[#3e57da]" />
                          <h4 className="cht-text-xs cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-800">
                            Course GitHub Autograder Template Repository
                          </h4>
                        </div>
                        <span className="cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-bg-indigo-50 cht-px-2.5 cht-py-0.5 cht-rounded-full">
                          1 Repo per Student per Course
                        </span>
                      </div>
                      <p className="cht-text-xs cht-text-slate-500">
                        Specify the GitHub template repository for this course (under <strong>Codehub-Technologies</strong>). All autograded module tasks in this course will map to subfolders inside this repository.
                      </p>
                      <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-gap-3">
                        <div className="cht-flex-1">
                          <Input
                            placeholder="e.g. python-codehub-assignments-bundle"
                            value={formData.autograder_template_repo}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                autograder_template_repo: e.target.value.trim(),
                              })
                            }
                            leftIcon={<GitBranch size={16} />}
                          />
                        </div>
                        {formData.id && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="cht-rounded-xl cht-shrink-0"
                            onClick={() =>
                              handleSaveCourseTemplateRepo(
                                formData.id!,
                                formData.autograder_template_repo
                              )
                            }
                          >
                            Save Template Repo
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Standalone Course Modules List */}
                    <div className="cht-space-y-5">
                      {formData.modules.map((module, idx) => (
                        <ModuleEditorCard
                          key={module.id || idx}
                          module={module}
                          moduleIndex={idx}
                          totalModules={formData.modules.length}
                          onMoveModule={(fromIdx, toIdx) => handleMoveModule(fromIdx, toIdx)}
                          onMoveLesson={handleMoveLesson}
                          onUpdateTitle={handleUpdateModuleTitle}
                          onDeleteModule={handleDeleteModule}
                          onAddLesson={handleAddLesson}
                          onUpdateLesson={handleUpdateLesson}
                          onDeleteLesson={handleDeleteLesson}
                          onAddExercise={handleAddExercise}
                          onUpdateExercise={handleUpdateExercise}
                          onDeleteExercise={handleDeleteExercise}
                          isSuperAdmin={isSuperAdmin}
                          onApproveItem={handleApproveContentItem}
                          courseTemplateRepo={formData.autograder_template_repo}
                          courseTitle={formData.title}
                          courseLevel={formData.level}
                          geminiApiKey={geminiApiKey}
                          geminiModel={geminiModel}
                          geminiSystemPrompt={geminiSystemPrompt}
                        />
                      ))}

                      {formData.modules.length === 0 && (
                        <div className="cht-py-16 cht-text-center cht-bg-white cht-rounded-[28px] cht-border cht-border-dashed cht-border-gray-200">
                          <FolderPlus size={36} className="cht-text-slate-300 cht-mx-auto cht-mb-3" />
                          <h4 className="cht-text-base cht-font-bold cht-text-slate-800 !cht-font-heading">
                            No modules created yet
                          </h4>
                          <p className="cht-text-xs cht-text-slate-400 cht-max-w-sm cht-mx-auto cht-mt-1">
                            Get started by adding your first module to structure your course topics and assignments.
                          </p>
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="cht-mt-4 cht-rounded-xl"
                            leftIcon={<Plus size={14} />}
                            onClick={() => handleAddModule()}
                          >
                            Create First Module
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Footer Controls */}
                <div className="cht-flex cht-items-center cht-justify-between cht-pt-6 cht-border-t cht-border-gray-200">
                  <Button variant="ghost" onClick={() => setStep(1)}>
                    Back to Identity & Pricing
                  </Button>
                  <div className="cht-flex cht-items-center cht-gap-3">
                    <Button 
                      variant="outline" 
                      leftIcon={<Eye size={14} />} 
                      className="cht-rounded-xl"
                      onClick={handlePreviewStudentView}
                    >
                      Preview Classroom
                    </Button>
                    <Button 
                      variant="primary" 
                      className="cht-rounded-xl"
                      onClick={() => {
                        addToast("Curriculum saved successfully!", "success");
                        navigate(returnPath);
                      }}
                    >
                      Save & Return
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Sidebar: Pricing, Metrics & Media (4 cols) */}
        <div className="lg:cht-col-span-4 cht-space-y-6">
          {/* Cover Media Card with Media Library Connection */}
          <GlassCard className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between">
              <div>
                <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                  Cover Image
                </h3>
                <p className="cht-text-[11px] cht-text-slate-400 cht-mt-0.5">
                  Linked to Central Media Library
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsMediaPickerOpen(true)}
                className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-[#3e57da]/10 hover:cht-bg-[#3e57da]/20 cht-text-[#3e57da] cht-text-xs cht-font-bold cht-transition-colors cursor-pointer"
                title="Open Media Library"
              >
                <Sparkles size={13} />
                Media Library
              </button>
            </div>

            <div 
              className="cht-relative cht-aspect-video cht-bg-slate-50 cht-rounded-2xl cht-border-2 cht-border-dashed cht-border-gray-200 hover:cht-border-[#3e57da]/40 hover:cht-bg-indigo-50/20 cht-transition-all cht-overflow-hidden cht-flex cht-flex-col cht-items-center cht-justify-center cht-text-center cht-p-4 group"
            >
              {formData.image_url ? (
                <>
                  <img 
                    src={formData.image_url} 
                    alt="Curriculum cover" 
                    className="cht-absolute cht-inset-0 cht-w-full cht-h-full cht-object-cover group-hover:cht-scale-105 cht-transition-transform duration-500" 
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800";
                    }}
                  />
                  <div className="cht-absolute cht-inset-0 cht-bg-black/50 cht-opacity-0 group-hover:cht-opacity-100 cht-transition-opacity cht-flex cht-items-center cht-justify-center cht-gap-2 cht-p-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="primary"
                      className="cht-rounded-xl !cht-text-xs cht-shadow-md"
                      leftIcon={<Sparkles size={13} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMediaPickerOpen(true);
                      }}
                    >
                      Media Library
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="cht-bg-white/90 hover:cht-bg-white cht-text-slate-800 cht-border-white/80 cht-rounded-xl !cht-text-xs"
                      leftIcon={<Upload size={13} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      Upload
                    </Button>
                  </div>
                </>
              ) : (
                <div className="cht-space-y-3 cht-text-slate-400">
                  <div className="cht-w-12 cht-h-12 cht-rounded-2xl cht-bg-slate-100 cht-flex cht-items-center cht-justify-center cht-mx-auto cht-text-slate-400 group-hover:cht-text-[#3e57da] group-hover:cht-bg-[#3e57da]/10 cht-transition-colors">
                    <ImageIcon size={24} />
                  </div>
                  <div>
                    <p className="cht-text-xs cht-font-bold cht-text-slate-700">No cover image selected</p>
                    <p className="cht-text-[10px] cht-text-slate-400 cht-mt-0.5">Select an asset from the media library or upload new</p>
                  </div>
                  <div className="cht-flex cht-items-center cht-justify-center cht-gap-2 cht-pt-1">
                    <button
                      type="button"
                      onClick={() => setIsMediaPickerOpen(true)}
                      className="cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-[#3e57da] hover:cht-bg-[#2d41a8] cht-text-white cht-text-xs cht-font-bold cht-inline-flex cht-items-center cht-gap-1.5 cht-shadow-sm cht-transition-colors cursor-pointer"
                    >
                      <Sparkles size={13} />
                      Choose from Library
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="cht-px-3 cht-py-1.5 cht-rounded-xl cht-bg-white cht-border cht-border-gray-200 hover:cht-bg-gray-50 cht-text-slate-700 cht-text-xs cht-font-bold cht-inline-flex cht-items-center cht-gap-1.5 cht-transition-colors cursor-pointer"
                    >
                      <Upload size={13} />
                      Upload
                    </button>
                  </div>
                </div>
              )}

              {isUploading && (
                <div className="cht-absolute cht-inset-0 cht-bg-white/90 cht-backdrop-blur-sm cht-flex cht-items-center cht-justify-center cht-text-xs cht-font-bold cht-text-[#3e57da] cht-gap-2">
                  <Loader2 size={16} className="cht-animate-spin" />
                  Uploading & syncing with Media Library...
                </div>
              )}
            </div>

            {/* Quick Action Buttons underneath preview */}
            <div className="cht-flex cht-items-center cht-gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cht-flex-1 cht-rounded-xl cht-text-xs cht-font-bold cht-border-[#3e57da]/30 cht-text-[#3e57da] hover:cht-bg-[#f2f4fd]"
                leftIcon={<Sparkles size={14} />}
                onClick={() => setIsMediaPickerOpen(true)}
              >
                Browse Media Library
              </Button>
              {formData.image_url && (
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, image_url: "" })}
                  className="cht-p-2 cht-rounded-xl cht-text-slate-400 hover:cht-text-rose-600 hover:cht-bg-rose-50 cht-border cht-border-transparent hover:cht-border-rose-100 cht-transition-colors"
                  title="Remove Cover Image"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>

            <input 
              ref={fileInputRef}
              type="file" 
              accept="image/*" 
              onChange={handleFileUpload} 
              className="cht-hidden" 
            />

            <Input
              label="Or Direct Image URL"
              placeholder="https://images.unsplash.com/..."
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              leftIcon={<ImageIcon size={16} />}
              helperText="Image selected from Media Library or external link"
            />
          </GlassCard>

          {/* Media Library Picker Modal */}
          <MediaPickerModal
            isOpen={isMediaPickerOpen}
            onClose={() => setIsMediaPickerOpen(false)}
            onSelect={handleSelectFromMediaLibrary}
            allowedTypes={["image"]}
            title={formData.type === "track" ? "Select Career Track Cover Image" : "Select Course Cover Image"}
            selectedUrl={formData.image_url}
          />

          {/* Tuition & Delivery Modes */}
          <GlassCard className="cht-p-6 cht-rounded-[28px] cht-space-y-5">
            <div className="cht-flex cht-items-center cht-justify-between">
              <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                Tuition & Delivery Modes
              </h3>
              <span className={`cht-px-2 cht-py-0.5 cht-rounded-md cht-text-[10px] cht-font-black cht-uppercase ${
                formData.payment_type === "Free" ? "cht-bg-emerald-50 cht-text-emerald-700" : "cht-bg-indigo-50 cht-text-[#3e57da]"
              }`}>
                {formData.payment_type}
              </span>
            </div>

            {!isSuperAdmin && (
              <div className="cht-p-3.5 cht-rounded-xl cht-bg-amber-50 cht-border cht-border-amber-200 cht-flex cht-items-center cht-gap-2.5 cht-text-xs cht-text-amber-800 cht-leading-relaxed">
                <ShieldCheck size={18} className="cht-text-amber-600 cht-shrink-0" />
                <span>Tuition pricing and payment policies are determined exclusively by the SuperAdmin. Instructors cannot modify these values.</span>
              </div>
            )}

            {/* Tuition Model Switch */}
            <div className="cht-space-y-1.5">
              <label className="cht-block cht-text-xs cht-font-bold cht-text-slate-700">
                Tuition Model
              </label>
              <div className="cht-grid cht-grid-cols-2 cht-gap-2">
                <button
                  type="button"
                  disabled={!isSuperAdmin}
                  onClick={() => setFormData({ ...formData, payment_type: "Paid" })}
                  className={`cht-py-2.5 cht-px-3 cht-rounded-xl cht-border cht-text-xs cht-font-bold cht-flex cht-items-center cht-justify-center cht-gap-2 cht-transition-all ${
                    !isSuperAdmin ? "cht-cursor-not-allowed opacity-80 " : ""
                  }${
                    formData.payment_type === "Paid"
                      ? "cht-bg-[#3e57da] cht-border-[#3e57da] cht-text-white cht-shadow-sm"
                      : "cht-bg-white cht-border-gray-200 cht-text-slate-700 hover:cht-border-gray-300"
                  }`}
                >
                  <DollarSign size={14} /> Paid Tuition
                </button>
                <button
                  type="button"
                  disabled={!isSuperAdmin}
                  onClick={() => setFormData({ ...formData, payment_type: "Free", online_price: 0, onsite_price: 0 })}
                  className={`cht-py-2.5 cht-px-3 cht-rounded-xl cht-border cht-text-xs cht-font-bold cht-flex cht-items-center cht-justify-center cht-gap-2 cht-transition-all ${
                    !isSuperAdmin ? "cht-cursor-not-allowed opacity-80 " : ""
                  }${
                    formData.payment_type === "Free"
                      ? "cht-bg-emerald-600 cht-border-emerald-600 cht-text-white cht-shadow-sm"
                      : "cht-bg-white cht-border-gray-200 cht-text-slate-700 hover:cht-border-gray-300"
                  }`}
                >
                  <Sparkles size={14} /> Free / Open
                </button>
              </div>
            </div>

            {formData.payment_type === "Paid" ? (
              <div className="cht-space-y-4">
                <Input
                  label="Online Access Tuition"
                  type="number"
                  placeholder="45000"
                  prefixText="₦"
                  leftIcon={<DollarSign size={16} />}
                  value={formData.online_price}
                  onChange={(e) => setFormData({ ...formData, online_price: e.target.value })}
                  disabled={!isSuperAdmin}
                  showStepper={isSuperAdmin}
                  onStepUp={() =>
                    setFormData((prev) => ({
                      ...prev,
                      online_price: Number(prev.online_price || 0) + 1000,
                    }))
                  }
                  onStepDown={() =>
                    setFormData((prev) => ({
                      ...prev,
                      online_price: Math.max(0, Number(prev.online_price || 0) - 1000),
                    }))
                  }
                />

                <Input
                  label="Onsite Physical Lab Tuition"
                  type="number"
                  placeholder="65000"
                  prefixText="₦"
                  leftIcon={<DollarSign size={16} />}
                  value={formData.onsite_price}
                  onChange={(e) => setFormData({ ...formData, onsite_price: e.target.value })}
                  disabled={!isSuperAdmin}
                  showStepper={isSuperAdmin}
                  onStepUp={() =>
                    setFormData((prev) => ({
                      ...prev,
                      onsite_price: Number(prev.onsite_price || 0) + 1000,
                    }))
                  }
                  onStepDown={() =>
                    setFormData((prev) => ({
                      ...prev,
                      onsite_price: Math.max(0, Number(prev.onsite_price || 0) - 1000),
                    }))
                  }
                />
              </div>
            ) : (
              <div className="cht-p-3.5 cht-rounded-xl cht-bg-emerald-50/70 cht-border cht-border-emerald-200/80 cht-text-xs cht-text-emerald-800 cht-leading-relaxed">
                Curriculum is configured as open access / scholarship. Enrolled students will not be prompted for tuition payment.
              </div>
            )}

            {/* Attendance Presence Modes */}
            <div className="cht-space-y-2 cht-pt-3 cht-border-t cht-border-gray-100">
              <div className="cht-flex cht-items-center cht-justify-between">
                <label className="cht-block cht-text-xs cht-font-bold cht-text-slate-700">
                  Attendance Presence Modes
                </label>
                <span className="cht-text-[11px] cht-text-slate-400">
                  {(presences.length > 0
                    ? formData.presence_ids.filter((id) => presences.some((p) => p.id === id))
                    : formData.presence_ids
                  ).length} selected
                </span>
              </div>

              <div className="cht-grid cht-grid-cols-2 cht-gap-2">
                {(presences.length > 0 ? presences : [{ id: 1, name: "Online" }, { id: 2, name: "Onsite" }]).map((p) => {
                  const isChecked = formData.presence_ids.includes(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          presence_ids: isChecked
                            ? prev.presence_ids.filter((pid) => pid !== p.id)
                            : [...prev.presence_ids, p.id],
                        }));
                      }}
                      className={`cht-p-3 cht-rounded-xl cht-border cht-text-left cht-flex cht-items-center cht-justify-between cht-transition-all ${
                        isChecked
                          ? "cht-bg-[#3e57da]/10 cht-border-[#3e57da] cht-text-[#3e57da]"
                          : "cht-bg-white cht-border-gray-200 cht-text-slate-600 hover:cht-border-gray-300"
                      }`}
                    >
                      <div className="cht-flex cht-items-center cht-gap-2">
                        {p.name.toLowerCase().includes("online") ? (
                          <Globe size={15} />
                        ) : (
                          <MapPin size={15} />
                        )}
                        <span className="cht-text-xs cht-font-bold">{p.name}</span>
                      </div>
                      <div
                        className={`cht-w-4 cht-h-4 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-transition-colors ${
                          isChecked
                            ? "cht-bg-[#3e57da] cht-text-white"
                            : "cht-border cht-border-gray-300"
                        }`}
                      >
                        {isChecked && <Check size={11} />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </GlassCard>

          {/* Social Proof & Live DB Metrics */}
          <GlassCard className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between">
              <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                Public Metrics & Proof
              </h3>
              <span className="cht-text-[10px] cht-font-black cht-uppercase cht-px-2.5 cht-py-0.5 cht-rounded-md cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200/80">
                Live DB Data
              </span>
            </div>
            <p className="cht-text-xs cht-text-slate-500">
              Automatically calculated from real student enrollments, verified course reviews, and published curriculum modules in the database.
            </p>

            <div className="cht-grid cht-grid-cols-2 cht-gap-3">
              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-space-y-1">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-400">
                    Rating (out of 5)
                  </span>
                  <Star size={14} className="cht-text-amber-500 cht-fill-amber-500" />
                </div>
                <p className="cht-text-lg cht-font-black cht-text-slate-900">
                  {Number(formData.rating) > 0 ? Number(formData.rating).toFixed(1) : "0.0"}
                  <span className="cht-text-xs cht-font-semibold cht-text-slate-400"> / 5.0</span>
                </p>
              </div>

              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-space-y-1">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-400">
                    Reviews Count
                  </span>
                  <FileText size={14} className="cht-text-[#3e57da]" />
                </div>
                <p className="cht-text-lg cht-font-black cht-text-slate-900">
                  {formData.review_count || "0"}
                  <span className="cht-text-xs cht-font-semibold cht-text-slate-400"> reviews</span>
                </p>
              </div>

              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-space-y-1">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-400">
                    Enrolled Count
                  </span>
                  <User size={14} className="cht-text-emerald-600" />
                </div>
                <p className="cht-text-lg cht-font-black cht-text-slate-900">
                  {formData.enrolled_count || "0"}
                  <span className="cht-text-xs cht-font-semibold cht-text-slate-400"> students</span>
                </p>
              </div>

              <div className="cht-p-3.5 cht-rounded-2xl cht-bg-slate-50/90 cht-border cht-border-slate-200/80 cht-space-y-1">
                <div className="cht-flex cht-items-center cht-justify-between">
                  <span className="cht-text-[10px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-400">
                    Lessons / Tasks
                  </span>
                  <Layers size={14} className="cht-text-[#3e57da]" />
                </div>
                <p className="cht-text-lg cht-font-black cht-text-slate-900">
                  {liveLessonsAndTasksCount}
                  <span className="cht-text-[11px] cht-font-semibold cht-text-slate-400">
                    {" "}({liveTopicsCount}L • {liveTasksCount}T)
                  </span>
                </p>
              </div>
            </div>
          </GlassCard>

          {/* Features & Inclusions Badges */}
          <GlassCard className="cht-p-6 cht-rounded-[28px] cht-space-y-4">
            <div className="cht-flex cht-items-center cht-justify-between">
              <h3 className="cht-text-sm cht-font-black cht-text-gray-900 cht-uppercase cht-tracking-wider">
                Features & Inclusions
              </h3>
              <span className="cht-text-[10px] cht-font-black cht-uppercase cht-px-2 cht-py-0.5 cht-rounded-md cht-bg-indigo-50 cht-text-[#3e57da]">
                Badges
              </span>
            </div>
            <p className="cht-text-xs cht-text-slate-500">
              Toggle value-add badges rendered on program syllabus cards and sales pages.
            </p>

            <div className="cht-space-y-2.5">
              {[
                {
                  key: "has_certificate" as const,
                  label: "Certificate of Completion",
                  desc: "Issue verified accreditation credential",
                  icon: Award,
                },
                {
                  key: "has_lifetime_access" as const,
                  label: "Lifetime Syllabus Access",
                  desc: "Unrestricted permanent access to updates",
                  icon: Clock,
                },
                {
                  key: "has_mentor_support" as const,
                  label: "1-on-1 Mentor Guidance",
                  desc: "Direct faculty deliverable reviews & office hours",
                  icon: User,
                },
                {
                  key: "has_digital_assets" as const,
                  label: "Project Asset Files",
                  desc: "Figma design files and source starters",
                  icon: Paperclip,
                },
              ].map((item) => {
                const active = Boolean(formData[item.key]);
                const Icon = item.icon;
                return (
                  <div
                    key={item.key}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        [item.key]: !prev[item.key],
                      }))
                    }
                    className={`cht-p-3 cht-rounded-xl cht-border cht-cursor-pointer cht-flex cht-items-center cht-justify-between cht-gap-3 cht-transition-all ${
                      active
                        ? "cht-bg-[#3e57da]/5 cht-border-[#3e57da]/40 cht-text-slate-900"
                        : "cht-bg-white cht-border-gray-200 hover:cht-border-gray-300 cht-text-slate-500"
                    }`}
                  >
                    <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                      <div
                        className={`cht-w-8 cht-h-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-shrink-0 ${
                          active
                            ? "cht-bg-[#3e57da] cht-text-white"
                            : "cht-bg-slate-100 cht-text-slate-400"
                        }`}
                      >
                        <Icon size={16} />
                      </div>
                      <div className="cht-min-w-0">
                        <p className="cht-text-xs cht-font-bold cht-text-slate-900 cht-truncate">
                          {item.label}
                        </p>
                        <p className="cht-text-[10px] cht-text-slate-400 cht-truncate">
                          {item.desc}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`cht-w-5 cht-h-5 cht-rounded-md cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-transition-colors ${
                        active
                          ? "cht-bg-[#3e57da] cht-text-white"
                          : "cht-border cht-border-gray-300"
                      }`}
                    >
                      {active && <Check size={12} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Live Visibility Toggle */}
            <div className="cht-pt-3 cht-border-t cht-border-gray-100">
              <div
                onClick={() => {
                  if (!isSuperAdmin) {
                    addToast("Only SuperAdmin can change public live status.", "warning");
                    return;
                  }
                  setFormData((prev) => ({
                    ...prev,
                    is_live: !prev.is_live,
                  }));
                }}
                className={`cht-p-3 cht-rounded-xl cht-border cht-cursor-pointer cht-flex cht-items-center cht-justify-between cht-gap-3 cht-transition-all ${
                  formData.is_live
                    ? "cht-bg-emerald-50/80 cht-border-emerald-300 cht-text-emerald-950"
                    : "cht-bg-slate-50 cht-border-slate-200 hover:cht-border-slate-300 cht-text-slate-600"
                }`}
              >
                <div className="cht-flex cht-items-center cht-gap-3 cht-min-w-0">
                  <div
                    className={`cht-w-8 cht-h-8 cht-rounded-lg cht-flex cht-items-center cht-justify-center cht-shrink-0 ${
                      formData.is_live
                        ? "cht-bg-emerald-600 cht-text-white"
                        : "cht-bg-slate-200 cht-text-slate-500"
                    }`}
                  >
                    <Globe size={16} />
                  </div>
                  <div className="cht-min-w-0">
                    <p className="cht-text-xs cht-font-bold cht-text-slate-900 cht-truncate">
                      Public Live Visibility
                    </p>
                    <p className="cht-text-[10px] cht-text-slate-500 cht-truncate">
                      {formData.is_live
                        ? "Live & visible on public catalog"
                        : "Draft / Offline (Hidden from public)"}
                    </p>
                  </div>
                </div>

                <div
                  className={`cht-w-9 cht-h-5 cht-rounded-full cht-p-0.5 cht-transition-colors ${
                    formData.is_live ? "cht-bg-emerald-600" : "cht-bg-slate-300"
                  }`}
                >
                  <div
                    className={`cht-w-4 cht-h-4 cht-rounded-full cht-bg-white cht-shadow-sm cht-transform cht-transition-transform ${
                      formData.is_live ? "cht-translate-x-4" : "cht-translate-x-0"
                    }`}
                  />
                </div>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>

      {/* SuperAdmin Gemini Pro Syllabus Architect Modal */}
      <AnimatePresence>
        {isSyllabusModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="cht-fixed cht-inset-0 cht-z-50 cht-bg-slate-900/60 cht-backdrop-blur-sm cht-flex cht-items-center cht-justify-center cht-p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              className="cht-w-full cht-max-w-3xl cht-max-h-[88vh] cht-bg-white cht-rounded-[28px] cht-shadow-2xl cht-border cht-border-indigo-100 cht-flex cht-flex-col cht-overflow-hidden"
            >
              {/* Modal Header */}
              <div className="cht-p-6 cht-border-b cht-border-slate-100 cht-bg-gradient-to-r cht-from-indigo-50/90 cht-via-violet-50/60 cht-to-white cht-flex cht-items-center cht-justify-between cht-gap-4">
                <div className="cht-flex cht-items-center cht-gap-3">
                  <div className="cht-w-10 cht-h-10 cht-rounded-2xl cht-bg-[#3e57da] cht-text-white cht-flex cht-items-center cht-justify-center cht-shadow-sm">
                    <Sparkles size={20} />
                  </div>
                  <div>
                    <div className="cht-flex cht-items-center cht-gap-2">
                      <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
                        Gemini Pro Syllabus Architect
                      </h3>
                      <span className="cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-indigo-100 cht-text-[#3e57da] cht-text-[10px] cht-font-black uppercase">
                        {geminiModel}
                      </span>
                    </div>
                    <p className="cht-text-xs cht-text-slate-500">
                      Generate a complete multi-module curriculum with rich lesson notes and GitHub Autograder exercises in seconds.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSyllabusModalOpen(false)}
                  className="cht-w-8 cht-h-8 cht-rounded-full cht-bg-white cht-border cht-border-slate-200 cht-flex cht-items-center cht-justify-center cht-text-slate-500 hover:cht-text-slate-800"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="cht-p-6 cht-overflow-y-auto cht-space-y-5 cht-flex-1">
                <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-3 cht-gap-4">
                  <div>
                    <label className="cht-block cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600 cht-mb-1.5">
                      Number of Modules
                    </label>
                    <select
                      value={syllabusModuleCount}
                      onChange={(e) => setSyllabusModuleCount(Number(e.target.value))}
                      className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-xl focus:cht-outline-none focus:cht-border-[#3e57da]"
                    >
                      {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={n}>
                          {n} Modules
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="cht-block cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600 cht-mb-1.5">
                      Topics per Module
                    </label>
                    <select
                      value={syllabusTopicsPerModule}
                      onChange={(e) => setSyllabusTopicsPerModule(Number(e.target.value))}
                      className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-xs cht-font-bold cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-xl focus:cht-outline-none focus:cht-border-[#3e57da]"
                    >
                      {[2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {n} Topics per Module
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="cht-block cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600 cht-mb-1.5">
                      Autograded Exercises
                    </label>
                    <button
                      type="button"
                      onClick={() => setSyllabusIncludeAutograder((prev) => !prev)}
                      className={`cht-w-full cht-px-3.5 cht-py-2.5 cht-rounded-xl cht-border cht-text-xs cht-font-bold cht-flex cht-items-center cht-justify-between cht-transition-colors ${
                        syllabusIncludeAutograder
                          ? "cht-bg-indigo-50 cht-border-indigo-300 cht-text-[#3e57da]"
                          : "cht-bg-slate-50 cht-border-slate-200 cht-text-slate-600"
                      }`}
                    >
                      <span>{syllabusIncludeAutograder ? "Include GitHub Autograder" : "Standard Exercises"}</span>
                      <Check size={14} className={syllabusIncludeAutograder ? "cht-opacity-100" : "cht-opacity-0"} />
                    </button>
                  </div>
                </div>

                <div className="cht-space-y-2">
                  <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-flex-wrap">
                    <label className="cht-block cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-text-slate-600">
                      Custom Syllabus Prompt / Raw Outline Paste
                    </label>
                    <div className="cht-inline-flex cht-rounded-xl cht-bg-slate-100 cht-p-0.5 cht-text-[10px] cht-font-bold">
                      <button
                        type="button"
                        onClick={() => setSyllabusPromptMode("augment")}
                        className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                          syllabusPromptMode === "augment"
                            ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                            : "cht-text-slate-500 hover:cht-text-slate-800"
                        }`}
                      >
                        Augment Template
                      </button>
                      <button
                        type="button"
                        onClick={() => setSyllabusPromptMode("override")}
                        className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-transition-colors ${
                          syllabusPromptMode === "override"
                            ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                            : "cht-text-slate-500 hover:cht-text-slate-800"
                        }`}
                      >
                        Full Custom Prompt
                      </button>
                    </div>
                  </div>

                  <textarea
                    rows={4}
                    value={syllabusExtraPrompt}
                    onChange={(e) => setSyllabusExtraPrompt(e.target.value)}
                    placeholder={
                      syllabusPromptMode === "override"
                        ? "Paste your exact module & topic outline or full custom prompt here (e.g., 'Module 1: Python Basics -> Variables, Loops. Module 2: FastAPI -> Routing, Pydantic...'). Gemini Pro will convert it into rich lessons and autograded labs."
                        : "Write custom instructions or paste specific topics to include (e.g., 'Cover Python data structures, OOP, pytest testing, and REST API consumption with progressively harder coding challenges')..."
                    }
                    className="cht-w-full cht-px-3.5 cht-py-2.5 cht-text-xs cht-bg-slate-50 cht-border cht-border-slate-200 cht-rounded-xl focus:cht-outline-none focus:cht-border-[#3e57da] cht-resize-y"
                  />

                  <div className="cht-flex cht-items-center cht-justify-between cht-gap-2 cht-flex-wrap">
                    <div className="cht-flex cht-items-center cht-gap-1.5 cht-flex-wrap">
                      <span className="cht-text-[10px] cht-font-bold cht-text-slate-400">
                        Quick Prompts:
                      </span>
                      {[
                        "Build toward a progressive capstone project",
                        "Include production code examples in every topic",
                        "Design strict pytest labs with edge-case grading",
                        "Use real-world fintech & e-commerce scenarios",
                      ].map((chip) => (
                        <button
                          key={chip}
                          type="button"
                          onClick={() => appendSyllabusPromptChip(chip)}
                          className="cht-text-[10px] cht-font-semibold cht-px-2.5 cht-py-0.5 cht-rounded-full cht-bg-slate-100 hover:cht-bg-indigo-50 cht-text-slate-600 hover:cht-text-[#3e57da] cht-transition-colors"
                        >
                          + {chip}
                        </button>
                      ))}
                    </div>
                    {syllabusExtraPrompt && (
                      <button
                        type="button"
                        onClick={() => setSyllabusExtraPrompt("")}
                        className="cht-text-[10px] cht-font-semibold cht-text-slate-400 hover:cht-text-rose-600"
                      >
                        Clear Prompt
                      </button>
                    )}
                  </div>
                </div>

                <div className="cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center cht-justify-between cht-gap-3 cht-p-3.5 cht-rounded-xl cht-bg-slate-50 cht-border cht-border-slate-200/80">
                  <div className="cht-flex cht-flex-col cht-gap-1">
                    <div className="cht-inline-flex cht-rounded-xl cht-bg-white cht-border cht-border-slate-200 cht-p-0.5 cht-text-[11px] cht-font-bold cht-w-fit">
                      <button
                        type="button"
                        onClick={() => setSyllabusReplaceExisting(false)}
                        className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-transition-colors ${
                          !syllabusReplaceExisting
                            ? "cht-bg-[#3e57da] cht-text-white cht-shadow-sm"
                            : "cht-text-slate-600 hover:cht-text-slate-900"
                        }`}
                      >
                        ➕ Append to Existing Modules
                      </button>
                      <button
                        type="button"
                        onClick={() => setSyllabusReplaceExisting(true)}
                        className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-transition-colors ${
                          syllabusReplaceExisting
                            ? "cht-bg-rose-600 cht-text-white cht-shadow-sm"
                            : "cht-text-slate-600 hover:cht-text-slate-900"
                        }`}
                      >
                        🔄 Replace All Existing Modules
                      </button>
                    </div>
                    <span className="cht-text-[11px] cht-text-slate-500">
                      {!syllabusReplaceExisting
                        ? "Gemini Pro will read your existing modules & topics and continue numbering from the next module without duplicating content."
                        : "Warning: Applying will replace all existing modules on this course with the newly generated syllabus."}
                    </span>
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    leftIcon={<Sparkles size={14} />}
                    onClick={handleGenerateSyllabusPreviewWithGemini}
                    isLoading={isGeneratingSyllabus}
                    className="cht-rounded-xl"
                  >
                    {generatedSyllabusModules.length > 0 ? "Regenerate Preview" : "Generate Syllabus Preview"}
                  </Button>
                </div>

                {/* Generated Syllabus Preview Tree */}
                {generatedSyllabusModules.length > 0 && (
                  <div className="cht-space-y-3 cht-pt-2">
                    <div className="cht-flex cht-items-center cht-justify-between">
                      <h4 className="cht-text-xs cht-font-black cht-uppercase cht-tracking-wider cht-text-indigo-950">
                        Generated Curriculum Preview ({generatedSyllabusModules.length} Modules)
                      </h4>
                      <span className="cht-text-[11px] cht-font-semibold cht-text-emerald-600">
                        Ready to bulk-create in database
                      </span>
                    </div>

                    <div className="cht-space-y-3">
                      {generatedSyllabusModules.map((mod, mIdx) => (
                        <div
                          key={mIdx}
                          className="cht-p-4 cht-rounded-2xl cht-bg-slate-50 cht-border cht-border-slate-200 cht-space-y-2.5"
                        >
                          <div className="cht-flex cht-items-center cht-justify-between cht-gap-2">
                            <span className="cht-text-sm cht-font-black cht-text-slate-900">
                              {mod.title}
                            </span>
                            <span className="cht-text-[11px] cht-font-bold cht-text-slate-500">
                              {(mod.lessons || []).length} Topics • {(mod.exercises || []).length} Exercises
                            </span>
                          </div>

                          <div className="cht-space-y-1.5 cht-pl-2">
                            {(mod.lessons || []).map((les, lIdx) => (
                              <div
                                key={lIdx}
                                className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-slate-700 cht-bg-white cht-px-3 cht-py-1.5 cht-rounded-lg cht-border cht-border-slate-100"
                              >
                                <span className="cht-font-semibold">
                                  {lIdx + 1}. {les.title}
                                </span>
                                <span className="cht-text-[10px] cht-font-bold cht-text-slate-400">
                                  {les.duration || "20 mins"}
                                </span>
                              </div>
                            ))}

                            {(mod.exercises || []).map((ex, eIdx) => (
                              <div
                                key={eIdx}
                                className="cht-flex cht-items-center cht-justify-between cht-text-xs cht-text-indigo-900 cht-bg-indigo-50/70 cht-px-3 cht-py-1.5 cht-rounded-lg cht-border cht-border-indigo-100"
                              >
                                <span className="cht-font-bold">
                                  Lab: {ex.title}
                                </span>
                                {ex.requires_autograder && ex.autograder_module_name && (
                                  <code className="cht-text-[10px] cht-font-mono cht-bg-white cht-px-2 cht-py-0.5 cht-rounded cht-border cht-border-indigo-200 cht-text-[#3e57da]">
                                    {ex.autograder_module_name}
                                  </code>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="cht-p-5 cht-border-t cht-border-slate-100 cht-bg-slate-50/70 cht-flex cht-items-center cht-justify-between cht-gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsSyllabusModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  leftIcon={<Check size={15} />}
                  disabled={generatedSyllabusModules.length === 0}
                  isLoading={isApplyingSyllabus}
                  onClick={handleBulkApplyGeneratedSyllabus}
                  className="cht-rounded-xl"
                >
                  Apply {generatedSyllabusModules.length || ""} Modules to Course
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InstructorCourseEditor;
